import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L, { divIcon } from 'leaflet';
// Keeps the area renderers in place if a moveend lands mid-fly (see the file).
import './leafletRendererGuard.js';
// Faster projection, clipping, partial redraws and hit tests, with Leaflet's exact results (see the file).
import './leafletFastPaths.js';
import { templateDivIcon } from './templateIcon.js';
import { GeoJSON, MapContainer, TileLayer, useMap } from 'react-leaflet';
import { feature as topoFeatureFromTopojson } from 'topojson-client';
import { useNavigate } from 'react-router-dom';
import { markerScreenPoint } from './markerScreenPoint.js';
import { ColourModeIcon, getColourMode, nextColourModeLabel, nextColourModeState } from './colourMode.js';
import ColourMetricMenu from './ColourMetricMenu.js';
import { findAreaLayerAt } from './touchAreaHover.js';
import { loadPostcodePostort, searchStreets } from './streetSearch.js';
import { createAreaSelectionOverlay } from './areaSelectionOverlay.js';
import { createGreyAreaCover, FILTERED_COVER } from './greyAreaCover.js';
import { createFilteredAreaBadges } from './filteredAreaBadges.js';
import {
    fadeAreaLayer,
    fadeAreaLayers,
    isAreaRevealPending,
    isRevealColourForced,
    noteAreaToggle,
    noteFiltersApplied,
    onAreaRevealChange,
    onRevealColourEnd,
    releaseHeldAreaFades,
    setListingsShownCheck,
    whenAreaFadeReady,
} from './areaToggleFade.js';
import PaletteOutlined from '@mui/icons-material/PaletteOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FavoriteButton from './FavoriteButton.js';

import ListViewFilterColumn from './ListViewFilterColumn';
import FilterList from './Drawer';
import ListingCardGallery from './ListingCardGallery';
import ListingCardFloorplan from './ListingCardFloorplan';
import ListingFullViewModal from './ListingFullViewModal';
import ListViewInfoPanel from './ListViewInfoPanel.js';
import { DotSquareMarker, dotSpriteUrl } from './dotSquares.js';
import CompareCard from './CompareCard.js';
import ComparePreview from './ComparePreview.js';
import { setCompareHover, toggleComparedArea, useComparedAreas, useComparedHomesShown } from './compareAreas.js';
import { apiFetch, loadAreaData, loadOverlay } from './edgePayloads.js';
import { createCompareMarkers } from './compareMarkers.js';
import { pulseListingAt } from './listingLocatePulse.js';
import { MAX_COMPARED } from './areaShare.js';
import { buildListingAreaInfo } from './listingAreaInfo.js';
import AreaTrendPill from './AreaTrendPill.js';
import helpBoxData from './helpBoxData.js';
import useActiveListings, { normalizeListingsPayload, prefetchCompactListings } from './useActiveListings';
import { fetchFullListingDetails, getListingKey, mergeListingDetailsIfChanged, readListingKeyFromSearch } from './listingDetailsApi.js';
import ListViewSearchChips from './ListViewSearchChips.js';
import { checkFavorites, isFavoriteListing, markFavoriteRemoved, useFavorites } from './favorites.js';
import { isVisitedKey, markVisited, useVisitedVersion } from './visited.js';
import { areaFillOpacity, areaStats as rawAreaStats, colorToScore, fillAreaStats, scoreInkColor, scoreToColor } from './areaData.js';
import { useColourScheme } from './useColourScheme.js';
import { computeNeighbourhoodScoring } from './neighbourhoodScoring.js';
import { LAN_LIST, findLan } from './lanAreas.js';
import { useCommute, useCommuteReach, useCommuteTimes, commuteModeLabel } from './commute.js';
import { CommuteModeIcon } from './CommuteFilter.js';
import { areasToggleToastText, areaToggleToastText, showMapToast } from './mapToast.js';
import { attachAreaBoxSelect, boxSelectTurnsOn } from './areaBoxSelect.js';
import { readSavedFilters, saveFilters } from './savedFilters.js';
import { attachMapTouchGuard } from './mapTouchGuard.js';
import {
    listvySuggestionsFromSaved,
    readSavedSelection,
    readViewState,
    saveListvySelection,
    saveViewState,
} from './savedSelection.js';
import { attachListingMiniCard } from './listingMiniCard.js';
import {
    SHAPE_GROUP_RADIUS,
    SHAPE_MARKER_RADIUS,
    SHAPE_OVERLAP_PX,
    SPREAD_COLLAPSE_GLYPH,
    shapeRadiusAtZoom,
    shapeMarkerVisual,
} from './shapeMarkers.js';
import { readSavedCheckedState, readSavedLegendCollapsed, saveMapToggles } from './mapToggles.js';
import {
    buildAreaGroupIndex,
    findSplitZoom,
    getAreaGroups,
    getMapGroups,
    getGroupsPerArea,
    NEW_CONSTRUCTION_COLOR,
} from './listingGroups.js';
import {
    LISTING_GROUP_ANIMATION_MAX_MARKERS,
    buildGroupAnimationItems,
    buildPresenceItems,
    LISTING_PRESENCE_ANIMATION_MS,
    createListingGroupAnimator,
    homeShapeSprite,
} from './listingGroupAnimation.js';
import { EMPTY_TILE_BOUNDS, createAreaMapReveal } from './areaMapReveal.js';
import { createAreaGeometrySwitch } from './areaGeometry.js';
import {
    OPEN_FREE_MAP_LABEL_PANE,
    OpenFreeMapBase,
    openFreeMapFailedThisSession,
    rememberOpenFreeMapFailure,
} from './openFreeMap.js';
import kommunNamesJson from '../kommun.json';
import { listingProps, summarizeFilters, track } from '../analytics/events.js';
import { getBrokerLogo } from './listingPlaceholder.js';
import { fetchPriceHistogram, histogramFilterBody } from './PriceRangeBar.js';

import './mapStyle.css';
import './listViewPage.css';

import SearchScore, { searchResultScore } from './SearchScore.js';
import { scoreWord } from './scoreWords.js';
import { exposeMapForPerf } from '../perfRun.js';
import IntroTips, { introSeen, requestIntroTip, SharedIntroTips, useSharedIntroOpen } from './IntroTips.js';
import { LISTVY_TIPS, SHARED_TIPS, WELCOME_TIP } from './introScenes.js';
const SWEDEN_CENTER = [62.3499986, 13.4666648];
const SWEDEN_MAP_BOUNDS = [
    [50.4, -1.6],
    [75.6, 37.6],
];
// Dark mode's mask over everything outside Sweden: above the map's labels (440), so they dim with it.
const OUTSIDE_SWEDEN_MASK_PANE = 'outsideSwedenMaskPane';
const LIST_VIEW_REGSO_PANE = 'listViewRegsoPane';
const LIST_VIEW_LISTINGS_PANE = 'listViewListingsPane';
// Regrouping glide overlay, just above the listing markers.
const LIST_VIEW_LISTING_ANIMATION_PANE = 'listViewListingAnimationPane';
// The regular map, shown only inside the hovered area on top of the white map (see areaMapReveal.js).
const LIST_VIEW_HOVER_TILE_PANE = 'listViewHoverTilePane';
const FILTER_ENDPOINT = 'https://api.xn--omrdeskollen-vcb.nu/listings/active/filter';
const REGSO_ENDPOINT_BASE = 'https://api.xn--omrdeskollen-vcb.nu/listings/active/regso';
const STADIA_API_KEY = String(process.env.REACT_APP_STADIA_API_KEY || '2fb5ceca-d4ab-4317-afa9-b94a7e8ffbc0').trim();
const OSM_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const STADIA_BRIGHT_URL = `https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}.png${STADIA_API_KEY ? `?api_key=${encodeURIComponent(STADIA_API_KEY)}` : ''}`;
const STADIA_TONER_URL = `https://tiles.stadiamaps.com/tiles/stamen_toner_lite/{z}/{x}/{y}.png${STADIA_API_KEY ? `?api_key=${encodeURIComponent(STADIA_API_KEY)}` : ''}`;
const TILE_URL_BRIGHT = STADIA_BRIGHT_URL;
const TILE_URL_TONER = STADIA_TONER_URL;
const LOCK_SINGLE_TILE_STYLE = false;
const LIST_VIEW_TILE_MIN_ZOOM = 5;
const LIST_VIEW_TILE_MAX_ZOOM = 17;
const projectListViewPoint = (map) => (lat, lng, zoom) => map.project([lat, lng], zoom);
const unprojectListViewPoint = (map) => (x, y, zoom) => map.unproject([x, y], zoom);
const LIST_VIEW_WHEEL_DEBOUNCE_MS = 20;
const LIST_VIEW_TILE_KEEP_BUFFER = 1;
const LIST_VIEW_TILE_UPDATE_INTERVAL_MS = 350;
const STADIA_PROBE_URL = STADIA_API_KEY
    ? `https://tiles.stadiamaps.com/tiles/stamen_toner_lite/0/0/0.png?api_key=${encodeURIComponent(STADIA_API_KEY)}`
    : 'https://tiles.stadiamaps.com/tiles/stamen_toner_lite/0/0/0.png';
const TILE_PROVIDER_FALLBACK_SESSION_KEY = 'tile-provider-fallback-osm';
const SWEDEN_COUNTRY_GEOJSON_URL = '/swedenGeo.json';
const MASK_OUTER_RING = [
    [-180, -85.0511],
    [180, -85.0511],
    [180, 85.0511],
    [-180, 85.0511],
    [-180, -85.0511],
];
const ORT_KOMMUN_FILE_URL = '/ortKommun.txt';
const REGSO_NAME_FILE_URL = '/regsoNamn.txt';
// Which areas each postort covers (built from listing addresses by the backend's scripts/build_postort_areas.py).
const POSTORT_AREAS_FILE_URL = '/postortAreas.json';
const LISTINGS_PAGE_SIZE = 20;
// The map's homes come in batches this big, the first alone (it tells the total) and the rest a few at a time.
// Batches of 200 one after the other took ~50 s for Stockholms län (11,000 homes, 58 requests of ~1 s each,
// mostly the server's per-request work); 2,000 three at a time take ~15 s (the Pi's upload is the limit then),
// with the same homes in the same order.
const MAP_LISTINGS_BATCH_SIZE = 2000;
const MAP_LISTINGS_PARALLEL = 3;
const LIST_VIEW_SUGGESTIONS_PER_TYPE = 20;
const LIST_VIEW_SEARCH_MIN_CHARS = 3;
// Street search (streetSearch.js): Nominatim allows about one request a second, so it waits for a pause in
// typing, and shows a few streets.
const LIST_VIEW_STREET_SEARCH_MIN_CHARS = 4;
const LIST_VIEW_STREET_SEARCH_DELAY_MS = 450;
const LIST_VIEW_STREET_SUGGESTIONS = 5;
// Hover tail (as in kartvy): moving straight from one area to the next holds the one just left in its hover
// look a little longer before it settles, so a quick sweep leaves a short trailing tail of lit tiles behind
// the pointer. Leaving the map altogether settles at the shorter delay, so a single mouseout isn't sticky.
const REGSO_HOVER_TAIL_MS = 220;
const REGSO_HOVER_EXIT_MS = 90;
// How close the map flies in on a picked street.
const LIST_VIEW_STREET_ZOOM = 15;
// A second click within this time is a double click (sticky area card) instead of selecting the area.
const REGSO_DOUBLE_CLICK_MS = 280;
// Listvy's zoom for house icons, one deeper than kartvy's (its map is smaller); circles below it.
// How long after listvy (or its landing) mounts before prefetching the next steps' data: past the first seconds'
// work (at 4× CPU the hidden map is built until ~3 s), then in idle time.
const PREFETCH_AFTER_MS = 4000;
const LIST_VIEW_ICON_ZOOM_THRESHOLD = 13;
// Markers that grow in or shrink away at once when the listings shown change; more than this just swap.
const LISTING_PRESENCE_ANIMATION_MAX = 1500;
const LIST_VIEW_FOCUS_ZOOM = 12;
const LIST_VIEW_CLUSTER_DISABLE_ZOOM_OFFSET = 3;
// Fill opacity of a hovered area, as in kartvy, so the regular map inside it shows through.
const REGSO_HOVER_FILL_OPACITY = 0.05;
// Fill of tiles outside the search / area filters. These are not lightened on hover: the dark grey
// fading out over the light base map flashed white while moving across them (see getOverlayStyle).
const INACTIVE_REGSO_FILL_COLOR = '#4c525e';
// An area the filters leave out (selected, but outside them): light grey with a funnel, apart from one clicked off.
const FILTERED_OUT_FILL = '#c9d1cf';
const REGSO_HOVER_FILL_OPACITY_INACTIVE = 0.2;
const LIST_VIEW_FLY_DURATION = 1.3;
const LIST_VIEW_FLY_EASE = 0.16;
const LIST_VIEW_MULTI_REGSO_MAX_AUTO_ZOOM = 12;
// The closest a kommun / postort search zooms when it fits its areas.
const LIST_VIEW_AREA_SEARCH_MAX_ZOOM = 13;
const FULL_LISTING_DETAILS_ERROR = 'Could not load full listing details';
const LISTING_FEED_TAB_FOR_SALE = 'for_sale';
const LISTING_FEED_TAB_COMING_SOON = 'coming_soon';
const COMING_SOON_LISTING_MARKER = 'snart till salu';
const SQM_PRICE_REASONABLE_MAX = 500000;
const MOBILE_LAYOUT_MAX_WIDTH = 700;

// Must match the grid-template-columns transition in listViewPage.css.
const MAP_COLUMN_SLIDE_MS = 340;

const isMobileViewportOnLoad = () => {
    if (typeof window === 'undefined') return false;
    if (typeof window.matchMedia === 'function') {
        return window.matchMedia(`(max-width: ${MOBILE_LAYOUT_MAX_WIDTH}px)`).matches;
    }
    return Number(window.innerWidth) <= MOBILE_LAYOUT_MAX_WIDTH;
};

const normalizeSearchText = (value) => String(value || '')
    .toLowerCase()
    .replace(/\b\d+[a-zåäö]?\b/gi, ' ')
    .replace(/[^a-zåäö0-9\s]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getSearchMatchInfo = (value, query, tokens) => {
    const values = (Array.isArray(value) ? value : [value])
        .map((entry) => normalizeSearchText(entry))
        .filter(Boolean);
    if (!values.length || !tokens.length || !query) return null;
    const ranked = values
        .map((entry) => {
            if (entry.startsWith(query)) {
                return { rank: 0, entry };
            }

            const words = entry.split(' ').filter(Boolean);
            const tokensMatchWordPrefix = tokens.every((token) => words.some((word) => word.startsWith(token)));
            if (tokensMatchWordPrefix) {
                return { rank: 1, entry };
            }

            const tokensMatchContains = tokens.every((token) => entry.includes(token));
            if (tokensMatchContains) {
                return { rank: 2, entry };
            }

            return null;
        })
        .filter(Boolean)
        .sort((a, b) => {
            if (a.rank !== b.rank) return a.rank - b.rank;
            if (a.entry.length !== b.entry.length) return a.entry.length - b.entry.length;
            return a.entry.localeCompare(b.entry, 'sv');
        });

    if (!ranked.length) return null;
    return {
        rank: ranked[0].rank,
        matchedValue: ranked[0].entry,
    };
};

const rankSuggestions = (items, {
    query,
    tokens,
    toSearchText,
    toSuggestion,
    toSortText,
}) => items
    .map((item) => {
        const matchInfo = getSearchMatchInfo(toSearchText(item), query, tokens);
        if (!matchInfo) return null;
        const suggestion = toSuggestion(item);
        const sortValue = normalizeSearchText(
            typeof toSortText === 'function' ? toSortText(item) : matchInfo.matchedValue,
        );
        return {
            suggestion,
            rank: matchInfo.rank,
            value: sortValue || matchInfo.matchedValue,
        };
    })
    .filter(Boolean)
    .sort((a, b) => {
        if (a.rank !== b.rank) return a.rank - b.rank;
        if (a.value.length !== b.value.length) return a.value.length - b.value.length;
        return a.value.localeCompare(b.value, 'sv');
    })
    .reduce((acc, entry) => {
        const labelKey = normalizeSearchText(entry?.suggestion?.label || entry.value);
        if (!labelKey || acc.seen.has(labelKey)) return acc;
        acc.seen.add(labelKey);
        acc.items.push(entry);
        return acc;
    }, { seen: new Set(), items: [] })
    .items
    .slice(0, LIST_VIEW_SUGGESTIONS_PER_TYPE)
    .map((entry) => entry.suggestion);

const parseOrtKommunPairs = (text) => text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
        const splitIndex = line.lastIndexOf(',');
        if (splitIndex <= 0 || splitIndex >= line.length - 1) return null;
        const ort = line.slice(0, splitIndex).trim();
        const kommun = line.slice(splitIndex + 1).trim();
        if (!ort || !kommun) return null;
        return { ort, kommun };
    })
    .filter(Boolean);

// A kommun search's code from its name. Searches use the genitive ("Stockholms", "Falu"), kommunKoder.json
// the plain name ("Stockholm", "Falun"): exact first, else a unique name one letter longer or shorter.
const kommunCodeForName = (kommunCodes, name) => {
    const wanted = normalizeSearchText(name);
    if (!wanted || !kommunCodes.length) return '';
    const exact = kommunCodes.find(([, kommunName]) => kommunName === wanted);
    if (exact) return exact[0];
    const near = kommunCodes.filter(([, kommunName]) => Math.abs(kommunName.length - wanted.length) === 1
        && (kommunName.startsWith(wanted) || wanted.startsWith(kommunName)));
    return near.length === 1 ? near[0][0] : '';
};

const normalizeKommunNames = (items) => (Array.isArray(items) ? items : [])
    .map((line) => String(line).trim())
    .filter(Boolean)
    .map((line) => line.replace(/\s+kommun$/i, '').trim())
    .filter(Boolean);

const parseRegsoNames = (text) => text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
        const splitIndex = line.indexOf(',');
        if (splitIndex <= 0 || splitIndex >= line.length - 1) return null;
        const regsoId = line.slice(0, splitIndex).trim();
        const name = line.slice(splitIndex + 1).trim();
        if (!regsoId || !name) return null;
        return { regsoId, name };
    })
    .filter(Boolean);

const parseMinFilterBound = (value) => {
    if (value == null || value === '' || value === 'min') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const parseMaxFilterBound = (value) => {
    if (value == null || value === '' || value === 'max') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

// The fixed bounds the landing map fits (Stockholm). The whole landing scene — this map, its dimming, the
// house markers and the area cards — is a baked static image (see the landing poster, below), so nothing is
// projected at runtime.
const LANDING_FIT_BOUNDS = { south: 59.17, west: 17.78, north: 59.50, east: 18.32 };

export const parseAreaValue = (listing) => {
    if (Number.isFinite(listing?.areaValue) && listing.areaValue > 0) {
        return listing.areaValue;
    }
    const fromText = String(listing?.area || '').replace(',', '.').match(/(\d+(\.\d+)?)/);
    if (!fromText) return null;
    const parsed = Number(fromText[1]);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const parseNumericLoose = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (value == null) return null;
    const raw = String(value).trim();
    if (!raw) return null;

    const tokenMatch = raw.match(/-?\d{1,3}(?:[\s\u00a0.,]\d{3})+(?:[.,]\d+)?|-?\d+(?:[.,]\d+)?/);
    const token = tokenMatch?.[0] ? String(tokenMatch[0]).trim() : '';
    if (!token) return null;

    const compactToken = token.replace(/\u00a0/g, ' ').replace(/\s+/g, '');
    const normalizedToken = compactToken.includes(',') && !compactToken.includes('.')
        ? compactToken.replace(',', '.')
        : compactToken;
    const numericOnly = normalizedToken.replace(/[^0-9.-]/g, '');
    if (numericOnly) {
        const parsed = Number(numericOnly);
        if (Number.isFinite(parsed)) return parsed;
    }

    const digitsOnly = token.replace(/\D/g, '');
    if (!digitsOnly) return null;
    const parsedDigits = Number(digitsOnly);
    return Number.isFinite(parsedDigits) ? parsedDigits : null;
};

export const getListingSqmPriceValue = (listing) => {
    const areaValue = parseAreaValue(listing);
    const rawPriceValue = Number.isFinite(listing?.priceValue)
        ? Number(listing.priceValue)
        : parseNumericLoose(listing?.price);
    const computedSqm = Number.isFinite(rawPriceValue) && Number.isFinite(areaValue) && areaValue > 0
        ? Math.round(rawPriceValue / areaValue)
        : null;
    if (Number.isFinite(computedSqm) && computedSqm > 0) return computedSqm;

    const explicitSqm = parseNumericLoose(listing?.sqmPrice);
    if (!Number.isFinite(explicitSqm) || explicitSqm <= 0 || explicitSqm > SQM_PRICE_REASONABLE_MAX) {
        return null;
    }
    return Math.round(explicitSqm);
};

const parseListingsTotal = (result, fallbackCount = 0) => {
    if (result == null) return fallbackCount;
    const directCandidates = [
        result?.total,
        result?.total_count,
        result?.totalCount,
        result?.count_total,
        result?.countTotal,
        result?.count,
        result?.num_results,
        result?.numResults,
    ];
    for (let index = 0; index < directCandidates.length; index += 1) {
        const candidate = Number(directCandidates[index]);
        if (Number.isFinite(candidate) && candidate >= 0) return Math.floor(candidate);
    }
    const metaCandidates = [
        result?.meta?.total,
        result?.meta?.count,
        result?.pagination?.total,
        result?.pagination?.count,
        result?.page?.total,
        result?.page?.count,
    ];
    for (let index = 0; index < metaCandidates.length; index += 1) {
        const candidate = Number(metaCandidates[index]);
        if (Number.isFinite(candidate) && candidate >= 0) return Math.floor(candidate);
    }
    return fallbackCount;
};

const getForSaleFromFeedTab = (feedTab) => feedTab !== LISTING_FEED_TAB_COMING_SOON;

export const formatListingPrice = (listing, fallback) => {
    const directValue = Number.isFinite(listing?.priceValue) ? Number(listing.priceValue) : null;
    if (Number.isFinite(directValue) && directValue > 0) {
        return `${directValue.toLocaleString('sv-SE').replace(/\u00a0/g, ' ')}kr`;
    }

    const raw = String(listing?.price || '').trim();
    if (!raw) return fallback;
    const lower = raw.toLowerCase();
    const numericLike = /^[\d\s.,]+$/.test(raw);
    const hasCurrency = /\b(kr|sek)\b/.test(lower);
    if (!numericLike && !hasCurrency) return raw;

    const digits = raw.replace(/\D/g, '');
    if (!digits) return raw;
    const parsed = Number(digits);
    if (!Number.isFinite(parsed) || parsed <= 0) return raw;
    return `${parsed.toLocaleString('sv-SE').replace(/\u00a0/g, ' ')}kr`;
};

const getListingAgeDays = (listing) => {
    const label = String(listing?.days || '').toLowerCase().trim();
    if (!label) return Number.MAX_SAFE_INTEGER;
    if (label.includes('idag') || label.includes('today')) return 0;
    if (label.includes('igår') || label.includes('yesterday')) return 1;

    const dayMatch = label.match(/(\d+)\s*(dag|days?)/);
    if (dayMatch) return Number(dayMatch[1]);

    const monthMatch = label.match(/(\d+)\s*(månad|månader|months?)/);
    if (monthMatch) return Number(monthMatch[1]) * 30;

    const yearMatch = label.match(/(\d+)\s*(år|years?)/);
    if (yearMatch) return Number(yearMatch[1]) * 365;

    return Number.MAX_SAFE_INTEGER;
};

const normalizeLooseText = (value) => String(value || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const isComingSoonListing = (listing) => {
    if (!listing || typeof listing !== 'object') return false;
    if (listing.isUpcoming === true || listing.isComingSoon === true || listing.comingSoon === true) {
        return true;
    }
    const statusCandidates = [
        listing.days,
        listing.status,
        listing.state,
        listing.listingStatus,
        listing.imgTag,
        listing.subtitle,
        listing.title,
        listing.description,
        listing.badge,
        ...(Array.isArray(listing.badges) ? listing.badges : []),
    ];
    return statusCandidates.some((value) => normalizeLooseText(value).includes(COMING_SOON_LISTING_MARKER));
};

const getListingIdentity = (listing) => {
    const explicitId = String(listing?.id || '').trim();
    if (explicitId) return explicitId;
    const lat = Number.isFinite(listing?.lat) ? listing.lat : '';
    const lng = Number.isFinite(listing?.lng) ? listing.lng : '';
    const address = normalizeSearchText(listing?.address || listing?.title || '');
    const price = String(listing?.priceValue ?? listing?.price ?? '');
    const rooms = String(listing?.roomsValue ?? listing?.rooms ?? '');
    const area = String(listing?.areaValue ?? listing?.area ?? '');
    const fee = String(listing?.feeValue ?? listing?.fee ?? '');
    return `${lat}|${lng}|${address}|${price}|${rooms}|${area}|${fee}`;
};

const dedupeListings = (items) => {
    if (!Array.isArray(items) || !items.length) return [];
    const seen = new Set();
    const unique = [];
    for (let i = 0; i < items.length; i += 1) {
        const listing = items[i];
        const identity = getListingIdentity(listing);
        if (!identity || seen.has(identity)) continue;
        seen.add(identity);
        unique.push(listing);
    }
    return unique;
};

const isFullRange = (range) => Array.isArray(range) && range[0] === 1 && range[1] === 6;

export const normalizeListingType = (type, languageState) => {
    if (type === 'apartment') return languageState.swedish ? 'Lägenhet' : 'Apartment';
    if (type === 'rowhouse') return languageState.swedish ? 'Par/Radhus' : 'Row house';
    if (type === 'fritidshus') return languageState.swedish ? 'Fritidshus' : 'Holiday home';
    return languageState.swedish ? 'Villa/Hus' : 'House';
};

const getListingTypeGroup = (type) => {
    const normalized = String(type || '').toLowerCase().trim();
    if (normalized === 'apartment' || normalized === 'lägenhet') return 'apartment';
    if (
        normalized === 'rowhouse'
        || normalized === 'townhouse'
        || normalized === 'radhus'
        || normalized === 'parhus'
        || normalized === 'par/radhus'
    ) return 'rowhouse';
    if (normalized === 'fritidshus') return 'fritidshus';
    return 'house';
};

// Map marker group: new builds get their own (and their own colour), the rest go by type.
const getListingMarkerGroup = (type, listing) => (listing?.isNewConstruction ? 'new' : getListingTypeGroup(type));

// type: a listing type or a marker group ('new' for nyproduktion).
const getMarkerColor = (type) => {
    if (type === 'new') return NEW_CONSTRUCTION_COLOR;
    const group = getListingTypeGroup(type);
    if (group === 'apartment') return '#4a96d0';
    if (group === 'rowhouse') return '#b86b2d';
    if (group === 'fritidshus') return '#b23a2e';
    return '#1f7a62';
};

export const normalizeListingScore = (value) => {
    if (value == null) return null;
    const parsed = Number(String(value).replace(',', '.'));
    if (!Number.isFinite(parsed)) return null;
    const clamped = Math.min(Math.max(parsed, 1), 6);
    return Math.round(clamped * 2) / 2;
};

// A suggestion the way kartvy's search list shows its rows (SearchBox.js): the name, its place muted after it and
// a tag for the kind, so both views' search lists look the same. The area count of a postort or an area word
// drops out on phones (.kartvy-search-tag-count, mapStyle.css).
const areaCountText = (count, swedish) => (count > 1
    ? (swedish ? ` · ${count} områden` : ` · ${count} areas`)
    : '');
const splitLabel = (label, separator) => {
    const text = String(label || '');
    const at = text.indexOf(separator);
    return at < 0 ? [text, ''] : [text.slice(0, at), text.slice(at + separator.length)];
};
// kommunName: the kommun an ort or an area lies in (its plain name), shown muted after the name like kartvy's.
export const suggestionParts = (suggestion, swedish, kommunName = '') => {
    switch (suggestion?.type) {
        case 'kommun':
            return { name: suggestion.label, place: '', tag: swedish ? 'Kommun' : 'Municipality' };
        case 'lan':
            return { name: suggestion.label, place: '', tag: swedish ? 'Län' : 'County' };
        case 'ort':
            return { name: suggestion.ort || suggestion.label, place: kommunName || suggestion.kommun || '', tag: swedish ? 'Ort' : 'Locality' };
        case 'postort': {
            const [, place] = splitLabel(suggestion.label, ', ');
            return {
                name: suggestion.postort || suggestion.label,
                place,
                tag: swedish ? 'Postort' : 'Postal town',
                count: areaCountText(suggestion.regsoIds?.length || 0, swedish),
            };
        }
        case 'omrade-word':
            return {
                name: suggestion.label,
                place: kommunName,
                tag: swedish ? 'Område' : 'Area',
                count: areaCountText(suggestion.regsoIds?.length || 0, swedish),
            };
        default: {
            if (suggestion?.address) {
                const [name, place] = splitLabel(suggestion.label, ', ');
                return { name, place, tag: swedish ? 'Gata' : 'Street' };
            }
            const [name] = splitLabel(suggestion?.label, ' · ');
            return { name, place: kommunName, tag: swedish ? 'Område' : 'Area' };
        }
    }
};

// The score's word, from the site's one list (scoreWords.js).
export const getAreaScoreLabel = (score, languageState) => scoreWord(score, Boolean(languageState?.swedish));

// Module-level helpers the info panel uses (one constant object, so the memoized panel isn't re-rendered).
const INFO_PANEL_HELPERS = {
    extractListingRegsoCode: (listing) => extractListingRegsoCode(listing),
    normalizeListingScore: (value) => normalizeListingScore(value),
    normalizeListingType: (type, languageState) => normalizeListingType(type, languageState),
    formatListingPrice: (listing, fallback) => formatListingPrice(listing, fallback),
    parseAreaValue: (listing) => parseAreaValue(listing),
    getListingSqmPriceValue: (listing) => getListingSqmPriceValue(listing),
};

export const extractListingRegsoCode = (listing) => {
    const directRegsoCode = String(
        listing?.regsoId
        || listing?.regsoCode
        || listing?.regso
        || listing?.regsokod
        || listing?.regso_id
        || '',
    ).trim();
    if (directRegsoCode) return directRegsoCode;

    // Some feeds only include regso inside generated fingerprint IDs.
    const listingId = String(listing?.id || '').trim();
    const idMatch = listingId.match(/^listing:([^|]+)\|/);
    return idMatch?.[1] ? String(idMatch[1]).trim() : '';
};

const getGeometryBounds = (geometry) => {
    if (!geometry?.coordinates) return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    const applyPoint = (coord) => {
        const x = coord[0];
        const y = coord[1];
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
    };

    if (geometry.type === 'Polygon') {
        geometry.coordinates.forEach((ring) => ring.forEach(applyPoint));
    } else if (geometry.type === 'MultiPolygon') {
        geometry.coordinates.forEach((polygon) => polygon.forEach((ring) => ring.forEach(applyPoint)));
    } else {
        return null;
    }

    if (!Number.isFinite(minX)) return null;
    return { west: minX, south: minY, east: maxX, north: maxY };
};

const closeRing = (ring) => {
    if (!Array.isArray(ring) || ring.length < 4) return null;
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) return ring;
    return [...ring, [first[0], first[1]]];
};

const buildOutsideSwedenMask = (swedenFeatureCollection) => {
    if (!swedenFeatureCollection) return null;
    const features = (() => {
        if (swedenFeatureCollection.type === 'FeatureCollection') {
            return swedenFeatureCollection.features || [];
        }
        if (swedenFeatureCollection.type === 'Feature') {
            return [swedenFeatureCollection];
        }
        if (swedenFeatureCollection.type && swedenFeatureCollection.coordinates) {
            return [{ type: 'Feature', properties: {}, geometry: swedenFeatureCollection }];
        }
        return [];
    })();

    if (!features.length) return null;
    const holes = [];

    features.forEach((featureEntry) => {
        const geometry = featureEntry?.geometry;
        if (!geometry) return;

        if (geometry.type === 'Polygon') {
            const outer = closeRing(geometry.coordinates?.[0]);
            if (outer) holes.push(outer);
            return;
        }

        if (geometry.type === 'MultiPolygon') {
            geometry.coordinates?.forEach((polygon) => {
                const outer = closeRing(polygon?.[0]);
                if (outer) holes.push(outer);
            });
        }
    });

    if (!holes.length) return null;

    return {
        type: 'Feature',
        properties: { id: 'outside-sweden-mask' },
        geometry: {
            type: 'Polygon',
            coordinates: [MASK_OUTER_RING, ...holes],
        },
    };
};

const MapInstanceBridge = ({ onMapReady }) => {
    const map = useMap();
    useEffect(() => {
        onMapReady(map || null);
        return () => {
            onMapReady(null);
        };
    }, [map, onMapReady]);
    return null;
};

const ListViewPage = ({ languageState, darkMode = false, onSwitchView = null }) => {
    const navigate = useNavigate();
    // OpenFreeMap first; Stadia (then OSM) only if it fails this session.
    const [useOpenFreeMap, setUseOpenFreeMap] = useState(() => !openFreeMapFailedThisSession());
    const [useStadiaTiles, setUseStadiaTiles] = useState(() => {
        try {
            return window.sessionStorage.getItem(TILE_PROVIDER_FALLBACK_SESSION_KEY) !== '1';
        } catch (error) {
            return true;
        }
    });
    const [mapInstance, setMapInstance] = useState(null);
    // Metric, Färger mode and legend are shared with kartvy and kept across switches (mapToggles.js).
    const [checkedState, setCheckedState] = useState(() => readSavedCheckedState({
        squareMeterPriceApartment: false,
        squareMeterPriceHouse: false,
        ownedVsRented: false,
        education: false,
        income: false,
        unemployment: false,
        totalScore: true,
        // Default to "Linjer" (outlines in the metric colour, no fill): gentler on a first visit than the
        // whole country filled in. A saved map_toggles cookie overrides this (mapToggles.js).
        hideMap: true,
        // With hideMap: no lines either (colourMode.js).
        hideLines: false,
    }));
    const [, setCustomFilter] = useState({
        house: [1, 6],
        apartment: [1, 6],
        education: [1, 6],
        owned: [1, 6],
        income: [1, 6],
        unemployment: [1, 6],
        totalScore: [1, 6],
    });
    const [, setCustomFilterActive] = useState(false);
    const [, setKey] = useState(0);
    // The filters last applied (both views share them, savedFilters.js): applied from the start.
    const [listingFilters, setListingFilters] = useState(() => readSavedFilters().listingFilters);
    const hasTrackedListingFiltersRef = useRef(false);
    const [topojsonFeature, setTopojsonFeature] = useState(null);
    const [swedenCountryGeometry, setSwedenCountryGeometry] = useState(null);
    const outsideSwedenMask = useMemo(
        () => buildOutsideSwedenMask(swedenCountryGeometry || topojsonFeature),
        [swedenCountryGeometry, topojsonFeature],
    );
    const [scoringSourceData, setScoringSourceData] = useState(null);
    const [overlayTopology, setOverlayTopology] = useState(null);
    // Each area is scored against the other areas in its kommun, topped up with neighbouring
    // areas when the kommun has fewer than 20 (see neighbourhoodScoring.js). Needs the overlay topology.
    // Recomputed when the colour scheme changes (Färgkarta's Färger / Värmekarta), so the cache holds its colours.
    const [colourScheme] = useColourScheme();
    const neighbourhoodScoring = useMemo(
        () => (scoringSourceData && overlayTopology
            ? computeNeighbourhoodScoring(scoringSourceData, overlayTopology)
            : null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [scoringSourceData, overlayTopology, colourScheme],
    );
    const colorCache = neighbourhoodScoring ? neighbourhoodScoring.colorCache : null;
    // Raw values come from areaData's areaStats; neighbourhood scores are merged over them.
    const areaStats = useMemo(
        () => (neighbourhoodScoring ? { ...rawAreaStats, ...neighbourhoodScoring.stats } : rawAreaStats),
        [neighbourhoodScoring],
    );
    const [searchDraft, setSearchDraft] = useState('');
    const [activeSuggestion, setActiveSuggestion] = useState(null);
    // What a reload (or a switch from kartvy) brings back: the picked areas are shared with kartvy and kept
    // (savedSelection.js); the tab, page and map position are this view's own state for the session.
    const restoredRef = useRef(null);
    if (!restoredRef.current) {
        const saved = readSavedSelection();
        const suggestions = listvySuggestionsFromSaved(saved, normalizeSearchText);
        restoredRef.current = {
            saved,
            suggestions,
            view: readViewState('listvy'),
            // Picked in kartvy since: the map fits them instead of going back to where it was.
            fromOtherView: suggestions.length > 0 && saved.savedBy !== 'listvy',
            // Saved again only once something changes (so just opening the view isn't "picked here").
            signature: suggestions.map((entry) => `${entry.id}:${(entry.excludedRegsoIds || []).join(',')}`).join('|'),
            page: Number(readViewState('listvy').page) || 1,
        };
    }
    const restored = restoredRef.current;
    const [selectedSuggestions, setSelectedSuggestions] = useState(() => restored.suggestions);
    // The landing only shows until the first search of this page load; clearing the search afterwards does
    // not bring it back (a reload does, since the page starts fresh). A restored search counts as "searched".
    const [hasSearched, setHasSearched] = useState(() => restored.suggestions.length > 0);
    const [selectionResultsById, setSelectionResultsById] = useState({});
    const [selectionMapResultsById, setSelectionMapResultsById] = useState({});
    const [selectionLoadingById, setSelectionLoadingById] = useState({});
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [ortKommunPairs, setOrtKommunPairs] = useState([]);
    const [kommunNames, setKommunNames] = useState([]);
    // [code, normalized name] per kommun (public/kommunKoder.json), so a kommun search knows its areas even
    // when none of its homes come back (a filter leaves them all out).
    const [kommunCodeList, setKommunCodeList] = useState([]);
    // code -> the kommun's plain name ("Österåker"), for the suggestions' muted place (suggestionParts).
    const [kommunNameByCode, setKommunNameByCode] = useState({});
    useEffect(() => {
        let cancelled = false;
        fetch('/kommunKoder.json')
            .then((response) => (response.ok ? response.json() : {}))
            .catch(() => ({}))
            .then((codes) => {
                if (cancelled) return;
                setKommunCodeList(Object.entries(codes || {}).map(([code, name]) => [code, normalizeSearchText(name)]));
                setKommunNameByCode(codes && typeof codes === 'object' ? codes : {});
            });
        return () => {
            cancelled = true;
        };
    }, []);
    const [regsoNameEntries, setRegsoNameEntries] = useState([]);
    const [postortAreas, setPostortAreas] = useState([]);
    const [selectionTotalsById, setSelectionTotalsById] = useState({});
    const [currentListingsPage, setCurrentListingsPage] = useState(1);
    const [listingFeedTab, setListingFeedTab] = useState(() => (
        restored.view.tab === LISTING_FEED_TAB_COMING_SOON ? LISTING_FEED_TAB_COMING_SOON : LISTING_FEED_TAB_FOR_SALE
    ));
    // The favourites tab shows saved listings instead of the search; listingFeedTab keeps the search's tab.
    const [showFavorites, setShowFavorites] = useState(() => restored.view.favorites === true);
    const { favorites } = useFavorites();
    // Bumps when a listing is opened in the full view, so the map markers redraw with the "visited" look.
    const visitedVersion = useVisitedVersion();
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [hoveredListingId, setHoveredListingId] = useState(null);
    const hoveredListingIdRef = useRef(null);
    hoveredListingIdRef.current = hoveredListingId;
    // The info panel under the map owns the area / map-marker hover state (ListViewInfoPanel.js).
    const infoPanelRef = useRef(null);
    const [mapSelectedListingId, setMapSelectedListingId] = useState(null);
    const [fullViewListing, setFullViewListing] = useState(null);
    // Screen point of the map marker the popup was opened from (it grows out of it); null from the list.
    const [fullViewOpenFrom, setFullViewOpenFrom] = useState(null);
    const [fullViewDetailsLoading, setFullViewDetailsLoading] = useState(false);
    const [fullViewDetailsError, setFullViewDetailsError] = useState('');
    const [mapZoom, setMapZoom] = useState(5);
    const showListingIcons = mapZoom >= LIST_VIEW_ICON_ZOOM_THRESHOLD;
    const [mapViewport, setMapViewport] = useState(null);
    const [mapPanesReady, setMapPanesReady] = useState(false);
    const [helpBoxCollapsed, setHelpBoxCollapsed] = useState(() => readSavedLegendCollapsed(isMobileViewportOnLoad()));
    useEffect(() => {
        saveMapToggles(checkedState, helpBoxCollapsed);
    }, [checkedState, helpBoxCollapsed]);
    // The admin panel's map speed test drives this map (perfRun.js).
    useEffect(() => exposeMapForPerf(mapInstance, 'listvy'), [mapInstance]);

    // "Av" (hideOverlay): no area overlay at all — hide every area-derived pane so only the base map and
    // listing markers show (the areas are already invisible via hideMap + hideLines; the legend and area
    // hover are gated elsewhere). Matches kartvy.
    useEffect(() => {
        if (!mapInstance) return;
        const off = Boolean(checkedState.hideOverlay);
        mapInstance.getContainer().classList.toggle('is-overlay-off', off);
        ['areaSelectionPane', 'areaRevealVeilPane', 'filteredAreaBadgePane', 'filteredAreaCoverPane',
            'greyAreaCoverPane', 'greyAreaBorderPane', LIST_VIEW_HOVER_TILE_PANE].forEach((name) => {
            const pane = mapInstance.getPane(name);
            if (pane) pane.style.display = off ? 'none' : '';
        });
    }, [mapInstance, checkedState.hideOverlay]);
    const feedColumnRef = useRef(null);
    const pageRootRef = useRef(null);
    const mapColumnRef = useRef(null);
    const mapColumnSlideTimerRef = useRef(null);
    // Desktop only: the map/info column can be slid out of the way to give the listings more room.
    const [mapColumnCollapsed, setMapColumnCollapsed] = useState(false);
    // The listing popup opens straight at its floor plans (from a card's floor plan tile).
    const [fullViewAtFloorplan, setFullViewAtFloorplan] = useState(false);
    const [mapColumnSliding, setMapColumnSliding] = useState(false);
    // The column's width when it was last open; its content keeps this width while sliding so the
    // map and panels don't reflow on every frame.
    const [mapColumnWidth, setMapColumnWidth] = useState(null);
    const mobileSearchFormRef = useRef(null);
    const selectedSuggestionIdsRef = useRef(new Set());
    const selectedSuggestionsRef = useRef([]);
    const selectionResultsByIdRef = useRef({});
    const searchFormRef = useRef(null);
    const landingSearchFormRef = useRef(null);
    // The first search auto-scrolls to the first listing once; afterwards the page behaves as usual.
    const firstSearchScrolledRef = useRef(false);
    // On the landing the overlay fades down the (fixed Stockholm) view by latitude: north of l1 = colour
    // fills, between l1 and l2 = colour lines only, south of l2 = no overlay (50 % / 35 % / 15 % of the view).
    const landingBandsRef = useRef(null);
    const regsoTileToggleHandlerRef = useRef(null);
    const helpBoxHostRef = useRef(null);
    const previousRegsoSliderFilterKeyRef = useRef(null);
    const listingLayerRef = useRef(null);
    const listingLayerBuildRafRef = useRef(null);
    const fullViewDetailsCacheRef = useRef(new Map());
    const pageFetchRequestRef = useRef(0);
    const mapFetchRequestRef = useRef(0);
    const pendingSuggestionFocusRef = useRef('');
    const stadiaTileFailedRef = useRef(false);
    const renderedMarkerByKeyRef = useRef(new Map());
    const listingMiniCardRef = useRef(null);
    // The mini card reads these when a home is tapped, not when it was attached.
    const listingPeekStateRef = useRef({ areaStats: null, swedish: true, handleMapListingClick: null });
    const renderedMarkerSignatureByKeyRef = useRef(new Map());
    const listingAnimatorRef = useRef(null);
    // Zoom, groups per area and entries last drawn, to tell when a zoom regroups the markers.
    const drawnListingGroupsRef = useRef(null);
    // The markers still growing in on the overlay (held back from the layer) while homes appear or go.
    const presenceHeldKeysRef = useRef(null);
    // Whether homes of this area are on the map (a click that turns it off waits for them to shrink away).
    const listingsShownInArea = useCallback((regsoCode) => (drawnListingGroupsRef.current?.entries || []).some((entry) => (
        (entry.listings || [entry.listing]).some((listing) => listing && extractListingRegsoCode(listing) === regsoCode)
    )), []);
    // The drawer's search: homes going away shrink first, then every area the new filters change fades, then
    // the new homes grow in (areaToggleFade.js).
    const handleBeforeFilterApply = useCallback(() => {
        noteFiltersApplied({ hold: Boolean(drawnListingGroupsRef.current?.entries?.length) });
        fadeAreaLayers(regsoGeoJsonRef.current);
    }, []);
    // The selection overlay asks this before dimming areas a search drops (areaSelectionOverlay.js).
    useEffect(() => {
        setListingsShownCheck(listingsShownInArea);
        return () => setListingsShownCheck(null);
    }, [listingsShownInArea]);
    // Areas a search just added stay without homes until they've lit up; this re-runs the markers then.
    const [areaRevealTick, setAreaRevealTick] = useState(0);
    useEffect(() => onAreaRevealChange(() => setAreaRevealTick((tick) => tick + 1)), []);
    const shouldFetchListings = selectedSuggestions.length > 0;
    // Before a search nothing is fetched: the list shows a prompt to search (no nationwide feed).
    const { listings, listingsProcessed } = useActiveListings({
        disabled: true,
        streamResults: false,
    });
    const searchSuggestions = useMemo(
        () => selectedSuggestions,
        [selectedSuggestions],
    );
    const searchResults = useMemo(() => {
        if (!searchSuggestions.length) return null;
        const merged = [];
        const seen = new Set();
        searchSuggestions.forEach((suggestion) => {
            const suggestionListings = selectionResultsById[suggestion.id] || [];
            suggestionListings.forEach((listing) => {
                const listingId = getListingIdentity(listing);
                if (!listingId || seen.has(listingId)) return;
                seen.add(listingId);
                merged.push(listing);
            });
        });
        return merged.slice(0, LISTINGS_PAGE_SIZE);
    }, [searchSuggestions, selectionResultsById]);
    const mapSearchResults = useMemo(() => {
        if (!searchSuggestions.length) return null;
        const merged = [];
        const seen = new Set();
        searchSuggestions.forEach((suggestion) => {
            const suggestionId = String(suggestion?.id || '').trim();
            if (!suggestionId) return;
            const mapResults = selectionMapResultsById[suggestionId];
            const suggestionListings = Array.isArray(mapResults)
                ? mapResults
                : (selectionResultsById[suggestionId] || []);
            // Areas deselected inside this search disappear at once; the refetch without them follows.
            const excludedInSearch = Array.isArray(suggestion.excludedRegsoIds) && suggestion.excludedRegsoIds.length
                ? new Set(suggestion.excludedRegsoIds)
                : null;
            suggestionListings.forEach((listing) => {
                if (excludedInSearch && excludedInSearch.has(extractListingRegsoCode(listing))) return;
                const listingId = getListingIdentity(listing);
                if (!listingId || seen.has(listingId)) return;
                seen.add(listingId);
                merged.push(listing);
            });
        });
        return merged;
    }, [searchSuggestions, selectionMapResultsById, selectionResultsById]);
    const searchLoading = useMemo(
        () => Object.values(selectionLoadingById).some(Boolean),
        [selectionLoadingById],
    );
    const regsoIdsByKommunCode = useMemo(() => {
        const byKommunCode = new Map();
        regsoNameEntries.forEach((entry) => {
            const regsoId = String(entry?.regsoId || '').trim();
            const codeMatch = regsoId.match(/^(\d{4})/);
            const kommunCode = codeMatch?.[1] ? String(codeMatch[1]).trim() : '';
            if (!kommunCode) return;
            const existing = byKommunCode.get(kommunCode);
            if (existing) {
                existing.add(regsoId);
                return;
            }
            byKommunCode.set(kommunCode, new Set([regsoId]));
        });
        return byKommunCode;
    }, [regsoNameEntries]);
    // A län's areas: every RegSO id that starts with its code.
    const regsoIdsForLan = useCallback((suggestion) => {
        const code = suggestion?.lanCode || findLan(suggestion?.lan || suggestion?.label)?.code;
        const ids = [];
        if (!code) return ids;
        regsoIdsByKommunCode.forEach((regsoIds, kommunCode) => {
            if (kommunCode.startsWith(code)) regsoIds.forEach((id) => ids.push(id));
        });
        return ids;
    }, [regsoIdsByKommunCode]);
    // The score beside a suggestion (SearchScore.js): only one that is a single area has one (an area, a street by
    // its area, a postort with one area); a kommun, län, ort or several areas have none.
    const suggestionScore = useCallback((suggestion) => {
        if (!suggestion || suggestion.type === 'kommun' || suggestion.type === 'lan' || suggestion.type === 'ort') return null;
        if (suggestion.regsoId) return searchResultScore([suggestion.regsoId], areaStats);
        return searchResultScore(suggestion.regsoIds, areaStats);
    }, [areaStats]);
    // The RegSO areas each selected search covers (the tiles it colours), minus the ones deselected on the
    // map (`excludedRegsoIds`). `complete` once the search's map results have loaded (area searches know
    // theirs at once); until then the set may still grow.
    const regsoCoverageBySuggestionId = useMemo(() => {
        const coverage = new Map();
        searchSuggestions.forEach((suggestion) => {
            const suggestionId = String(suggestion?.id || '').trim();
            if (!suggestionId) return;
            const regsoIds = new Set();
            if (suggestion.regsoId) {
                regsoIds.add(String(suggestion.regsoId).trim());
                coverage.set(suggestionId, { regsoIds, complete: true });
                return;
            }
            const excluded = new Set(Array.isArray(suggestion.excludedRegsoIds) ? suggestion.excludedRegsoIds : []);
            const lanRegsoIds = suggestion.type === 'lan' ? regsoIdsForLan(suggestion) : [];
            if (lanRegsoIds.length > 0) {
                lanRegsoIds.forEach((id) => {
                    if (!excluded.has(id)) regsoIds.add(id);
                });
                coverage.set(suggestionId, { regsoIds, complete: true });
                return;
            }
            if (Array.isArray(suggestion.regsoIds) && suggestion.regsoIds.length > 0) {
                suggestion.regsoIds.forEach((value) => {
                    const id = String(value || '').trim();
                    if (id && !excluded.has(id)) regsoIds.add(id);
                });
                coverage.set(suggestionId, { regsoIds, complete: true });
                return;
            }
            const mapResults = selectionMapResultsById[suggestionId];
            const suggestionListings = Array.isArray(mapResults)
                ? mapResults
                : (selectionResultsById[suggestionId] || []);
            const kommunCodes = new Set();
            const addKommunCode = (id) => {
                const codeMatch = id.match(/^(\d{4})/);
                const kommunCode = codeMatch?.[1] ? String(codeMatch[1]).trim() : '';
                if (kommunCode) kommunCodes.add(kommunCode);
            };
            suggestionListings.forEach((listing) => {
                const id = extractListingRegsoCode(listing);
                if (!id) return;
                regsoIds.add(id);
                addKommunCode(id);
            });
            // Deselected areas still identify the kommun (their listings are no longer returned).
            excluded.forEach(addKommunCode);
            // So does its name: without it a kommun whose homes all fall outside the filters would lose its
            // areas, which changed the excluded areas sent to the server, brought the homes back, and so on.
            if (suggestion?.type === 'kommun') {
                const kommunCode = kommunCodeForName(kommunCodeList, suggestion.kommun);
                if (kommunCode) kommunCodes.add(kommunCode);
            }

            // For kommun searches, include all RegSO tiles in resolved kommun code(s),
            // not only those with listings, so zero-listing tiles remain colored.
            if (suggestion?.type === 'kommun' && kommunCodes.size > 0) {
                kommunCodes.forEach((kommunCode) => {
                    const regsoInKommun = regsoIdsByKommunCode.get(kommunCode);
                    if (!regsoInKommun) return;
                    regsoInKommun.forEach((regsoId) => {
                        if (regsoId) regsoIds.add(regsoId);
                    });
                });
            }
            excluded.forEach((regsoId) => regsoIds.delete(regsoId));
            coverage.set(suggestionId, { regsoIds, complete: Array.isArray(mapResults) });
        });
        return coverage;
    }, [searchSuggestions, selectionMapResultsById, selectionResultsById, regsoIdsByKommunCode, regsoIdsForLan, kommunCodeList]);
    const searchRegsoSet = useMemo(() => {
        const regsoIds = new Set();
        regsoCoverageBySuggestionId.forEach(({ regsoIds: covered }) => {
            covered.forEach((regsoId) => regsoIds.add(regsoId));
        });
        return regsoIds;
    }, [regsoCoverageBySuggestionId]);
    // Areas clicked off inside a search: grey, like the areas the area filter leaves out, not dimmed.
    const excludedSearchRegsoSet = useMemo(() => {
        const regsoIds = new Set();
        searchSuggestions.forEach((suggestion) => {
            (Array.isArray(suggestion?.excludedRegsoIds) ? suggestion.excludedRegsoIds : []).forEach((regsoId) => {
                if (!searchRegsoSet.has(regsoId)) regsoIds.add(regsoId);
            });
        });
        return regsoIds;
    }, [searchRegsoSet, searchSuggestions]);
    const hasSearchSelection = searchSuggestions.length > 0;
    const allowMapListingInteractions = showListingIcons;
    const stadiaTileUrl = LOCK_SINGLE_TILE_STYLE
        ? TILE_URL_TONER
        : (checkedState.hideMap ? TILE_URL_BRIGHT : TILE_URL_TONER);
    const tileUrl = useStadiaTiles ? stadiaTileUrl : OSM_TILE_URL;
    // Only worth it when the base map is the white one and the regular one differs from it.
    const showHoverTileLayer = mapPanesReady && (useOpenFreeMap || useStadiaTiles)
        && !LOCK_SINGLE_TILE_STYLE && !checkedState.hideMap;
    const hoverTileLayerRef = useRef(null);
    // Lighter area outlines when zoomed out (areaGeometry.js).
    const regsoGeoJsonRef = useRef(null);
    const areaGeometrySwitchRef = useRef(null);
    if (!areaGeometrySwitchRef.current) areaGeometrySwitchRef.current = createAreaGeometrySwitch();
    const areaMapRevealRef = useRef(null);
    if (!areaMapRevealRef.current) areaMapRevealRef.current = createAreaMapReveal(() => hoverTileLayerRef.current);
    const handleOpenFreeMapFail = useCallback(() => {
        rememberOpenFreeMapFailure();
        setUseOpenFreeMap(false);
    }, []);
    // The OpenFreeMap hover layer moves its canvas while panning: re-apply the clip when it does.
    const handleHoverGlLayer = useCallback((layer) => {
        const previous = hoverTileLayerRef.current;
        if (previous && previous !== layer && typeof previous.off === 'function') {
            previous.off('reposition', areaMapRevealRef.current.applyClip);
        }
        hoverTileLayerRef.current = layer;
        if (layer) {
            layer.on('reposition', areaMapRevealRef.current.applyClip);
            areaMapRevealRef.current.reset();
        }
    }, []);

    useEffect(() => {
        if (!overlayTopology) return undefined;
        // Built after the map has drawn, so it doesn't delay the first paint.
        const build = () => {
            areaGeometrySwitchRef.current.setTopology(overlayTopology);
            if (mapInstance) areaGeometrySwitchRef.current.apply(regsoGeoJsonRef.current, mapInstance.getZoom());
        };
        if (typeof window.requestIdleCallback === 'function') {
            const id = window.requestIdleCallback(build, { timeout: 2000 });
            return () => window.cancelIdleCallback(id);
        }
        const id = window.setTimeout(build, 300);
        return () => window.clearTimeout(id);
    }, [mapInstance, overlayTopology]);

    useEffect(() => {
        if (!mapInstance) return undefined;
        // Switch the area outlines before the zoom animation (Leaflet re-projects at zoomend), and catch
        // zooms without an animation at zoomend.
        const onZoomAnim = (event) => areaGeometrySwitchRef.current.apply(regsoGeoJsonRef.current, event.zoom, false);
        const onZoomEnd = () => areaGeometrySwitchRef.current.apply(regsoGeoJsonRef.current, mapInstance.getZoom());
        mapInstance.on('zoomanim', onZoomAnim);
        mapInstance.on('zoomend', onZoomEnd);
        return () => {
            mapInstance.off('zoomanim', onZoomAnim);
            mapInstance.off('zoomend', onZoomEnd);
        };
    }, [mapInstance]);

    useEffect(() => {
        if (!mapInstance) return undefined;
        // Layer pixels change with zoom: animate the clip along with the zoom, then set it exactly after.
        const { animateClipForZoom, applyClip } = areaMapRevealRef.current;
        mapInstance.on('zoomanim', animateClipForZoom);
        mapInstance.on('zoomend viewreset', applyClip);
        return () => {
            mapInstance.off('zoomanim', animateClipForZoom);
            mapInstance.off('zoomend viewreset', applyClip);
        };
    }, [mapInstance]);

    useEffect(() => {
        // Hide until something is hovered; also resets when the layer is (re)created.
        if (!showHoverTileLayer) areaMapRevealRef.current.reset();
        else areaMapRevealRef.current.applyClip();
    }, [showHoverTileLayer]);
    const switchToOsmTiles = useCallback(() => {
        if (stadiaTileFailedRef.current || !useStadiaTiles) return;
        stadiaTileFailedRef.current = true;
        try {
            window.sessionStorage.setItem(TILE_PROVIDER_FALLBACK_SESSION_KEY, '1');
        } catch (error) {
            // no-op if storage is unavailable
        }
        setUseStadiaTiles(false);
    }, [useStadiaTiles]);
    const handleTileLoadError = useCallback(() => {
        switchToOsmTiles();
    }, [switchToOsmTiles]);

    useEffect(() => {
        if (useOpenFreeMap || !useStadiaTiles) return;
        let cancelled = false;
        const controller = new AbortController();
        const runProbe = async () => {
            try {
                const response = await fetch(STADIA_PROBE_URL, {
                    method: 'GET',
                    cache: 'no-store',
                    mode: 'cors',
                    signal: controller.signal,
                });
                if (cancelled) return;
                if (!response.ok) {
                    switchToOsmTiles();
                }
            } catch (error) {
                if (!cancelled) {
                    switchToOsmTiles();
                }
            }
        };
        runProbe();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [switchToOsmTiles, useOpenFreeMap, useStadiaTiles]);
    const regsoSliderFilterKey = useMemo(
        () => JSON.stringify({
            totalValue: listingFilters.totalValue,
            apartmentValue: listingFilters.apartmentValue,
            houseValue: listingFilters.houseValue,
            incomeValue: listingFilters.incomeValue,
            unemploymentValue: listingFilters.unemploymentValue,
            ownedValue: listingFilters.ownedValue,
            educationValue: listingFilters.educationValue,
        }),
        [
            listingFilters.apartmentValue,
            listingFilters.educationValue,
            listingFilters.houseValue,
            listingFilters.incomeValue,
            listingFilters.ownedValue,
            listingFilters.totalValue,
            listingFilters.unemploymentValue,
        ],
    );
    const pageSourceListings = useMemo(() => {
        const source = hasSearchSelection
            ? dedupeListings(Array.isArray(searchResults) ? searchResults : [])
            : dedupeListings(listings);
        return source;
    }, [hasSearchSelection, listings, searchResults]);
    const mapSourceListings = useMemo(() => {
        const source = hasSearchSelection
            ? dedupeListings(Array.isArray(mapSearchResults) ? mapSearchResults : [])
            : dedupeListings(listings);
        return source;
    }, [hasSearchSelection, listings, mapSearchResults]);
    const dateOrderedListings = useMemo(
        () => [...pageSourceListings].sort((a, b) => getListingAgeDays(a) - getListingAgeDays(b)),
        [pageSourceListings],
    );
    const mapDateOrderedListings = useMemo(
        () => [...mapSourceListings].sort((a, b) => getListingAgeDays(a) - getListingAgeDays(b)),
        [mapSourceListings],
    );

    useEffect(() => {
        selectedSuggestionIdsRef.current = new Set(selectedSuggestions.map((entry) => entry.id));
    }, [selectedSuggestions]);

    useEffect(() => {
        selectedSuggestionsRef.current = selectedSuggestions;
    }, [selectedSuggestions]);

    useEffect(() => {
        selectionResultsByIdRef.current = selectionResultsById;
    }, [selectionResultsById]);

    const searchContextKey = useMemo(
        () => selectedSuggestions
            .map((entry) => String(entry?.id || '').trim())
            .filter(Boolean)
            .sort()
            .join('|'),
        [selectedSuggestions],
    );

    useEffect(() => {
        setCurrentListingsPage(1);
    }, [searchContextKey]);

    useEffect(() => {
        setCurrentListingsPage(1);
    }, [listingFeedTab, shouldFetchListings]);

    const removeSuggestionsByIds = useCallback((ids) => {
        const idSet = new Set(
            (Array.isArray(ids) ? ids : [ids])
                .map((value) => String(value || '').trim())
                .filter(Boolean),
        );
        if (!idSet.size) return;

        idSet.forEach((id) => selectedSuggestionIdsRef.current.delete(id));
        setSelectedSuggestions((prev) => prev.filter((entry) => !idSet.has(String(entry?.id || '').trim())));
        setSelectionResultsById((prev) => {
            let changed = false;
            const next = { ...prev };
            idSet.forEach((id) => {
                if (!(id in next)) return;
                delete next[id];
                changed = true;
            });
            return changed ? next : prev;
        });
        setSelectionMapResultsById((prev) => {
            let changed = false;
            const next = { ...prev };
            idSet.forEach((id) => {
                if (!(id in next)) return;
                delete next[id];
                changed = true;
            });
            return changed ? next : prev;
        });
        setSelectionTotalsById((prev) => {
            let changed = false;
            const next = { ...prev };
            idSet.forEach((id) => {
                if (!(id in next)) return;
                delete next[id];
                changed = true;
            });
            return changed ? next : prev;
        });
        setSelectionLoadingById((prev) => {
            let changed = false;
            const next = { ...prev };
            idSet.forEach((id) => {
                if (!next[id]) return;
                delete next[id];
                changed = true;
            });
            return changed ? next : prev;
        });
        setActiveSuggestion((current) => {
            const currentId = String(current?.id || '').trim();
            return currentId && idSet.has(currentId) ? null : current;
        });
        const pendingId = String(pendingSuggestionFocusRef.current || '').trim();
        if (pendingId && idSet.has(pendingId)) {
            pendingSuggestionFocusRef.current = '';
        }
    }, []);

    // A bigger search replaces the smaller ones inside it: when a search's coverage is known, earlier
    // searches whose areas all lie within it (e.g. Svedmyra, then Stockholms kommun) are removed.
    // Earlier searches with areas outside it stay; a smaller search added after a bigger one stays too.
    useEffect(() => {
        const removals = [];
        selectedSuggestions.forEach((later, laterIndex) => {
            const laterCoverage = regsoCoverageBySuggestionId.get(String(later?.id || '').trim());
            if (!laterCoverage?.complete || !laterCoverage.regsoIds.size) return;
            selectedSuggestions.slice(0, laterIndex).forEach((earlier) => {
                const earlierId = String(earlier?.id || '').trim();
                if (!earlierId || removals.includes(earlierId)) return;
                const earlierCoverage = regsoCoverageBySuggestionId.get(earlierId);
                if (!earlierCoverage?.complete || !earlierCoverage.regsoIds.size) return;
                for (const regsoId of earlierCoverage.regsoIds) {
                    if (!laterCoverage.regsoIds.has(regsoId)) return;
                }
                removals.push(earlierId);
            });
        });
        // A search with every area deselected is gone too.
        const emptied = selectedSuggestions
            .filter((entry) => {
                const entryId = String(entry?.id || '').trim();
                const coverage = regsoCoverageBySuggestionId.get(entryId);
                return entryId && !removals.includes(entryId) && Array.isArray(entry?.excludedRegsoIds)
                    && entry.excludedRegsoIds.length > 0 && coverage?.complete && coverage.regsoIds.size === 0;
            })
            .map((entry) => String(entry.id).trim());
        if (removals.length) {
            track('search.merged', { removed: removals.length });
        }
        if (removals.length || emptied.length) removeSuggestionsByIds([...removals, ...emptied]);
    }, [regsoCoverageBySuggestionId, removeSuggestionsByIds, selectedSuggestions]);

    useEffect(() => {
        if (!shouldFetchListings || !listingsProcessed || !listings.length || !selectedSuggestions.length) return;
        setSelectionResultsById((prev) => {
            let changed = false;
            const next = { ...prev };
            selectedSuggestions.forEach((suggestion) => {
                const suggestionId = String(suggestion?.id || '').trim();
                if (!suggestionId) return;
                if (selectionLoadingById[suggestionId]) return;
                const existing = prev[suggestionId];
                if (Array.isArray(existing) && existing.length > 0) return;

                let computed = null;
                if ((suggestion.type === 'omrade' || suggestion.type === 'omrade-map') && suggestion.regsoId) {
                    computed = listings.filter(
                        (listing) => extractListingRegsoCode(listing) === suggestion.regsoId,
                    );
                } else if ((suggestion.type === 'omrade-word' || suggestion.type === 'postort')
                    && Array.isArray(suggestion.regsoIds) && suggestion.regsoIds.length > 0) {
                    const regsoIdSet = new Set(
                        suggestion.regsoIds.map((value) => String(value).trim()).filter(Boolean),
                    );
                    computed = listings.filter((listing) => regsoIdSet.has(extractListingRegsoCode(listing)));
                }

                if (!Array.isArray(computed)) return;
                next[suggestionId] = computed;
                changed = true;
            });
            return changed ? next : prev;
        });
    }, [listings, listingsProcessed, selectedSuggestions, selectionLoadingById, shouldFetchListings]);

    useEffect(() => {
        let cancelled = false;
        const loadSearchSources = async () => {
            try {
                const [ortKommunResponse, regsoNameResponse] = await Promise.all([
                    fetch(ORT_KOMMUN_FILE_URL),
                    fetch(REGSO_NAME_FILE_URL),
                ]);
                if (!ortKommunResponse.ok || !regsoNameResponse.ok) return;
                const [ortKommunText, regsoNameText] = await Promise.all([
                    ortKommunResponse.text(),
                    regsoNameResponse.text(),
                ]);
                if (cancelled) return;
                setOrtKommunPairs(parseOrtKommunPairs(ortKommunText));
                setKommunNames(normalizeKommunNames(kommunNamesJson));
                setRegsoNameEntries(parseRegsoNames(regsoNameText));
            } catch (error) {
                if (!cancelled) {
                    setOrtKommunPairs([]);
                    setKommunNames([]);
                    setRegsoNameEntries([]);
                }
            }
        };
        loadSearchSources();
        // Optional: without it, postorter are only found through area names.
        fetch(POSTORT_AREAS_FILE_URL)
            .then((response) => (response.ok ? response.json() : null))
            .then((payload) => {
                if (!cancelled && Array.isArray(payload?.postorter)) setPostortAreas(payload.postorter);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            const desktopSearchContains = Boolean(searchFormRef.current?.contains(event.target));
            const mobileSearchContains = Boolean(mobileSearchFormRef.current?.contains(event.target));
            const landingSearchContains = Boolean(landingSearchFormRef.current?.contains(event.target));
            if (desktopSearchContains || mobileSearchContains || landingSearchContains) return;
            setShowSuggestions(false);
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);

    useEffect(() => {
        setMapPanesReady(false);
        if (!mapInstance) return;
        if (!mapInstance.getPane(LIST_VIEW_REGSO_PANE)) {
            const pane = mapInstance.createPane(LIST_VIEW_REGSO_PANE);
            pane.style.zIndex = '405';
        }
        if (!mapInstance.getPane(LIST_VIEW_LISTINGS_PANE)) {
            const pane = mapInstance.createPane(LIST_VIEW_LISTINGS_PANE);
            pane.style.zIndex = '640';
        }
        if (!mapInstance.getPane(LIST_VIEW_HOVER_TILE_PANE)) {
            // Above the white map (tile pane, 200), below the areas.
            const pane = mapInstance.createPane(LIST_VIEW_HOVER_TILE_PANE);
            pane.style.zIndex = '250';
            pane.style.pointerEvents = 'none';
        }
        if (!mapInstance.getPane(OUTSIDE_SWEDEN_MASK_PANE)) {
            const pane = mapInstance.createPane(OUTSIDE_SWEDEN_MASK_PANE);
            pane.style.zIndex = '445';
            pane.style.pointerEvents = 'none';
        }
        if (!mapInstance.getPane(OPEN_FREE_MAP_LABEL_PANE)) {
            // The map's labels, above the areas, below the listing markers and popups.
            const pane = mapInstance.createPane(OPEN_FREE_MAP_LABEL_PANE);
            pane.style.zIndex = '440';
            pane.style.pointerEvents = 'none';
        }
        setMapPanesReady(true);
    }, [mapInstance]);

    useEffect(() => {
        if (!mapInstance || !mapPanesReady) return;
        const listingsPane = mapInstance.getPane(LIST_VIEW_LISTINGS_PANE);
        if (!listingsPane) return;
        // During active area search/selection, keep tile clicks reliable by letting
        // interactions pass through the listing pane.
        listingsPane.style.pointerEvents = allowMapListingInteractions ? 'auto' : 'none';
    }, [allowMapListingInteractions, mapInstance, mapPanesReady]);

    useEffect(() => {
        if (!mapInstance) return;
        const syncViewport = () => {
            setMapZoom(mapInstance.getZoom() ?? 5);
            const bounds = mapInstance.getBounds();
            setMapViewport({
                south: bounds.getSouth(),
                west: bounds.getWest(),
                north: bounds.getNorth(),
                east: bounds.getEast(),
            });
        };
        syncViewport();
        mapInstance.on('zoomend', syncViewport);
        mapInstance.on('moveend', syncViewport);
        return () => {
            mapInstance.off('zoomend', syncViewport);
            mapInstance.off('moveend', syncViewport);
        };
    }, [mapInstance]);

    useEffect(() => {
        let cancelled = false;
        const loadColorData = async () => {
            try {
                const result = await loadAreaData();
                if (!cancelled) {
                    fillAreaStats(result);
                    setScoringSourceData(result);
                }
            } catch (error) {
                console.error('Error fetching list view color data:', error);
            }
        };
        loadColorData();
        return () => {
            cancelled = true;
        };
    }, []);

    // Once the page has settled (the landing's picture shown, the map behind it built), what the next steps need is
    // fetched while the visitor reads or types: the street search's postcodes, and kartvy's listings (2.3 MB) into
    // its cache, so a switch to kartvy shows its homes at once (it waited 3.7 s for them on a first visit); phones too
    // (the owner, 2026-11-04: their Karta chip goes to kartvy). Not with Save-Data or on a slow connection. The
    // overlay and area data load from the start.
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const connection = navigator.connection || {};
        const slow = connection.saveData || /(^|-)2g$|^3g$/.test(String(connection.effectiveType || ''));
        if (slow) return undefined;
        let idleId = null;
        const run = () => {
            loadPostcodePostort().catch(() => {});
            prefetchCompactListings();
        };
        const timer = window.setTimeout(() => {
            idleId = window.requestIdleCallback ? window.requestIdleCallback(run, { timeout: 5000 }) : null;
            if (idleId === null) run();
        }, PREFETCH_AFTER_MS);
        return () => {
            window.clearTimeout(timer);
            if (idleId !== null) window.cancelIdleCallback?.(idleId);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        const loadTopology = async () => {
            try {
                const topoData = await loadOverlay();
                if (cancelled || !topoData?.objects) return;
                const objectKeys = Object.keys(topoData.objects);
                if (!objectKeys.length) return;
                const primaryObject = topoData.objects[objectKeys[0]];
                const geo = topoFeatureFromTopojson(topoData, primaryObject);
                if (!cancelled) {
                    setTopojsonFeature(geo);
                    setOverlayTopology(topoData);
                }
            } catch (error) {
                console.error('Error fetching list view overlay:', error);
            }
        };
        loadTopology();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        const loadCountryMask = async () => {
            try {
                const response = await fetch(SWEDEN_COUNTRY_GEOJSON_URL, { cache: 'force-cache' });
                if (!response.ok) {
                    throw new Error(`Failed to fetch Sweden country geometry: ${response.status}`);
                }
                const geoJson = await response.json();
                if (!cancelled) {
                    setSwedenCountryGeometry(geoJson);
                }
            } catch (error) {
                console.warn('ListView Sweden country mask fallback (topojson overlay):', error);
            }
        };
        loadCountryMask();
        return () => {
            cancelled = true;
        };
    }, []);

    // The round "Färgkarta" map button: a menu of what the areas are coloured by (ColourMetricMenu.js).
    const [colourMetricAnchor, setColourMetricAnchor] = useState(null);
    const handleColourMetricSelect = (key) => {
        if (checkedState[key]) return;
        handleMapToggleChange({ target: { name: key, checked: true } });
    };

    // Colours -> lines only -> nothing -> colours (colourMode.js).
    const handleColourModeToggle = () => {
        const next = nextColourModeState(checkedState);
        track('map.colour_mode', { mode: getColourMode(next) });
        setCheckedState(next);
    };

    const handleMapToggleChange = (event) => {
        const { name, checked } = event.target;
        track('map.metric', { metric: name, enabled: Boolean(checked) });
        setCheckedState((prevState) => {
            if (name === 'hideMap') {
                return {
                    ...prevState,
                    hideMap: checked,
                };
            }
            return Object.keys(prevState).reduce((acc, key) => {
                if (key === 'hideMap' || key === 'hideLines' || key === 'hideOverlay') acc[key] = prevState[key];
                else acc[key] = key === name ? checked : false;
                return acc;
            }, {});
        });
    };

    useEffect(() => {
        if (!hasTrackedListingFiltersRef.current) {
            hasTrackedListingFiltersRef.current = true;
            return;
        }
        const summary = summarizeFilters(listingFilters);
        track(summary.fields.length ? 'filter.apply' : 'filter.reset', summary);
    }, [listingFilters]);

    // Analytics: feed paging (page 2+), the for-sale / coming tab, and searches that match nothing.
    const trackedFeedTabRef = useRef(listingFeedTab);
    useEffect(() => {
        if (trackedFeedTabRef.current === listingFeedTab) return;
        trackedFeedTabRef.current = listingFeedTab;
        track('feed.tab', { tab: listingFeedTab === LISTING_FEED_TAB_COMING_SOON ? 'coming' : 'for_sale' });
    }, [listingFeedTab]);
    const favoritesTabTrackedRef = useRef(false);
    useEffect(() => {
        if (!showFavorites) {
            favoritesTabTrackedRef.current = false;
            return;
        }
        if (favoritesTabTrackedRef.current) return;
        favoritesTabTrackedRef.current = true;
        track('feed.tab', { tab: 'favorites', count: favorites.length });
    }, [favorites.length, showFavorites]);
    useEffect(() => {
        if (currentListingsPage > 1) track('feed.page', { page: currentListingsPage });
    }, [currentListingsPage]);

    const filterContext = useMemo(() => {
        const priceMin = parseMinFilterBound(listingFilters.priceMin);
        const priceMax = parseMaxFilterBound(listingFilters.priceMax);
        const roomMin = parseMinFilterBound(listingFilters.roomMin);
        const roomMax = parseMaxFilterBound(listingFilters.roomMax);
        const sqmMin = parseMinFilterBound(listingFilters.sqmMin);
        const sqmMax = parseMaxFilterBound(listingFilters.sqmMax);
        const feeMax = parseMaxFilterBound(listingFilters.feeMax);
        const sqmPriceMin = parseMinFilterBound(listingFilters.sqmPriceMin);
        const sqmPriceMax = parseMaxFilterBound(listingFilters.sqmPriceMax);
        const floorMin = parseMinFilterBound(listingFilters.floorMin);
        const floorMax = parseMaxFilterBound(listingFilters.floorMax);
        const yearBuiltMin = parseMinFilterBound(listingFilters.yearBuiltMin);
        const yearBuiltMax = parseMaxFilterBound(listingFilters.yearBuiltMax);
        // null when off, so the server request (serverListingFilters) leaves them out.
        const balcony = listingFilters.balcony === true ? true : null;
        const elevator = listingFilters.elevator === true ? true : null;
        const newConstruction = listingFilters.newConstruction === 'only' || listingFilters.newConstruction === 'exclude'
            ? listingFilters.newConstruction
            : null;
        return {
            priceMin, priceMax, roomMin, roomMax, sqmMin, sqmMax, feeMax,
            sqmPriceMin, sqmPriceMax, floorMin, floorMax, yearBuiltMin, yearBuiltMax, balcony, elevator, newConstruction,
        };
    }, [listingFilters]);
    const regsoFilterContext = useMemo(() => ({
        totalActive: !isFullRange(listingFilters.totalValue),
        apartmentActive: !isFullRange(listingFilters.apartmentValue),
        houseActive: !isFullRange(listingFilters.houseValue),
        incomeActive: !isFullRange(listingFilters.incomeValue),
        unemploymentActive: !isFullRange(listingFilters.unemploymentValue),
        ownedActive: !isFullRange(listingFilters.ownedValue),
        educationActive: !isFullRange(listingFilters.educationValue),
        totalRange: listingFilters.totalValue,
        apartmentRange: listingFilters.apartmentValue,
        houseRange: listingFilters.houseValue,
        incomeRange: listingFilters.incomeValue,
        unemploymentRange: listingFilters.unemploymentValue,
        ownedRange: listingFilters.ownedValue,
        educationRange: listingFilters.educationValue,
    }), [listingFilters]);
    // Areas within the travel-time limit (Restid till jobbet), or null without a limit; homes in areas outside
    // it are hidden, but the tiles keep their look (only the area scores grey a tile out).
    const { reach: commuteReach } = useCommuteReach();
    const commuteSettings = useCommute();
    // Whether the landing Filter panel is open ('filter' | null) — the Filter button toggles it. Restid lives
    // inside that panel (the Restid section of the reused FilterList), so there is no separate Restid panel.
    const [landingPanel, setLandingPanel] = useState(null);
    const regsoFiltersActive = useMemo(
        () => regsoFilterContext.totalActive
            || regsoFilterContext.apartmentActive
            || regsoFilterContext.houseActive
            || regsoFilterContext.incomeActive
            || regsoFilterContext.unemploymentActive
            || regsoFilterContext.ownedActive
            || regsoFilterContext.educationActive
            || Boolean(commuteReach),
        [commuteReach, regsoFilterContext],
    );
    // Regso filters compare the area's per-metric scores with the selected score ranges.
    const regsoScoreInRange = useCallback((prefix, regsoCode, range) => {
        const score = regsoCode && colorCache ? colorToScore[colorCache[`${prefix}${regsoCode}`]] : null;
        return score != null && score >= range[0] && score <= range[1];
    }, [colorCache]);
    // The area scores alone: these grey a tile out. The travel time only hides homes (passesRegsoCommonFilters).
    const passesRegsoScoreFilters = useCallback((regsoCode) => {
        if (!regsoCode) return false;
        const context = regsoFilterContext;
        if (context.totalActive && !regsoScoreInRange('combined_', regsoCode, context.totalRange)) return false;
        if (context.incomeActive && !regsoScoreInRange('netInc_', regsoCode, context.incomeRange)) return false;
        if (context.unemploymentActive && !regsoScoreInRange('unemployment_', regsoCode, context.unemploymentRange)) return false;
        if (context.ownedActive && !regsoScoreInRange('apartment_', regsoCode, context.ownedRange)) return false;
        if (context.educationActive && !regsoScoreInRange('education_', regsoCode, context.educationRange)) return false;
        return true;
    }, [regsoFilterContext, regsoScoreInRange]);
    const passesRegsoCommonFilters = useCallback((regsoCode) => {
        if (!regsoCode) return false;
        if (commuteReach && !commuteReach.has(regsoCode)) return false;
        return passesRegsoScoreFilters(regsoCode);
    }, [commuteReach, passesRegsoScoreFilters]);
    const passesRegsoListingFilters = useCallback((listing) => {
        if (!regsoFiltersActive) return true;
        const regsoCode = extractListingRegsoCode(listing);
        if (!passesRegsoCommonFilters(regsoCode)) return false;

        if (regsoFilterContext.apartmentActive && listing?.type === 'apartment'
            && !regsoScoreInRange('sqm_price_apartment_', regsoCode, regsoFilterContext.apartmentRange)) return false;
        if (regsoFilterContext.houseActive && listing?.type !== 'apartment'
            && !regsoScoreInRange('sqm_price_house_', regsoCode, regsoFilterContext.houseRange)) return false;
        return true;
    }, [passesRegsoCommonFilters, regsoFilterContext, regsoFiltersActive, regsoScoreInRange]);
    const passesRegsoTileFilters = useCallback((regsoCode) => {
        if (!regsoFiltersActive) return true;
        if (!passesRegsoScoreFilters(regsoCode)) return false;

        if (regsoFilterContext.apartmentActive
            && !regsoScoreInRange('sqm_price_apartment_', regsoCode, regsoFilterContext.apartmentRange)) return false;
        if (regsoFilterContext.houseActive
            && !regsoScoreInRange('sqm_price_house_', regsoCode, regsoFilterContext.houseRange)) return false;
        return true;
    }, [passesRegsoScoreFilters, regsoFilterContext, regsoFiltersActive, regsoScoreInRange]);
    const kommunSuggestionsIndex = useMemo(() => {
        const byKommun = new Map();
        const sourceKommunNames = kommunNames.length > 0
            ? kommunNames
            : ortKommunPairs.map(({ kommun }) => kommun);
        sourceKommunNames.forEach((kommun) => {
            const key = normalizeSearchText(kommun);
            if (!key || byKommun.has(key)) return;
            byKommun.set(key, kommun);
        });
        return Array.from(byKommun.values()).sort((a, b) => a.localeCompare(b, 'sv'));
    }, [kommunNames, ortKommunPairs]);
    const ortSuggestionsIndex = useMemo(() => {
        const byOrt = new Map();
        ortKommunPairs.forEach(({ ort, kommun }) => {
            const key = `${normalizeSearchText(ort)}|${normalizeSearchText(kommun)}`;
            if (!key || byOrt.has(key)) return;
            byOrt.set(key, { ort, kommun });
        });
        return Array.from(byOrt.values()).sort((a, b) => a.ort.localeCompare(b.ort, 'sv'));
    }, [ortKommunPairs]);
    const lanSuggestionsIndex = useMemo(
        () => [...LAN_LIST].sort((a, b) => a.name.localeCompare(b.name, 'sv')),
        [],
    );
    const postortByRegsoId = useMemo(() => {
        const byRegsoId = new Map();
        listings.forEach((listing) => {
            const regsoId = extractListingRegsoCode(listing);
            const postort = String(listing?.postort || '').trim();
            if (!regsoId || !postort) return;
            const existing = byRegsoId.get(regsoId);
            if (existing) {
                existing.add(postort);
            } else {
                byRegsoId.set(regsoId, new Set([postort]));
            }
        });
        return byRegsoId;
    }, [listings]);
    const omradeRegsoSuggestionsIndex = useMemo(() => {
        const byRegso = new Map();
        regsoNameEntries.forEach((entry) => {
            const regsoId = String(entry.regsoId || '').trim();
            const name = String(entry.name || '').trim();
            if (!regsoId || !name || byRegso.has(regsoId)) return;
            const postorter = Array.from(postortByRegsoId.get(regsoId) || []).sort((a, b) => a.localeCompare(b, 'sv'));
            byRegso.set(regsoId, {
                regsoId,
                name,
                postorter,
            });
        });
        return Array.from(byRegso.values()).sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    }, [postortByRegsoId, regsoNameEntries]);
    const regsoNameById = useMemo(() => {
        const byId = new Map();
        regsoNameEntries.forEach((entry) => {
            const regsoId = String(entry?.regsoId || '').trim();
            const name = String(entry?.name || '').trim();
            if (!regsoId || !name || byId.has(regsoId)) return;
            byId.set(regsoId, name);
        });
        return byId;
    }, [regsoNameEntries]);
    // Postort name → its entries (name, kommun, kommunName, regsoIds). A postort that is also an ort in the same
    // kommun is used by that ort's suggestion; the others are the Postorter group.
    const postortAreasByName = useMemo(() => {
        const byName = new Map();
        postortAreas.forEach((entry) => {
            const key = normalizeSearchText(entry?.name);
            if (!key || !Array.isArray(entry?.regsoIds) || !entry.regsoIds.length) return;
            if (!byName.has(key)) byName.set(key, []);
            byName.get(key).push(entry);
        });
        return byName;
    }, [postortAreas]);
    const findPostortForOrt = useCallback((ortName, kommunName) => {
        const kommunKey = normalizeSearchText(kommunName).replace(/s$/, '');
        return (postortAreasByName.get(normalizeSearchText(ortName)) || [])
            .find((entry) => normalizeSearchText(entry.kommunName).replace(/s$/, '') === kommunKey) || null;
    }, [postortAreasByName]);
    const omradeWordSuggestionsIndex = useMemo(() => {
        const byWord = new Map();
        regsoNameEntries.forEach((entry) => {
            const regsoId = String(entry.regsoId || '').trim();
            const name = String(entry.name || '').trim();
            if (!regsoId || !name) return;
            const words = name
                .split(/[\s\-/,]+/)
                .map((word) => word.trim())
                .filter((word) => normalizeSearchText(word).length >= LIST_VIEW_SEARCH_MIN_CHARS);
            words.forEach((word) => {
                const key = normalizeSearchText(word);
                if (!key) return;
                const existing = byWord.get(key);
                if (existing) {
                    existing.regsoIds.add(regsoId);
                } else {
                    byWord.set(key, {
                        word,
                        regsoIds: new Set([regsoId]),
                    });
                }
            });
        });
        // Compound names belong to the word too ("Enskede" also covers Enskededalen and Enskedefältet), but only
        // in the kommuns where the word stands on its own, so a short word doesn't collect areas across Sweden.
        const wordsByKommun = new Map();
        regsoNameEntries.forEach((entry) => {
            const regsoId = String(entry.regsoId || '').trim();
            const kommunCode = regsoId.slice(0, 4);
            if (!regsoId || !kommunCode) return;
            const words = normalizeSearchText(entry.name).split(' ').filter(Boolean);
            if (!wordsByKommun.has(kommunCode)) wordsByKommun.set(kommunCode, []);
            wordsByKommun.get(kommunCode).push({ regsoId, words });
        });
        byWord.forEach((entry, key) => {
            if (key.length < 5) return;
            const kommunCodes = new Set(Array.from(entry.regsoIds).map((regsoId) => regsoId.slice(0, 4)));
            kommunCodes.forEach((kommunCode) => {
                (wordsByKommun.get(kommunCode) || []).forEach(({ regsoId, words }) => {
                    if (words.some((word) => word !== key && word.startsWith(key))) entry.regsoIds.add(regsoId);
                });
            });
        });
        return Array.from(byWord.values())
            .map((entry) => ({
                word: entry.word,
                regsoIds: Array.from(entry.regsoIds),
            }))
            .sort((a, b) => a.word.localeCompare(b.word, 'sv'));
    }, [regsoNameEntries]);
    // The Postorter group: every postort with its areas, except one an ort of the same name in the same kommun
    // already searches (it would list the same areas twice).
    const postortSuggestionsIndex = useMemo(() => {
        const ortKommunsByName = new Map();
        ortKommunPairs.forEach(({ ort, kommun }) => {
            const key = normalizeSearchText(ort);
            if (!ortKommunsByName.has(key)) ortKommunsByName.set(key, []);
            ortKommunsByName.get(key).push(kommun);
        });
        const entries = [];
        postortAreasByName.forEach((postorter, key) => {
            postorter.forEach((postort) => {
                if ((ortKommunsByName.get(key) || []).some((kommun) => findPostortForOrt(postort.name, kommun) === postort)) return;
                entries.push(postort);
            });
        });
        return entries.sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    }, [findPostortForOrt, ortKommunPairs, postortAreasByName]);
    const normalizedSearchDraft = useMemo(() => normalizeSearchText(searchDraft), [searchDraft]);
    // Streets ("Postiljonsvägen, Enskede"): each turns on the area it lies in. The postort comes from the street's
    // postcode, else from the postort its area belongs to, else the town Nominatim gives.
    const postortNameByRegso = useMemo(() => {
        const byRegso = new Map();
        postortAreas.forEach((entry) => {
            (entry?.regsoIds || []).forEach((regsoId) => {
                if (!byRegso.has(regsoId)) byRegso.set(regsoId, entry.name);
            });
        });
        return byRegso;
    }, [postortAreas]);
    const [streetSuggestions, setStreetSuggestions] = useState({ query: '', items: [], loading: false });
    const lookupStreets = useCallback(async (query, signal) => {
        const [streets, postcodePostort] = await Promise.all([
            searchStreets(query, signal),
            loadPostcodePostort(),
        ]);
        const seen = new Set();
        const items = [];
        streets.forEach((street) => {
            if (items.length >= LIST_VIEW_STREET_SUGGESTIONS) return;
            const layer = findAreaLayerAt(regsoGeoJsonRef.current, street.lat, street.lng);
            const regsoId = String(layer?.feature?.properties?.regsokod || '').trim();
            if (!regsoId) return;
            const place = postcodePostort.get(street.postcode) || postortNameByRegso.get(regsoId) || street.town;
            const label = place ? `${street.road}, ${place}` : street.road;
            const key = normalizeSearchText(label);
            if (seen.has(key)) return;
            seen.add(key);
            items.push({
                id: `address:${regsoId}:${key}`,
                type: 'omrade',
                address: true,
                label,
                kommun: '',
                ort: '',
                lan: '',
                regsoId,
                postort: '',
                focusPoint: [street.lat, street.lng],
            });
        });
        return items;
    }, [postortNameByRegso]);
    useEffect(() => {
        const query = searchDraft.trim();
        if (normalizeSearchText(query).length < LIST_VIEW_STREET_SEARCH_MIN_CHARS) {
            setStreetSuggestions({ query: '', items: [], loading: false });
            return undefined;
        }
        setStreetSuggestions((prev) => (prev.query === query ? prev : { query, items: [], loading: true }));
        const controller = new AbortController();
        const timer = setTimeout(async () => {
            try {
                const items = await lookupStreets(query, controller.signal);
                if (!controller.signal.aborted) setStreetSuggestions({ query, items, loading: false });
            } catch (error) {
                if (!controller.signal.aborted) setStreetSuggestions({ query, items: [], loading: false });
            }
        }, LIST_VIEW_STREET_SEARCH_DELAY_MS);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [lookupStreets, searchDraft]);
    const searchTokens = useMemo(
        () => normalizedSearchDraft.split(' ').filter(Boolean),
        [normalizedSearchDraft],
    );
    const groupedSuggestions = useMemo(() => {
        if (!searchTokens.length || normalizedSearchDraft.length < LIST_VIEW_SEARCH_MIN_CHARS) {
            return {
                kommun: [],
                ort: [],
                lan: [],
                postort: [],
                omrade: [],
                address: [],
            };
        }

        const kommun = rankSuggestions(kommunSuggestionsIndex, {
            query: normalizedSearchDraft,
            tokens: searchTokens,
            toSearchText: (kommunName) => kommunName,
            toSortText: (kommunName) => kommunName,
            toSuggestion: (kommunName) => ({
                id: `kommun:${kommunName}`,
                type: 'kommun',
                label: `${kommunName} kommun`,
                kommun: kommunName,
                ort: '',
                lan: '',
                regsoId: '',
            }),
        });

        const ort = rankSuggestions(ortSuggestionsIndex, {
            query: normalizedSearchDraft,
            tokens: searchTokens,
            toSearchText: (entry) => entry.ort,
            toSortText: (entry) => entry.ort,
            toSuggestion: (entry) => {
                const postort = findPostortForOrt(entry.ort, entry.kommun);
                return {
                    id: `ort:${entry.ort}:${entry.kommun}`,
                    type: 'ort',
                    label: `${entry.ort}, ${entry.kommun} kommun`,
                    kommun: entry.kommun,
                    ort: entry.ort,
                    lan: '',
                    regsoId: '',
                    ...(postort ? { regsoIds: postort.regsoIds } : {}),
                };
            },
        });
        const lan = rankSuggestions(lanSuggestionsIndex, {
            query: normalizedSearchDraft,
            tokens: searchTokens,
            toSearchText: (lan) => [lan.label, lan.name],
            toSortText: (lan) => lan.name,
            toSuggestion: (lan) => ({
                id: `lan:${lan.name}`,
                type: 'lan',
                label: lan.label,
                kommun: '',
                ort: '',
                lan: lan.name,
                lanCode: lan.code,
                regsoId: '',
            }),
        });

        const postort = rankSuggestions(postortSuggestionsIndex, {
            query: normalizedSearchDraft,
            tokens: searchTokens,
            toSearchText: (entry) => entry.name,
            toSortText: (entry) => entry.name,
            toSuggestion: (entry) => ({
                id: `postort:${normalizeSearchText(entry.name)}:${entry.kommun}`,
                type: 'postort',
                label: entry.kommunName ? `${entry.name}, ${entry.kommunName}` : entry.name,
                kommun: '',
                ort: '',
                lan: '',
                regsoId: '',
                regsoIds: entry.regsoIds,
                postort: entry.name,
            }),
        });

        const omradeCandidates = [
            ...omradeRegsoSuggestionsIndex.map((entry) => ({
                searchTerms: [entry.name, ...(entry.postorter || [])],
                sortText: entry.name,
                suggestion: {
                    id: `omrade:${entry.regsoId}`,
                    type: 'omrade',
                    label: entry.postorter?.length
                        ? `${entry.name} · ${entry.postorter.slice(0, 2).join(', ')}`
                        : entry.name,
                    kommun: '',
                    ort: '',
                    lan: '',
                    regsoId: entry.regsoId,
                    postort: '',
                },
            })),
            ...omradeWordSuggestionsIndex.map((entry) => ({
                searchTerms: [entry.word],
                sortText: entry.word,
                suggestion: {
                    id: `omrade-word:${normalizeSearchText(entry.word)}`,
                    type: 'omrade-word',
                    label: entry.word,
                    kommun: '',
                    ort: '',
                    lan: '',
                    regsoId: entry.regsoIds.length === 1 ? String(entry.regsoIds[0] || '').trim() : '',
                    regsoIds: entry.regsoIds,
                    postort: '',
                },
            })),
        ];
        const omrade = rankSuggestions(omradeCandidates, {
            query: normalizedSearchDraft,
            tokens: searchTokens,
            toSearchText: (entry) => entry.searchTerms,
            toSortText: (entry) => entry.sortText,
            toSuggestion: (entry) => entry.suggestion,
        });

        const address = streetSuggestions.query === searchDraft.trim() ? streetSuggestions.items : [];

        return { kommun, ort, lan, postort, omrade, address };
    }, [findPostortForOrt, kommunSuggestionsIndex, lanSuggestionsIndex, normalizedSearchDraft, omradeRegsoSuggestionsIndex, omradeWordSuggestionsIndex, ortSuggestionsIndex, postortSuggestionsIndex, searchDraft, searchTokens, streetSuggestions]);
    const streetLookupPending = streetSuggestions.loading && streetSuggestions.query === searchDraft.trim();
    // The kommun a suggestion lies in, for its muted place: an ort by its kommun's name, an area (or an area word
    // whose areas share one kommun) by its code's first four digits.
    const suggestionKommun = useCallback((suggestion) => {
        if (suggestion?.type === 'ort') {
            return kommunNameByCode[kommunCodeForName(kommunCodeList, suggestion.kommun)] || '';
        }
        if (suggestion?.type !== 'omrade' && suggestion?.type !== 'omrade-word' && suggestion?.type !== 'omrade-map') return '';
        if (suggestion.address) return '';
        const ids = suggestion.regsoId ? [suggestion.regsoId] : (suggestion.regsoIds || []);
        const codes = new Set(ids.map((id) => String(id || '').slice(0, 4)).filter(Boolean));
        return codes.size === 1 ? (kommunNameByCode[[...codes][0]] || '') : '';
    }, [kommunCodeList, kommunNameByCode]);
    const flatSuggestions = useMemo(
        () => [
            ...groupedSuggestions.kommun,
            ...groupedSuggestions.ort,
            ...groupedSuggestions.lan,
            ...groupedSuggestions.postort,
            ...groupedSuggestions.omrade,
            ...groupedSuggestions.address,
        ],
        [groupedSuggestions],
    );
    // Analytics: what people type that matches nothing (once they pause typing).
    const lastNoMatchRef = useRef('');
    useEffect(() => {
        if (normalizedSearchDraft.length < LIST_VIEW_SEARCH_MIN_CHARS || flatSuggestions.length > 0) return undefined;
        const timer = setTimeout(() => {
            if (lastNoMatchRef.current === normalizedSearchDraft) return;
            lastNoMatchRef.current = normalizedSearchDraft;
            track('search.no_match', { q: normalizedSearchDraft.slice(0, 60) });
        }, 1500);
        return () => clearTimeout(timer);
    }, [flatSuggestions.length, normalizedSearchDraft]);

    useEffect(() => {
        const previousKey = previousRegsoSliderFilterKeyRef.current;
        if (previousKey == null) {
            previousRegsoSliderFilterKeyRef.current = regsoSliderFilterKey;
            return;
        }
        if (previousKey !== regsoSliderFilterKey) {
            // RegSO slider changes should override manual map tile picks.
            const mapSelectionIds = selectedSuggestionsRef.current
                .filter((entry) => entry?.type === 'omrade-map')
                .map((entry) => entry.id);
            if (mapSelectionIds.length) {
                removeSuggestionsByIds(mapSelectionIds);
            }
        }
        previousRegsoSliderFilterKeyRef.current = regsoSliderFilterKey;
    }, [regsoSliderFilterKey, removeSuggestionsByIds]);

    const selectedFilteredOutRegsoSet = useMemo(() => {
        const regsoIds = new Set();
        if (!regsoFiltersActive) return regsoIds;
        selectedSuggestions.forEach((suggestion) => {
            if (suggestion?.type !== 'omrade-map') return;
            const regsoId = String(suggestion?.regsoId || '').trim();
            if (!regsoId) return;
            if (!passesRegsoTileFilters(regsoId)) {
                regsoIds.add(regsoId);
            }
        });
        return regsoIds;
    }, [passesRegsoTileFilters, regsoFiltersActive, selectedSuggestions]);

    const matchesListing = useCallback((listing) => {
        // houseType is 'all' or a comma-list of apartments/houses/rowhouse/fritidshus (multi-select).
        if (listingFilters.houseType && listingFilters.houseType !== 'all') {
            const types = String(listingFilters.houseType).split(',');
            const typeOk = (types.includes('apartments') && listing.type === 'apartment')
                || (types.includes('houses') && listing.type === 'house')
                || (types.includes('rowhouse') && listing.type === 'rowhouse')
                || (types.includes('fritidshus') && listing.type === 'fritidshus');
            if (!typeOk) return false;
        }

        if (filterContext.priceMin != null || filterContext.priceMax != null) {
            if (listing.priceValue == null) return false;
            if (filterContext.priceMin != null && listing.priceValue < filterContext.priceMin) return false;
            if (filterContext.priceMax != null && listing.priceValue > filterContext.priceMax) return false;
        }

        if (filterContext.roomMin != null || filterContext.roomMax != null) {
            if (listing.roomsValue == null) return false;
            if (filterContext.roomMin != null && listing.roomsValue < filterContext.roomMin) return false;
            if (filterContext.roomMax != null && listing.roomsValue > filterContext.roomMax) return false;
        }

        if (filterContext.sqmMin != null || filterContext.sqmMax != null) {
            const areaValue = parseAreaValue(listing);
            if (areaValue == null) return false;
            if (filterContext.sqmMin != null && areaValue < filterContext.sqmMin) return false;
            if (filterContext.sqmMax != null && areaValue > filterContext.sqmMax) return false;
        }

        if (filterContext.feeMax != null) {
            if (listing.feeValue == null) return false;
            if (listing.feeValue > filterContext.feeMax) return false;
        }

        if (filterContext.sqmPriceMin != null || filterContext.sqmPriceMax != null) {
            const sqmPrice = listing.sqmPriceValue ?? parseFloat(listing.sqmPrice);
            if (!Number.isFinite(sqmPrice) || sqmPrice <= 0) return false;
            if (filterContext.sqmPriceMin != null && sqmPrice < filterContext.sqmPriceMin) return false;
            if (filterContext.sqmPriceMax != null && sqmPrice > filterContext.sqmPriceMax) return false;
        }
        // Floor, year built, balcony and elevator come from the server's filtering (listvy listings don't carry
        // them); only checked here when a listing has the field.
        if ((filterContext.floorMin != null || filterContext.floorMax != null) && listing.floorValue != null) {
            if (filterContext.floorMin != null && listing.floorValue < filterContext.floorMin) return false;
            if (filterContext.floorMax != null && listing.floorValue > filterContext.floorMax) return false;
        }
        if ((filterContext.yearBuiltMin != null || filterContext.yearBuiltMax != null) && listing.yearBuilt != null) {
            if (filterContext.yearBuiltMin != null && listing.yearBuilt < filterContext.yearBuiltMin) return false;
            if (filterContext.yearBuiltMax != null && listing.yearBuilt > filterContext.yearBuiltMax) return false;
        }
        if (filterContext.balcony && listing.hasBalcony === false) return false;
        if (filterContext.elevator && listing.hasElevator === false) return false;
        if (filterContext.newConstruction === 'only' && listing.isNewConstruction === false) return false;
        if (filterContext.newConstruction === 'exclude' && listing.isNewConstruction === true) return false;
        if (regsoFiltersActive) {
            const listingRegsoCode = extractListingRegsoCode(listing);
            const bypassRegsoFilters = Boolean(
                listingRegsoCode && selectedFilteredOutRegsoSet.has(listingRegsoCode),
            );
            if (bypassRegsoFilters) {
                return true;
            }
            return passesRegsoListingFilters(listing);
        }
        return true;
    }, [listingFilters.houseType, filterContext, passesRegsoListingFilters, regsoFiltersActive, selectedFilteredOutRegsoSet]);

    const filteredPageListings = useMemo(
        () => dateOrderedListings.filter((listing) => matchesListing(listing)),
        [dateOrderedListings, matchesListing],
    );
    const filteredMapListings = useMemo(
        () => mapDateOrderedListings.filter((listing) => matchesListing(listing)),
        [mapDateOrderedListings, matchesListing],
    );

    const regsoBoundsIndex = useMemo(
        () => (topojsonFeature?.features || [])
            .map((featureEntry) => ({
                regsoCode: String(featureEntry?.properties?.regsokod || '').trim(),
                bounds: getGeometryBounds(featureEntry?.geometry),
            }))
            .filter((entry) => entry.regsoCode && entry.bounds),
        [topojsonFeature],
    );
    const regsoBoundsByCode = useMemo(() => {
        const byCode = new Map();
        regsoBoundsIndex.forEach((entry) => {
            byCode.set(entry.regsoCode, entry.bounds);
        });
        return byCode;
    }, [regsoBoundsIndex]);
    const matchesListingFeedTab = useCallback((listing) => {
        const isComingSoon = isComingSoonListing(listing);
        if (listingFeedTab === LISTING_FEED_TAB_COMING_SOON) return isComingSoon;
        return !isComingSoon;
    }, [listingFeedTab]);
    const visibleLoadedListings = useMemo(
        () => filteredPageListings.filter((listing) => matchesListingFeedTab(listing)),
        [filteredPageListings, matchesListingFeedTab],
    );
    const mapListings = useMemo(
        () => (showFavorites
            // Favourites no longer for sale stay in the list but have nothing to show on the map.
            ? favorites.filter((listing) => (
                !listing.favoriteRemoved && Number.isFinite(listing.lat) && Number.isFinite(listing.lng)
            ))
            : filteredMapListings.filter((listing) => matchesListingFeedTab(listing))),
        [favorites, filteredMapListings, matchesListingFeedTab, showFavorites],
    );
    const showSearchPrompt = !shouldFetchListings && !showFavorites;
    // The landing screen: the search-prompt state, but only before the first search of this page load.
    const showLanding = showSearchPrompt && !hasSearched;

    // On phone, the landing scrolls gently: a touch damper halves the scroll speed. The map and veil are a
    // fixed backdrop (CSS) so this never reveals blank space.
    useEffect(() => {
        const el = pageRootRef.current;
        if (!showLanding || !el || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        if (!window.matchMedia('(max-width: 700px)').matches) return undefined;
        let lastY = null;
        // A touch in the search suggestions or the Filter panel scrolls that list itself, not the page (taken
        // here, the panel's lower filters and its Sök bostäder couldn't be reached).
        const onStart = (event) => {
            const inList = event.target instanceof Element
                && event.target.closest('.list-view-search-suggestions, .lvl-dropdown--filter');
            lastY = !inList && event.touches && event.touches[0] ? event.touches[0].clientY : null;
        };
        const onMove = (event) => {
            if (lastY == null || !event.touches || !event.touches[0]) return;
            const y = event.touches[0].clientY;
            el.scrollTop += (lastY - y) * 0.5; // half speed
            lastY = y;
            if (event.cancelable) event.preventDefault();
        };
        const clear = () => { lastY = null; };
        el.addEventListener('touchstart', onStart, { passive: true });
        el.addEventListener('touchmove', onMove, { passive: false });
        el.addEventListener('touchend', clear, { passive: true });
        el.addEventListener('touchcancel', clear, { passive: true });
        return () => {
            el.removeEventListener('touchstart', onStart);
            el.removeEventListener('touchmove', onMove);
            el.removeEventListener('touchend', clear);
            el.removeEventListener('touchcancel', clear);
        };
    }, [showLanding]);
    // On phone, typing in the landing's search: the keyboard would cover the suggestions, so the page gets room
    // to scroll (.is-landing-typing), the field is lifted up under the header, and the list is kept to what's
    // left above the keyboard (the visual viewport) and scrolls on its own.
    useEffect(() => {
        const root = pageRootRef.current;
        if (!showLanding || !root || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        if (!window.matchMedia('(max-width: 700px)').matches) return undefined;
        const viewport = window.visualViewport;
        let blurTimer = null;
        let liftTimer = null;
        const fitList = () => {
            const list = root.querySelector('.list-view-landing .list-view-search-suggestions');
            if (!list) return;
            const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
            list.style.maxHeight = `${Math.max(150, Math.round(bottom - list.getBoundingClientRect().top - 12))}px`;
        };
        const isLandingInput = (target) => target instanceof Element && target.matches('input') && target.closest('.list-view-landing');
        const onFocus = (event) => {
            if (!isLandingInput(event.target)) return;
            clearTimeout(blurTimer);
            root.classList.add('is-landing-typing');
            // Once the keyboard is up: the field just under the header.
            clearTimeout(liftTimer);
            liftTimer = setTimeout(() => {
                const top = event.target.getBoundingClientRect().top;
                root.scrollBy({ top: top - 76, behavior: 'smooth' });
                setTimeout(fitList, 380);
            }, 280);
        };
        const onBlur = (event) => {
            if (!isLandingInput(event.target)) return;
            // Not at once: a tap on a suggestion blurs the field first, and the layout must hold for its click.
            blurTimer = setTimeout(() => root.classList.remove('is-landing-typing'), 400);
        };
        const onInput = () => requestAnimationFrame(fitList);
        root.addEventListener('focusin', onFocus);
        root.addEventListener('focusout', onBlur);
        root.addEventListener('input', onInput);
        viewport?.addEventListener('resize', fitList);
        viewport?.addEventListener('scroll', fitList);
        const observer = typeof MutationObserver === 'function' ? new MutationObserver(() => requestAnimationFrame(fitList)) : null;
        const landing = root.querySelector('.list-view-landing');
        if (observer && landing) observer.observe(landing, { childList: true, subtree: true });
        return () => {
            clearTimeout(blurTimer);
            clearTimeout(liftTimer);
            root.classList.remove('is-landing-typing');
            root.removeEventListener('focusin', onFocus);
            root.removeEventListener('focusout', onBlur);
            root.removeEventListener('input', onInput);
            viewport?.removeEventListener('resize', fitList);
            viewport?.removeEventListener('scroll', fitList);
            observer?.disconnect();
        };
    }, [showLanding]);
    useEffect(() => {
        if (shouldFetchListings) setHasSearched(true);
    }, [shouldFetchListings]);
    const feedListings = showFavorites ? favorites : (shouldFetchListings ? visibleLoadedListings : []);
    // Minutes to work for the homes on this page (Restid till jobbet), once a workplace is set.
    const commuteTimes = useCommuteTimes(feedListings);

    // On the phone layout the first search jumps down to the first listing, once (the listings sit below the
    // map and filters there). On desktop/tablet the listings are already beside/in view, so auto-scrolling
    // them only nudged the feed column slightly for no reason — skip it there.
    useEffect(() => {
        if (firstSearchScrolledRef.current) return;
        if (!shouldFetchListings || showFavorites || searchLoading || isPageLoading) return;
        if (visibleLoadedListings.length === 0) return;
        firstSearchScrolledRef.current = true;
        const isPhone = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
            && window.matchMedia(`(max-width: ${MOBILE_LAYOUT_MAX_WIDTH}px)`).matches;
        if (!isPhone) return;
        requestAnimationFrame(() => {
            const column = feedColumnRef.current;
            if (!column) return;
            const target = column.querySelector('.list-view-card') || column.querySelector('.list-view-feed-tabbar');
            target?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        });
    }, [shouldFetchListings, showFavorites, searchLoading, isPageLoading, visibleLoadedListings.length]);

    // Leaving the landing: fade the map and filters in for a moment ("tone in" on the first search).
    const [revealingAfterLanding, setRevealingAfterLanding] = useState(false);
    const wasLandingRef = useRef(showLanding);
    useEffect(() => {
        if (wasLandingRef.current && !showLanding) {
            setRevealingAfterLanding(true);
            const timer = setTimeout(() => setRevealingAfterLanding(false), 600);
            wasLandingRef.current = showLanding;
            return () => clearTimeout(timer);
        }
        wasLandingRef.current = showLanding;
        return undefined;
    }, [showLanding]);

    useEffect(() => {
        if (showFavorites) checkFavorites();
    }, [showFavorites]);
    const listingById = useMemo(() => {
        const byId = new Map();
        const appendListings = (source) => {
            if (!Array.isArray(source)) return;
            source.forEach((listing) => {
                const id = String(listing?.id || '').trim();
                if (!id || byId.has(id)) return;
                byId.set(id, listing);
            });
        };
        appendListings(mapListings);
        appendListings(visibleLoadedListings);
        return byId;
    }, [mapListings, visibleLoadedListings]);
    const listingIconCacheRef = useRef(new Map());
    const listingClusterIconCacheRef = useRef(new Map());


    useEffect(() => {
        if (!mapSelectedListingId) return;
        const stillVisible = mapListings.some((listing) => String(listing.id) === mapSelectedListingId);
        if (!stillVisible) {
            setMapSelectedListingId(null);
        }
    }, [mapListings, mapSelectedListingId]);

    // Shape markers (shapeMarkers.js) as DOM icons, from their canvas visual; --glow colours the hover glow.
    const getShapeIcon = useCallback((visual, emphasized = false) => {
        const size = Math.round(visual.radius * 2);
        const cacheKey = `shape:${visual.shape}:${visual.fill}:${visual.halo}:${visual.count ?? ''}:${visual.visited ? 'v' : ''}:${size}:${emphasized ? 1 : 0}`;
        const cached = listingIconCacheRef.current.get(cacheKey);
        if (cached) return cached;
        const sprite = homeShapeSprite(visual.shape, {
            size,
            fill: visual.fill,
            halo: visual.halo,
            detail: visual.detail,
            count: visual.count,
            visited: visual.visited,
        });
        const box = sprite.box;
        // Parsed once per look and copied for each marker (templateIcon.js).
        const icon = templateDivIcon({
            className: 'list-view-listing-marker-wrapper',
            html: `<div class="listing-shape-marker${emphasized ? ' is-emphasized' : ''}" style="width:${box}px;height:${box}px;--glow:${visual.halo};"><img src="${sprite.url}" width="${box}" height="${box}" alt="" style="display:block"></div>`,
            iconSize: [box, box],
            iconAnchor: [box / 2, box / 2],
        });
        listingIconCacheRef.current.set(cacheKey, icon);
        return icon;
    }, []);

    const getListingClusterIcon = useCallback((count, type, showCount, size, noBorder = false) => {
        const typeKey = type === 'new' ? 'new' : getListingTypeGroup(type);
        const cacheKey = `${typeKey}:${count}:${showCount ? 1 : 0}:${size}:${noBorder ? 1 : 0}`;
        const cached = listingClusterIconCacheRef.current.get(cacheKey);
        if (cached) return cached;
        // A square in its type's colour with a white edge, the same sprite the glide draws (dotSquares.js), as an image.
        const sprite = dotSpriteUrl(
            getMarkerColor(typeKey),
            '#ffffff',
            noBorder ? 0 : 2,
            noBorder ? size / 2 : (size / 2) + 1,
            window.devicePixelRatio || 1,
        );
        const icon = templateDivIcon({
            className: 'list-view-listing-cluster-wrapper',
            html: `<img class="listing-dot-shape" src="${sprite.url}" width="${sprite.size}" height="${sprite.size}" alt="" draggable="false">`,
            iconSize: [sprite.size, sprite.size],
            iconAnchor: [sprite.size / 2, sprite.size / 2],
        });
        listingClusterIconCacheRef.current.set(cacheKey, icon);
        return icon;
    }, []);

    const cancelListingLayerBuild = useCallback(() => {
        if (listingLayerBuildRafRef.current) {
            cancelAnimationFrame(listingLayerBuildRafRef.current);
            listingLayerBuildRafRef.current = null;
        }
    }, []);

    useEffect(() => () => {
        cancelListingLayerBuild();
    }, [cancelListingLayerBuild]);

    useEffect(() => {
        if (!mapInstance || !mapPanesReady) return undefined;
        const layer = L.layerGroup().addTo(mapInstance);
        const renderedByKey = renderedMarkerByKeyRef.current;
        const renderedSignatureByKey = renderedMarkerSignatureByKeyRef.current;
        listingLayerRef.current = layer;
        renderedByKey.clear();
        renderedSignatureByKey.clear();
        const animator = createListingGroupAnimator(mapInstance, LIST_VIEW_LISTING_ANIMATION_PANE, 645);
        listingAnimatorRef.current = animator;
        drawnListingGroupsRef.current = null;
        return () => {
            cancelListingLayerBuild();
            animator.destroy();
            if (listingAnimatorRef.current === animator) listingAnimatorRef.current = null;
            layer.clearLayers();
            layer.remove();
            renderedByKey.clear();
            renderedSignatureByKey.clear();
            if (listingLayerRef.current === layer) {
                listingLayerRef.current = null;
            }
        };
    }, [cancelListingLayerBuild, mapInstance, mapPanesReady]);

    // Homes still on top of each other at the closest zoom, fanned out around their point after a click on
    // their group ({ ids, lat, lng, zoom }); several can be open at once (the owner, 2026-11-01). A zoom change
    // gathers them all again.
    const [listingSpreads, setListingSpreads] = useState([]);
    useEffect(() => {
        if (listingSpreads.length && Number.isFinite(mapZoom) && listingSpreads.some((item) => item.zoom !== mapZoom)) {
            setListingSpreads((current) => current.filter((item) => item.zoom === mapZoom));
        }
    }, [listingSpreads, mapZoom]);

    // Listings bucketed per area and type; groups are split from these per zoom level.
    const listingAreaGroupIndex = useMemo(
        () => buildAreaGroupIndex(mapListings, extractListingRegsoCode, getListingMarkerGroup),
        [mapListings],
    );

    const clusteredMapEntries = useMemo(() => {
        if (!mapPanesReady || !Array.isArray(mapListings) || !mapListings.length || !mapViewport) {
            return [];
        }

        const zoom = Number.isFinite(mapZoom) ? mapZoom : 5;
        const maxZoom = mapInstance?.getMaxZoom?.() ?? 18;
        // Counts (and house-shaped groups) one zoom deeper since 2026-11-01, like kartvy's.
        const showClusterCount = showListingIcons || zoom >= (maxZoom - 5);
        const clusterIconSize = showClusterCount ? 25 : 16;
        const noBorder = zoom < (maxZoom - 5);
        const shapeRadius = shapeRadiusAtZoom(SHAPE_MARKER_RADIUS, zoom, maxZoom);
        const shapeGroupRadius = shapeRadiusAtZoom(SHAPE_GROUP_RADIUS, zoom, maxZoom);

        // Canvas look of each marker for the regrouping glide; matches the DOM icons below (white 2px
        // border outside the circle).
        // Clickable markers that would overlap are merged into one group; zoomed out they're plain dots
        // that ignore the mouse, so they're left as they are.
        const groups = showClusterCount
            ? getMapGroups(listingAreaGroupIndex, zoom, maxZoom, mapViewport, {
                project: projectListViewPoint(mapInstance),
                unproject: unprojectListViewPoint(mapInstance),
                minDistancePx: SHAPE_OVERLAP_PX,
                spreads: listingSpreads,
            })
            : getAreaGroups(listingAreaGroupIndex, getGroupsPerArea(zoom, maxZoom), mapViewport);
        return groups.filter((group) => !isAreaRevealPending(group.areaCode)).map((group) => {
            const placement = {
                lat: group.lat, lng: group.lng, listings: group.listings, spreadFrom: group.spreadFrom, areaCode: group.areaCode,
            };
            if (group.count === 1) {
                const listing = group.listings[0];
                const shapeVisual = showListingIcons
                    ? shapeMarkerVisual([listing], {
                        radius: shapeRadius,
                        isNew: Boolean(listing?.isNewConstruction),
                        visited: isVisitedKey(getListingKey(listing)),
                    })
                    : null;
                return {
                    ...placement,
                    kind: 'listing',
                    key: `listing:${listing?.id || group.key}`,
                    listing,
                    shapeVisual,
                    visual: shapeVisual || {
                        radius: 4.6,
                        dot: true,
                        fill: getMarkerColor(group.typeKey),
                        stroke: '#ffffff',
                        weight: 1.5,
                    },
                };
            }
            const shapeVisual = showClusterCount
                ? shapeMarkerVisual(group.listings, {
                    radius: shapeGroupRadius,
                    count: group.count,
                    isNew: group.typeKey === 'new',
                })
                : null;
            return {
                ...placement,
                kind: 'cluster',
                key: `cluster:${group.key}`,
                center: [group.lat, group.lng],
                type: group.typeKey,
                count: group.count,
                showClusterCount,
                iconSize: clusterIconSize,
                noBorder,
                shapeVisual,
                visual: shapeVisual || {
                    radius: noBorder ? clusterIconSize / 2 : (clusterIconSize / 2) + 1,
                    dot: true,
                    fill: getMarkerColor(group.typeKey),
                    stroke: '#ffffff',
                    weight: noBorder ? 0 : 2,
                    count: showClusterCount ? group.count : null,
                },
            };
        });
    }, [
        listingAreaGroupIndex,
        mapInstance,
        mapListings,
        mapPanesReady,
        mapViewport,
        mapZoom,
        listingSpreads,
        showListingIcons,
        // Rebuild the entries (and so each single home's shapeVisual) when a listing is opened.
        visitedVersion,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        areaRevealTick,
    ]);

    const mergeListingDetailsInCollection = useCallback((collection, incomingListing) => {
        if (!Array.isArray(collection) || !collection.length) return collection;
        const targetKey = getListingKey(incomingListing);
        if (!targetKey) return collection;

        let changed = false;
        const nextCollection = collection.map((item) => {
            if (getListingKey(item) !== targetKey) return item;
            const merged = mergeListingDetailsIfChanged(item, incomingListing);
            if (merged !== item) changed = true;
            return merged;
        });
        return changed ? nextCollection : collection;
    }, []);

    const openFullViewListing = useCallback((listing, source = 'feed', openFrom = null, { floorplan = false } = {}) => {
        if (!listing) return;
        track('listing.open', { source, ...listingProps(listing), score: listing.betyg ?? undefined });
        markVisited(listing);
        setFullViewOpenFrom(openFrom);
        setFullViewAtFloorplan(floorplan);
        setFullViewListing(listing);
        setFullViewDetailsLoading(false);
        setFullViewDetailsError('');
    }, []);

    const closeFullViewListing = useCallback(() => {
        setFullViewListing(null);
        setFullViewDetailsLoading(false);
        setFullViewDetailsError('');
    }, []);

    // A shared link (?bostad=/annons=) opened directly: strip the param off this history entry so closing the
    // popup returns to the plain view (not off the site), then open the listing from a stub — the details
    // fetch below fills it in, and a sold/removed listing falls through to the same "no longer for sale" note.
    const sharedListingOpenedRef = useRef(false);
    useEffect(() => {
        if (sharedListingOpenedRef.current) return;
        sharedListingOpenedRef.current = true;
        const key = readListingKeyFromSearch(window.location.search);
        if (!key) return;
        try {
            const url = new URL(window.location.href);
            ['bostad', 'annons'].forEach((kind) => url.searchParams.delete(kind));
            window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        } catch (error) {
            /* leave the URL as is */
        }
        openFullViewListing({ id: key }, 'share_link');
    }, [openFullViewListing]);

    useEffect(() => {
        if (!fullViewListing) {
            setFullViewDetailsLoading(false);
            setFullViewDetailsError('');
            return undefined;
        }

        const listingKey = getListingKey(fullViewListing);
        if (!listingKey) {
            setFullViewDetailsLoading(false);
            setFullViewDetailsError('');
            return undefined;
        }
        // A favourite the nightly scrape removed: show the saved copy, there are no details to load.
        if (fullViewListing.favoriteRemoved) {
            setFullViewDetailsLoading(false);
            setFullViewDetailsError('');
            return undefined;
        }

        const applyDetailsToState = (details) => {
            setFullViewListing((prev) => {
                if (!prev) return prev;
                if (getListingKey(prev) !== listingKey) return prev;
                return mergeListingDetailsIfChanged(prev, details);
            });
            setSelectionResultsById((prev) => {
                const keys = Object.keys(prev || {});
                if (!keys.length) return prev;
                let changed = false;
                const next = { ...prev };
                keys.forEach((key) => {
                    const currentGroup = Array.isArray(prev[key]) ? prev[key] : [];
                    const nextGroup = mergeListingDetailsInCollection(currentGroup, details);
                    if (nextGroup !== currentGroup) {
                        next[key] = nextGroup;
                        changed = true;
                    }
                });
                return changed ? next : prev;
            });
        };

        const cached = fullViewDetailsCacheRef.current.get(listingKey);
        if (cached) {
            applyDetailsToState(cached);
            setFullViewDetailsLoading(false);
            setFullViewDetailsError('');
            return undefined;
        }

        const controller = new AbortController();
        let cancelled = false;
        setFullViewDetailsLoading(true);
        setFullViewDetailsError('');

        const run = async () => {
            try {
                const details = await fetchFullListingDetails(listingKey, { signal: controller.signal });
                if (cancelled) return;
                fullViewDetailsCacheRef.current.set(listingKey, details);
                applyDetailsToState(details);
                setFullViewDetailsLoading(false);
                setFullViewDetailsError('');
            } catch (error) {
                if (cancelled || error?.name === 'AbortError') return;
                setFullViewDetailsLoading(false);
                if (error?.status === 404) {
                    // Sold or taken down since the listings were loaded: say so in the popup instead of an
                    // error. A favourite is kept, marked as no longer for sale.
                    if (isFavoriteListing(fullViewListing)) markFavoriteRemoved(fullViewListing);
                    setFullViewDetailsError('');
                    setFullViewListing((prev) => (
                        prev && getListingKey(prev) === listingKey ? { ...prev, favoriteRemoved: true } : prev
                    ));
                    return;
                }
                setFullViewDetailsError(FULL_LISTING_DETAILS_ERROR);
            }
        };

        run();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [fullViewListing, mergeListingDetailsInCollection]);

    const handleMapListingClick = useCallback((listing, event = null) => {
        const targetId = String(listing?.id || '').trim();
        if (!targetId) return;

        setMapSelectedListingId(targetId);
        infoPanelRef.current?.setHoveredMapListing(targetId);
        openFullViewListing(listing, 'map_marker', markerScreenPoint(event));

        if (feedColumnRef.current) {
            feedColumnRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [openFullViewListing]);

    useEffect(() => {
        if (!mapPanesReady || !mapInstance || !listingLayerRef.current) return undefined;

        const listingLayer = listingLayerRef.current;
        const renderedByKey = renderedMarkerByKeyRef.current;
        const renderedSignatureByKey = renderedMarkerSignatureByKeyRef.current;
        const maxZoom = mapInstance.getMaxZoom?.() ?? 18;
        const animator = listingAnimatorRef.current;

        cancelListingLayerBuild();

        // A zoom that changes the groups per area glides the old markers into the new ones first.
        const zoom = Number.isFinite(mapZoom) ? mapZoom : 5;
        const groupsPerArea = getGroupsPerArea(zoom, maxZoom);
        // Crossing the icon zoom turns dots into house icons (or back): glide those too, so they morph.
        const icons = zoom >= LIST_VIEW_ICON_ZOOM_THRESHOLD;
        const previousGroups = drawnListingGroupsRef.current;
        drawnListingGroupsRef.current = { zoom, groupsPerArea, icons, entries: clusteredMapEntries, spreads: listingSpreads };
        const spreadsBefore = previousGroups?.spreads || [];
        // A new spread glides its homes out from their group like a zoom that splits groups.
        const spreading = Boolean(previousGroups && listingSpreads.length > spreadsBefore.length);
        // Collapsing one (its centre marker) glides them back together, like zooming out (splitting false).
        const gathering = Boolean(previousGroups && listingSpreads.length < spreadsBefore.length && previousGroups.zoom === zoom);
        const animate = Boolean(animator)
            && previousGroups != null
            && (spreading || gathering || (previousGroups.zoom !== zoom
                && (previousGroups.groupsPerArea !== groupsPerArea || previousGroups.icons !== icons)))
            && previousGroups.entries.length > 0
            && clusteredMapEntries.length > 0
            && renderedByKey.size > 0
            && Math.max(previousGroups.entries.length, clusteredMapEntries.length) <= LISTING_GROUP_ANIMATION_MAX_MARKERS;
        if (animate) {
            // The overlay takes over from the old markers until the glide ends.
            listingLayer.clearLayers();
            renderedByKey.clear();
            renderedSignatureByKey.clear();
        }

        const nextEntryByKey = new Map();
        clusteredMapEntries.forEach((entry) => {
            nextEntryByKey.set(entry.key, entry);
        });

        // Same zoom, other listings (an area clicked on or off, a search or filter change): the new markers
        // grow in, the ones going away shrink away. New ones are held back from the layer until they've grown.
        let heldKeys = null;
        let presenceItems = null;
        // A re-run (a card hover, new handlers) while homes are still growing in or shrinking away lets that
        // play on; the new markers stay held back until it ends.
        const continuePresence = !animate && Boolean(animator?.isRunning()) && presenceHeldKeysRef.current != null
            && previousGroups?.zoom === zoom;
        if (!continuePresence && presenceHeldKeysRef.current != null) {
            // Zoomed or regrouped meanwhile: everything is drawn afresh below.
            presenceHeldKeysRef.current = null;
            animator?.cancel();
        }
        if (!animate && animator && previousGroups && previousGroups.zoom === zoom) {
            const keysBefore = new Set(previousGroups.entries.map((entry) => entry.key));
            const added = clusteredMapEntries.filter((entry) => !keysBefore.has(entry.key));
            const removed = previousGroups.entries.filter((entry) => !nextEntryByKey.has(entry.key));
            const presence = (added.length || removed.length) && added.length + removed.length <= LISTING_PRESENCE_ANIMATION_MAX;
            // An area turned off fades once its homes have shrunk away (areaToggleFade.js).
            if (added.length || removed.length) {
                releaseHeldAreaFades(presence && removed.length ? LISTING_PRESENCE_ANIMATION_MS : 0);
            }
            if (presence) {
                presenceItems = buildPresenceItems(added, removed, (entry) => entry.visual);
                heldKeys = new Set(added.map((entry) => entry.key));
            }
        }
        const continuing = continuePresence && !presenceItems;
        if (continuing) heldKeys = presenceHeldKeysRef.current;

        // Remove entries no longer present without clearing the full layer.
        renderedByKey.forEach((markerLayer, key) => {
            if (nextEntryByKey.has(key)) return;
            listingLayer.removeLayer(markerLayer);
            renderedByKey.delete(key);
            renderedSignatureByKey.delete(key);
        });

        if (!clusteredMapEntries.length) {
            if (!presenceItems) return undefined;
            presenceHeldKeysRef.current = new Set();
            animator.start(presenceItems, () => {
                presenceHeldKeysRef.current = null;
            });
            return undefined;
        }

        const buildSignature = (entry) => {
            if (entry.kind === 'cluster') {
                const lat = Number(entry?.center?.[0]);
                const lng = Number(entry?.center?.[1]);
                const latKey = Number.isFinite(lat) ? lat.toFixed(6) : 'nan';
                const lngKey = Number.isFinite(lng) ? lng.toFixed(6) : 'nan';
                return [
                    'cluster',
                    latKey,
                    lngKey,
                    entry.count,
                    entry.type,
                    entry.showClusterCount ? 1 : 0,
                    entry.iconSize,
                    entry.noBorder ? 1 : 0,
                    entry.shapeVisual ? `${entry.shapeVisual.shape}:${entry.shapeVisual.fill}:${entry.shapeVisual.halo}:${entry.shapeVisual.radius}` : '',
                ].join('|');
            }

            const listing = entry.listing;
            const listingId = String(listing?.id || entry.key || '');
            const lat = Number(entry.lat);
            const lng = Number(entry.lng);
            const latKey = Number.isFinite(lat) ? lat.toFixed(6) : 'nan';
            const lngKey = Number.isFinite(lng) ? lng.toFixed(6) : 'nan';
            // Hover emphasis is toggled on the marker's element (see the hover effect below), not baked into
            // the key, so hovering a card never rebuilds a marker.
            return [
                'listing',
                listingId,
                latKey,
                lngKey,
                getListingMarkerGroup(listing?.type, listing),
                showListingIcons ? 1 : 0,
                allowMapListingInteractions ? 1 : 0,
                entry.shapeVisual ? `${entry.shapeVisual.shape}:${entry.shapeVisual.fill}:${entry.shapeVisual.halo}:${entry.shapeVisual.radius}:${entry.shapeVisual.visited ? 'v' : ''}` : '',
            ].join('|');
        };

        const buildMarker = (entry) => {
            if (entry.kind === 'cluster') {
                const clusterIds = (entry.listings || []).map((item) => String(item.id));
                const clusterHovered = hoveredListingIdRef.current != null && clusterIds.includes(hoveredListingIdRef.current);
                const marker = L.marker(entry.center, {
                    pane: LIST_VIEW_LISTINGS_PANE,
                    // Count-less groups (zoomed far out) let clicks through to the area below.
                    interactive: entry.showClusterCount,
                    icon: entry.shapeVisual
                        ? getShapeIcon(entry.shapeVisual, clusterHovered)
                        : getListingClusterIcon(
                            entry.count,
                            entry.type,
                            entry.showClusterCount,
                            entry.iconSize,
                            entry.noBorder,
                        ),
                });
                marker.__listingIds = clusterIds;
                if (entry.showClusterCount) {
                    // Zooms in until the group splits; homes at one address never split, so they're listed.
                    marker.on('click', () => {
                        const splitZoom = findSplitZoom(
                            listingAreaGroupIndex,
                            entry,
                            mapInstance.getZoom(),
                            maxZoom,
                            projectListViewPoint(mapInstance),
                            SHAPE_OVERLAP_PX,
    shapeRadiusAtZoom,
                        );
                        if (splitZoom != null) {
                            mapInstance.setView(entry.center, splitZoom);
                            return;
                        }
                        // Homes at one address: the closest zoom, then spread out around the point, beside the
                        // ones already open there.
                        setListingSpreads((current) => [...current.filter((item) => item.zoom === maxZoom), {
                            ids: new Set(entry.listings.map((listing) => listing.id)),
                            lat: entry.center[0],
                            lng: entry.center[1],
                            zoom: maxZoom,
                        }]);
                        if (mapInstance.getZoom() !== maxZoom) mapInstance.setView(entry.center, maxZoom);
                    });
                }
                return marker;
            }

            const listing = entry.listing;
            // The entry's position: a spread listing is drawn beside its real point.
            const position = [entry.lat, entry.lng];
            if (!listing || !Number.isFinite(position[0]) || !Number.isFinite(position[1])) {
                return null;
            }

            const listingId = String(listing.id);
            const isHovered = hoveredListingIdRef.current === listingId;
            if (showListingIcons) {
                const marker = L.marker(position, {
                    pane: LIST_VIEW_LISTINGS_PANE,
                    interactive: allowMapListingInteractions,
                    icon: getShapeIcon(entry.shapeVisual, isHovered),
                });
                // The mini card (below) finds the home under a finger through this.
                marker.__listing = listing;
                marker.__listingIds = [listingId];
                if (allowMapListingInteractions) {
                    marker.on('click', (event) => {
                        // A tap opens the mini card first; a mouse click opens the listing.
                        if (listingMiniCardRef.current?.handleMarkerTap(listing, event)) return;
                        handleMapListingClick(listing, event);
                    });
                    marker.on('mouseover', () => {
                        infoPanelRef.current?.setHoveredMapListing(listingId);
                        setHoveredListingId(listingId); // highlight its card in the list (and vice versa)
                    });
                    marker.on('mouseout', () => {
                        infoPanelRef.current?.clearHoveredMapListing(listingId);
                        setHoveredListingId((current) => (current === listingId ? null : current));
                    });
                }
                return marker;
            }

            // A square in its type's colour (dotSquares.js), bigger while its card is hovered.
            const markerGroup = getListingMarkerGroup(listing.type, listing);
            const dot = new DotSquareMarker(position, {
                pane: LIST_VIEW_LISTINGS_PANE,
                interactive: false,
                radius: isHovered ? 8 : 4.6,
                color: '#ffffff',
                fillColor: getMarkerColor(markerGroup),
                fillOpacity: 1,
                weight: isHovered ? 2.5 : 1.5,
            });
            dot.__listingIds = [listingId];
            return dot;
        };

        let cancelled = false;
        let drawIndex = 0;
        const batchSize = 420;

        const drawChunk = () => {
            if (cancelled) return;
            const end = Math.min(drawIndex + batchSize, clusteredMapEntries.length);

            for (let i = drawIndex; i < end; i += 1) {
                const entry = clusteredMapEntries[i];
                const key = entry.key;
                if (heldKeys?.has(key)) continue;
                const nextSignature = buildSignature(entry);
                if (renderedSignatureByKey.get(key) === nextSignature) {
                    continue;
                }

                const previousMarker = renderedByKey.get(key);
                if (previousMarker) {
                    listingLayer.removeLayer(previousMarker);
                    renderedByKey.delete(key);
                    renderedSignatureByKey.delete(key);
                }

                let marker = buildMarker(entry);
                if (!marker) continue;
                // A spread home gets a thin line back to its real point, drawn under its marker.
                if (entry.spreadFrom) {
                    const leg = L.polyline([[entry.spreadFrom.lat, entry.spreadFrom.lng], [entry.lat, entry.lng]], {
                        pane: LIST_VIEW_LISTINGS_PANE,
                        interactive: false,
                        color: getMarkerColor(getListingMarkerGroup(entry.listing?.type, entry.listing)),
                        weight: 2,
                        opacity: 0.9,
                        dashArray: '1 6',
                    });
                    marker = L.featureGroup([leg, marker]);
                }
                listingLayer.addLayer(marker);
                renderedByKey.set(key, marker);
                renderedSignatureByKey.set(key, nextSignature);
            }

            drawIndex = end;
            if (drawIndex < clusteredMapEntries.length) {
                listingLayerBuildRafRef.current = requestAnimationFrame(drawChunk);
            } else {
                listingLayerBuildRafRef.current = null;
            }
        };

        // When the homes have grown in, they join the layer.
        const endPresence = () => {
            presenceHeldKeysRef.current = null;
            if (cancelled) return;
            heldKeys = null;
            cancelListingLayerBuild();
            drawIndex = 0;
            drawChunk();
        };
        if (presenceItems) {
            presenceHeldKeysRef.current = heldKeys;
            listingLayerBuildRafRef.current = requestAnimationFrame(drawChunk);
            animator.start(presenceItems, endPresence);
        } else if (continuing) {
            listingLayerBuildRafRef.current = requestAnimationFrame(drawChunk);
            animator.setOnDone(endPresence);
        } else if (!animate) {
            listingLayerBuildRafRef.current = requestAnimationFrame(drawChunk);
        } else {
            animator.start(
                buildGroupAnimationItems(
                    previousGroups.entries,
                    clusteredMapEntries,
                    spreading || groupsPerArea > previousGroups.groupsPerArea
                        || (groupsPerArea === previousGroups.groupsPerArea && zoom > previousGroups.zoom),
                    (entry) => entry.visual,
                ),
                () => {
                    if (!cancelled) drawChunk();
                },
            );
        }
        // A new zoom gesture ends the glide, so markers never animate at the wrong scale.
        const handleZoomStart = () => animator?.finish();
        mapInstance.on('zoomstart', handleZoomStart);
        return () => {
            cancelled = true;
            mapInstance.off('zoomstart', handleZoomStart);
            // Re-runs (e.g. a card hover) draw the current entries straight away, except homes still growing
            // in or shrinking away (above).
            if (presenceHeldKeysRef.current == null) animator?.cancel();
            cancelListingLayerBuild();
        };
    }, [
        cancelListingLayerBuild,
        clusteredMapEntries,
        getShapeIcon,
        listingAreaGroupIndex,
        listingSpreads,
        mapZoom,
        getListingClusterIcon,
        handleMapListingClick,
        allowMapListingInteractions,
        mapInstance,
        mapPanesReady,
        showListingIcons,
    ]);

    // A small round "merge" marker at each spread's centre (shapeMarkers.js SPREAD_COLLAPSE_GLYPH), so the
    // fanned-out homes can be gathered again without zooming (as in kartvy). Tapping one drops that spread,
    // which glides its homes back into their count marker (the `gathering` animation in the marker effect).
    // Defined after that effect so it runs after it (its clearLayers on the spread glide won't wipe these).
    useEffect(() => {
        const layer = listingLayerRef.current;
        if (!layer || !mapInstance || !mapPanesReady || !listingSpreads.length) return undefined;
        const icon = divIcon({
            className: 'list-view-listing-cluster-wrapper listing-spread-collapse',
            html: '<span class="listing-spread-collapse-dot" role="button" aria-label="'
                + (languageState.swedish ? 'Slå ihop bostäderna' : 'Merge the homes') + '">'
                + SPREAD_COLLAPSE_GLYPH + '</span>',
            iconSize: [24, 24],
            iconAnchor: [12, 12],
        });
        const markers = listingSpreads.map((item) => {
            const marker = L.marker([item.lat, item.lng], {
                pane: LIST_VIEW_LISTINGS_PANE,
                interactive: true,
                icon,
                zIndexOffset: 2000, // above the spread homes, at their shared centre
            });
            marker.on('click', (event) => {
                L.DomEvent.stop(event);
                setListingSpreads((current) => current.filter((other) => other !== item));
            });
            layer.addLayer(marker);
            return marker;
        });
        return () => markers.forEach((marker) => layer.removeLayer(marker));
    }, [listingSpreads, mapInstance, mapPanesReady, languageState.swedish]);

    // Hovering a listing card highlights its marker on the map (and the marker a listing sits in, if it's
    // grouped): toggle the emphasis straight on the drawn markers, so no marker is rebuilt. A rebuild while a
    // card is hovered re-applies it from hoveredListingIdRef (buildMarker above).
    useEffect(() => {
        const markers = renderedMarkerByKeyRef.current;
        if (!markers) return;
        const id = hoveredListingId;
        markers.forEach((marker) => {
            const match = id != null && Array.isArray(marker.__listingIds) && marker.__listingIds.includes(id);
            const icon = marker._icon && typeof marker._icon.querySelector === 'function'
                ? marker._icon.querySelector('.listing-shape-marker')
                : null;
            if (icon) {
                icon.classList.toggle('is-emphasized', match);
            } else if (typeof marker.setRadius === 'function' && typeof marker.setStyle === 'function') {
                // A zoomed-out dot.
                marker.setRadius(match ? 9 : 4.6);
                marker.setStyle({ weight: match ? 4 : 1 });
            }
        });
    }, [hoveredListingId]);

    // Remembers whether the last pointer interaction was a touch tap, so the area hover look is skipped on
    // touch (see the mouseover handler): a tap fires a synthesized mouseover that would otherwise flash the
    // area's coloured map before the tile's own select/deselect animation (as in kartvy).
    const lastPointerTypeRef = useRef('mouse');

    // Touch screens: tapping a home on the map opens a mini card for it; tapping the card opens the listing
    // (listingMiniCard.js). A long press anywhere on the map does nothing (mapTouchGuard.js).
    listingPeekStateRef.current = { areaStats, swedish: languageState.swedish, handleMapListingClick };
    useEffect(() => {
        if (!mapInstance) return undefined;
        const detachTouchGuard = attachMapTouchGuard(mapInstance);
        const pointerContainer = mapInstance.getContainer();
        const onPointerDown = (event) => { lastPointerTypeRef.current = event.pointerType || 'mouse'; };
        pointerContainer.addEventListener('pointerdown', onPointerDown, true);
        const listingAt = (event) => {
            const point = mapInstance.mouseEventToContainerPoint(event);
            let best = null;
            renderedMarkerByKeyRef.current.forEach((marker) => {
                const listing = marker?.__listing;
                if (!listing || !marker.getLatLng) return;
                const distance = mapInstance.latLngToContainerPoint(marker.getLatLng()).distanceTo(point);
                if (distance <= SHAPE_MARKER_RADIUS + 6 && (!best || distance < best.distance)) best = { listing, distance };
            });
            return best?.listing || null;
        };
        const miniCard = attachListingMiniCard(mapInstance, {
            listingAt,
            cardFor: (listing) => {
                const { areaStats: stats, swedish } = listingPeekStateRef.current;
                const code = extractListingRegsoCode(listing);
                return { score: listing.betyg ?? (code && stats ? stats[`combined_${code}`] : null), swedish };
            },
            onOpen: (listing, event) => listingPeekStateRef.current.handleMapListingClick(listing, event),
        });
        listingMiniCardRef.current = miniCard;
        return () => {
            miniCard.detach();
            detachTouchGuard();
            pointerContainer.removeEventListener('pointerdown', onPointerDown, true);
            if (listingMiniCardRef.current === miniCard) listingMiniCardRef.current = null;
        };
    }, [mapInstance]);

    const handleListingCardDoubleClick = useCallback((listing) => {
        if (!mapInstance) return;
        const lat = Number(listing?.lat);
        const lng = Number(listing?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

        const target = [lat, lng];
        const currentZoom = mapInstance.getZoom() ?? 5;
        const maxZoom = mapInstance.getMaxZoom?.() ?? 18;
        const noClusterZoom = Math.max(
            LIST_VIEW_FOCUS_ZOOM,
            maxZoom - LIST_VIEW_CLUSTER_DISABLE_ZOOM_OFFSET,
        );
        const bounds = mapInstance.getBounds();
        const isInView = bounds ? bounds.contains(target) : false;
        const isZoomedOut = currentZoom < noClusterZoom;
        if (!isInView || isZoomedOut) {
            mapInstance.stop();
            mapInstance.flyTo(target, Math.max(currentZoom, noClusterZoom), {
                animate: true,
                duration: LIST_VIEW_FLY_DURATION,
                easeLinearity: LIST_VIEW_FLY_EASE,
            });
        }
    }, [mapInstance]);

    const focusMapToListings = useCallback((items, forceSingleFocus = false, maxAutoZoom = null) => {
        if (!mapInstance || !Array.isArray(items) || !items.length) return;
        const points = items
            .filter((item) => Number.isFinite(item?.lat) && Number.isFinite(item?.lng))
            .map((item) => [item.lat, item.lng]);
        if (!points.length) return;

        const limitZoom = (zoom) => (
            Number.isFinite(maxAutoZoom) ? Math.min(zoom, maxAutoZoom) : zoom
        );

        mapInstance.stop();
        if (forceSingleFocus || points.length === 1) {
            const [lat, lng] = points[0];
            const targetZoom = limitZoom(14);
            mapInstance.flyTo([lat, lng], targetZoom, {
                animate: true,
                duration: LIST_VIEW_FLY_DURATION,
                easeLinearity: LIST_VIEW_FLY_EASE,
            });
            return;
        }

        const uniqueRegsoIds = new Set(
            items.map((item) => extractListingRegsoCode(item)).filter(Boolean),
        );
        const hasMultipleRegsoIds = uniqueRegsoIds.size > 1;

        const limitedPoints = points.slice(0, 240);
        let minLat = Infinity;
        let maxLat = -Infinity;
        let minLng = Infinity;
        let maxLng = -Infinity;
        for (let i = 0; i < limitedPoints.length; i += 1) {
            const [lat, lng] = limitedPoints[i];
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
            if (lng < minLng) minLng = lng;
            if (lng > maxLng) maxLng = lng;
        }

        const center = [(minLat + maxLat) / 2, (minLng + maxLng) / 2];
        const largerSpan = Math.max(maxLat - minLat, maxLng - minLng);
        let targetZoom =
            largerSpan > 2.5 ? 11
                : largerSpan > 1.2 ? 12
                    : largerSpan > 0.65 ? 13
                        : largerSpan > 0.3 ? 13
                            : 14;
        if (hasMultipleRegsoIds) {
            targetZoom = Math.min(targetZoom, LIST_VIEW_MULTI_REGSO_MAX_AUTO_ZOOM);
        }
        targetZoom = limitZoom(targetZoom);

        mapInstance.flyTo(center, targetZoom, {
            animate: true,
            duration: LIST_VIEW_FLY_DURATION,
            easeLinearity: LIST_VIEW_FLY_EASE,
        });
    }, [mapInstance]);
    const focusMapToRegso = useCallback((regsoId) => {
        if (!mapInstance) return false;
        const regsoCode = String(regsoId || '').trim();
        if (!regsoCode) return false;
        const bounds = regsoBoundsByCode.get(regsoCode);
        if (!bounds) return false;
        const { west, south, east, north } = bounds;
        if (![west, south, east, north].every(Number.isFinite)) return false;
        const center = [(south + north) / 2, (west + east) / 2];
        const span = Math.max(north - south, east - west);
        const targetZoom =
            span > 1.2 ? 11
                : span > 0.65 ? 12
                    : span > 0.3 ? 13
                        : 14;
        mapInstance.stop();
        mapInstance.flyTo(center, targetZoom, {
            animate: true,
            duration: LIST_VIEW_FLY_DURATION,
            easeLinearity: LIST_VIEW_FLY_EASE,
        });
        return true;
    }, [mapInstance, regsoBoundsByCode]);
    // The map position: back where it was after a reload; fitted to the picks when they came from kartvy.
    useEffect(() => {
        if (!mapInstance) return undefined;
        const save = () => {
            const centre = mapInstance.getCenter();
            saveViewState('listvy', { centre: [centre.lat, centre.lng], zoom: mapInstance.getZoom() });
        };
        mapInstance.on('moveend', save);
        return () => mapInstance.off('moveend', save);
    }, [mapInstance]);

    const focusMapToRegsoIds = useCallback((regsoIds) => {
        if (!mapInstance) return false;
        const normalizedIds = Array.from(new Set(
            (Array.isArray(regsoIds) ? regsoIds : [])
                .map((value) => String(value || '').trim())
                .filter(Boolean),
        ));
        if (!normalizedIds.length) return false;
        if (normalizedIds.length === 1) {
            return focusMapToRegso(normalizedIds[0]);
        }

        let minSouth = Infinity;
        let minWest = Infinity;
        let maxNorth = -Infinity;
        let maxEast = -Infinity;
        let hasBounds = false;

        normalizedIds.forEach((regsoCode) => {
            const bounds = regsoBoundsByCode.get(regsoCode);
            if (!bounds) return;
            const { south, west, north, east } = bounds;
            if (![south, west, north, east].every(Number.isFinite)) return;
            if (south < minSouth) minSouth = south;
            if (west < minWest) minWest = west;
            if (north > maxNorth) maxNorth = north;
            if (east > maxEast) maxEast = east;
            hasBounds = true;
        });

        if (!hasBounds) return false;

        const center = [(minSouth + maxNorth) / 2, (minWest + maxEast) / 2];
        const span = Math.max(maxNorth - minSouth, maxEast - minWest);
        let targetZoom =
            span > 2.5 ? 11
                : span > 1.2 ? 12
                    : span > 0.65 ? 13
                        : span > 0.3 ? 13
                            : 14;
        targetZoom = Math.min(targetZoom, LIST_VIEW_MULTI_REGSO_MAX_AUTO_ZOOM);

        mapInstance.stop();
        mapInstance.flyTo(center, targetZoom, {
            animate: true,
            duration: LIST_VIEW_FLY_DURATION,
            easeLinearity: LIST_VIEW_FLY_EASE,
        });
        return true;
    }, [focusMapToRegso, mapInstance, regsoBoundsByCode]);

    // Picks that came from kartvy: fit the map to them once their areas' bounds are known.
    const fittedRestoredRef = useRef(false);
    useEffect(() => {
        if (fittedRestoredRef.current || !restored.fromOtherView || !mapInstance || !regsoBoundsByCode.size) return;
        fittedRestoredRef.current = true;
        focusMapToRegsoIds(restored.saved.picks.flatMap((pick) => pick.regsoIds || []));
    }, [focusMapToRegsoIds, mapInstance, regsoBoundsByCode, restored]);

    // Searches that cover many areas (kommun, postort, ort, län, a word in area names) fit the whole of them on the
    // map instead of zooming in on the first page of listings. Kommun-wide searches take every area in the
    // kommuns their listings are in. A single area keeps the closer zoom (focusMapToListings / focusMapToRegso).
    const focusMapToSearchArea = useCallback((suggestion, listingsForFocus = []) => {
        if (!mapInstance || !suggestion) return false;
        // A street: fly to it, a bit closer than to its area.
        if (Array.isArray(suggestion.focusPoint) && suggestion.focusPoint.every(Number.isFinite)) {
            mapInstance.stop();
            mapInstance.flyTo(suggestion.focusPoint, LIST_VIEW_STREET_ZOOM, {
                animate: true,
                duration: LIST_VIEW_FLY_DURATION,
                easeLinearity: LIST_VIEW_FLY_EASE,
            });
            return true;
        }
        const ids = new Set();
        const lanRegsoIds = suggestion.type === 'lan' ? regsoIdsForLan(suggestion) : [];
        if (lanRegsoIds.length > 0) {
            lanRegsoIds.forEach((id) => ids.add(id));
        } else if (Array.isArray(suggestion.regsoIds) && suggestion.regsoIds.length > 1) {
            suggestion.regsoIds.forEach((value) => {
                const id = String(value || '').trim();
                if (id) ids.add(id);
            });
        } else if (['kommun', 'ort', 'lan'].includes(suggestion.type)) {
            const kommunCodes = new Set();
            (Array.isArray(listingsForFocus) ? listingsForFocus : []).forEach((listing) => {
                const code = String(extractListingRegsoCode(listing) || '').slice(0, 4);
                if (/^\d{4}$/.test(code)) kommunCodes.add(code);
            });
            kommunCodes.forEach((code) => {
                (regsoIdsByKommunCode.get(code) || []).forEach((id) => ids.add(id));
            });
        }
        if (ids.size < 2) return false;
        let south = Infinity;
        let west = Infinity;
        let north = -Infinity;
        let east = -Infinity;
        ids.forEach((id) => {
            const bounds = regsoBoundsByCode.get(id);
            if (!bounds || ![bounds.south, bounds.west, bounds.north, bounds.east].every(Number.isFinite)) return;
            south = Math.min(south, bounds.south);
            west = Math.min(west, bounds.west);
            north = Math.max(north, bounds.north);
            east = Math.max(east, bounds.east);
        });
        if (![south, west, north, east].every(Number.isFinite)) return false;
        mapInstance.stop();
        mapInstance.flyToBounds([[south, west], [north, east]], {
            padding: [16, 16],
            maxZoom: LIST_VIEW_AREA_SEARCH_MAX_ZOOM,
            duration: LIST_VIEW_FLY_DURATION,
            easeLinearity: LIST_VIEW_FLY_EASE,
        });
        return true;
    }, [mapInstance, regsoBoundsByCode, regsoIdsByKommunCode, regsoIdsForLan]);

    // Filters the server applies, so pages and totals match them instead of a page being filtered after
    // it loaded (which left pages short or empty). Area-score filters go as the areas they hide, per
    // listing type. An area picked on the map outside the filters stays hidden in bigger searches too:
    // its own (regso) request brings its listings, so they aren't counted twice.
    const serverListingFiltersKey = useMemo(() => {
        const filters = {};
        if (listingFilters.houseType && listingFilters.houseType !== 'all') {
            // A comma-list of apartments/houses/rowhouse (multi-select); the server builds type IN (...).
            filters.houseType = listingFilters.houseType;
        }
        Object.entries(filterContext).forEach(([name, value]) => {
            if (value != null) filters[name] = value;
        });
        if (regsoFiltersActive) {
            const apartmentExcluded = [];
            const houseExcluded = [];
            searchRegsoSet.forEach((regsoCode) => {
                const commonOk = passesRegsoCommonFilters(regsoCode);
                if (!commonOk || (regsoFilterContext.apartmentActive
                    && !regsoScoreInRange('sqm_price_apartment_', regsoCode, regsoFilterContext.apartmentRange))) {
                    apartmentExcluded.push(regsoCode);
                }
                if (!commonOk || (regsoFilterContext.houseActive
                    && !regsoScoreInRange('sqm_price_house_', regsoCode, regsoFilterContext.houseRange))) {
                    houseExcluded.push(regsoCode);
                }
            });
            if (apartmentExcluded.length) filters.excludeApartmentRegsoIds = apartmentExcluded.sort();
            if (houseExcluded.length) filters.excludeHouseRegsoIds = houseExcluded.sort();
        }
        return JSON.stringify(filters);
    }, [
        filterContext,
        listingFilters.houseType,
        passesRegsoCommonFilters,
        regsoFilterContext,
        regsoFiltersActive,
        regsoScoreInRange,
        searchRegsoSet,
    ]);
    const serverListingFilters = useMemo(() => JSON.parse(serverListingFiltersKey), [serverListingFiltersKey]);

    // Price (and fee, field 'fee') bars in the filter drawer: the server counts the search's areas with the drawer's filters as
    // they are being set (not the price), the area-score ones sent as the areas they hide, like the list.
    // No search yet: null, so the drawer counts all of Sweden.
    const getPriceHistogram = useCallback((filters, edges, field = 'price') => {
        if (!hasSearchSelection || !searchRegsoSet.size) return null;
        const active = (range) => Array.isArray(range) && !isFullRange(range);
        const common = [
            ['combined_', filters.totalValue], ['netInc_', filters.incomeValue],
            ['unemployment_', filters.unemploymentValue], ['apartment_', filters.ownedValue],
            ['education_', filters.educationValue],
        ].filter(([, range]) => active(range));
        const apartmentRange = active(filters.apartmentValue) ? filters.apartmentValue : null;
        const houseRange = active(filters.houseValue) ? filters.houseValue : null;
        const apartmentExcluded = [];
        const houseExcluded = [];
        searchRegsoSet.forEach((regsoCode) => {
            if (selectedFilteredOutRegsoSet.has(regsoCode)) return;
            const commonOk = common.every(([prefix, range]) => regsoScoreInRange(prefix, regsoCode, range));
            if (!commonOk || (apartmentRange && !regsoScoreInRange('sqm_price_apartment_', regsoCode, apartmentRange))) {
                apartmentExcluded.push(regsoCode);
            }
            if (!commonOk || (houseRange && !regsoScoreInRange('sqm_price_house_', regsoCode, houseRange))) {
                houseExcluded.push(regsoCode);
            }
        });
        return fetchPriceHistogram({
            edges,
            ...(field === 'price' ? {} : { field }),
            regsoIds: [...searchRegsoSet].sort(),
            forSale: getForSaleFromFeedTab(listingFeedTab),
            ...histogramFilterBody(filters, field),
            ...(apartmentExcluded.length ? { excludeApartmentRegsoIds: apartmentExcluded.sort() } : {}),
            ...(houseExcluded.length ? { excludeHouseRegsoIds: houseExcluded.sort() } : {}),
        });
    }, [hasSearchSelection, listingFeedTab, regsoScoreInRange, searchRegsoSet, selectedFilteredOutRegsoSet]);
    const priceHistogramScope = useMemo(
        () => ({}),
        [hasSearchSelection, listingFeedTab, regsoScoreInRange, searchRegsoSet, selectedFilteredOutRegsoSet],
    );

    useEffect(() => {
        setCurrentListingsPage(1);
    }, [serverListingFiltersKey]);

    const fetchSuggestionListingsSlice = useCallback(async (
        suggestionToApply,
        suggestionId,
        {
            offset = 0,
            limit = LISTINGS_PAGE_SIZE,
            signal,
            requestTag = 'slice',
        } = {},
    ) => {
        const safeOffset = Math.max(0, Math.floor(Number(offset) || 0));
        const safeLimit = Math.max(1, Math.floor(Number(limit) || LISTINGS_PAGE_SIZE));
        const forSale = getForSaleFromFeedTab(listingFeedTab);
        const excludedRegsoIds = Array.isArray(suggestionToApply?.excludedRegsoIds)
            ? suggestionToApply.excludedRegsoIds
            : [];

        const hasServerFilters = Object.keys(serverListingFilters).length > 0;
        // A server without the listing filters answers 400: fall back to the unfiltered request (the
        // page is still filtered client-side by matchesListing).
        const fetchWithFilterFallback = async (request) => {
            const response = await request(serverListingFilters);
            if (response.status !== 400 || !hasServerFilters) return response;
            return request({});
        };

        const fetchFilteredListings = async (payload) => {
            if (!payload) return { listings: [], total: 0 };
            const response = await fetchWithFilterFallback((filters) => apiFetch(FILTER_ENDPOINT, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...payload,
                    ...filters,
                    // Areas deselected on the map inside this search.
                    ...(excludedRegsoIds.length ? { excludeRegsoIds: excludedRegsoIds } : {}),
                    offset: safeOffset,
                    limit: safeLimit,
                    forSale,
                }),
                signal,
            }));
            if (!response.ok) {
                throw new Error(`Search request failed: ${response.status}`);
            }
            const result = await response.json();
            const normalized = normalizeListingsPayload(result, `list-view-filter-${suggestionId}-${requestTag}`);
            return {
                listings: normalized,
                total: parseListingsTotal(result, normalized.length),
            };
        };

        const fetchRegsoListings = async (regsoId) => {
            if (!regsoId) return { listings: [], total: 0 };
            const response = await fetchWithFilterFallback((filters) => {
                const url = new URL(`${REGSO_ENDPOINT_BASE}/${encodeURIComponent(regsoId)}`);
                url.searchParams.set('offset', String(safeOffset));
                url.searchParams.set('limit', String(safeLimit));
                url.searchParams.set('forSale', String(forSale));
                // One area: the area-score filters are applied client-side (the area shows or not).
                Object.entries(filters).forEach(([name, value]) => {
                    if (!Array.isArray(value)) url.searchParams.set(name, String(value));
                });
                return apiFetch(url.toString(), {
                    method: 'GET',
                    headers: {
                        Accept: 'application/json',
                    },
                    signal,
                });
            });
            if (!response.ok) {
                throw new Error(`Regso search request failed: ${response.status}`);
            }
            const result = await response.json();
            const normalized = normalizeListingsPayload(result, `list-view-regso-${suggestionId}-${requestTag}`);
            return {
                listings: normalized,
                total: parseListingsTotal(result, normalized.length),
            };
        };

        if (
            (suggestionToApply.type === 'omrade'
                || suggestionToApply.type === 'omrade-map'
                || suggestionToApply.type === 'omrade-word')
            && suggestionToApply.regsoId
        ) {
            const regsoId = String(suggestionToApply.regsoId || '').trim();
            return fetchRegsoListings(regsoId);
        }
        // Multi-area searches (a word in area names, a postort, an ort with a known postort) by their areas.
        // A backend without `regsoIds` answers 400; then the search goes by name as before.
        if ((suggestionToApply.type === 'omrade-word' || suggestionToApply.type === 'ort' || suggestionToApply.type === 'postort')
            && Array.isArray(suggestionToApply.regsoIds) && suggestionToApply.regsoIds.length > 0) {
            try {
                return await fetchFilteredListings({ regsoIds: suggestionToApply.regsoIds });
            } catch (error) {
                if (signal?.aborted) throw error;
                if (suggestionToApply.type === 'postort') {
                    const postort = String(suggestionToApply.postort || '').trim();
                    return postort ? fetchFilteredListings({ postort }) : { listings: [], total: 0 };
                }
                if (suggestionToApply.type === 'omrade-word') {
                    const word = String(suggestionToApply.word || suggestionToApply.label || '').trim();
                    return word ? fetchFilteredListings({ ort: word }) : { listings: [], total: 0 };
                }
            }
        }

        const payload = {};
        if (suggestionToApply.kommun) payload.kommun = suggestionToApply.kommun;
        if (suggestionToApply.ort) payload.ort = suggestionToApply.ort;
        if (suggestionToApply.lan) payload.lan = suggestionToApply.lan;
        if (!payload.kommun && !payload.ort && !payload.lan) {
            return { listings: [], total: 0 };
        }
        return fetchFilteredListings(payload);
    }, [listingFeedTab, serverListingFilters]);

    const fetchSuggestionListingsPage = useCallback(async (suggestionToApply, suggestionId, pageNumber) => {
        const safePage = Math.max(1, Math.floor(Number(pageNumber) || 1));
        const offset = (safePage - 1) * LISTINGS_PAGE_SIZE;
        return fetchSuggestionListingsSlice(suggestionToApply, suggestionId, {
            offset,
            limit: LISTINGS_PAGE_SIZE,
            requestTag: `page-${safePage}`,
        });
    }, [fetchSuggestionListingsSlice]);

    const fetchSuggestionListingsForMap = useCallback(async (suggestionToApply, suggestionId, signal) => {
        const merged = [];
        const seen = new Set();
        const take = (chunkListings) => chunkListings.forEach((listing) => {
            const listingId = getListingIdentity(listing);
            if (!listingId || seen.has(listingId)) return;
            seen.add(listingId);
            merged.push(listing);
        });
        const fetchBatch = (offset) => fetchSuggestionListingsSlice(suggestionToApply, suggestionId, {
            offset,
            limit: MAP_LISTINGS_BATCH_SIZE,
            signal,
            requestTag: `map-${offset}`,
        }).then((chunk) => {
            const parsedTotal = Number(chunk?.total);
            return {
                listings: Array.isArray(chunk?.listings) ? chunk.listings : [],
                total: Number.isFinite(parsedTotal) && parsedTotal >= 0 ? Math.floor(parsedTotal) : null,
            };
        });

        // The first batch tells how many there are; the rest are fetched a few at a time and taken in order.
        const first = await fetchBatch(0);
        take(first.listings);
        let offset = first.listings.length;
        if (!first.listings.length) return merged;
        if (first.total != null) {
            const offsets = [];
            for (let next = offset; next < first.total; next += MAP_LISTINGS_BATCH_SIZE) offsets.push(next);
            const chunks = new Array(offsets.length);
            let cursor = 0;
            const worker = async () => {
                while (cursor < offsets.length) {
                    const index = cursor;
                    cursor += 1;
                    chunks[index] = await fetchBatch(offsets[index]);
                }
            };
            await Promise.all(Array.from({ length: Math.min(MAP_LISTINGS_PARALLEL, offsets.length) }, worker));
            for (let i = 0; i < chunks.length; i += 1) {
                take(chunks[i].listings);
                // One that came back short (the homes changed meanwhile): the ones after it would overlap.
                if (chunks[i].listings.length < MAP_LISTINGS_BATCH_SIZE) break;
            }
            return merged;
        }
        // No total from the server: one batch after another until a short one.
        let guard = 0;
        let last = first.listings.length;
        while (last >= MAP_LISTINGS_BATCH_SIZE && guard < 2000) {
            guard += 1;
            const chunk = await fetchBatch(offset);
            take(chunk.listings);
            last = chunk.listings.length;
            if (!last) break;
            offset += last;
        }
        return merged;
    }, [fetchSuggestionListingsSlice]);

    useEffect(() => {
        if (!selectedSuggestions.length) {
            setIsPageLoading(false);
            setSelectionResultsById({});
            setSelectionTotalsById({});
            return;
        }
        const targets = selectedSuggestions
            .map((suggestion) => ({
                suggestion,
                suggestionId: String(suggestion?.id || '').trim(),
            }))
            .filter((entry) => entry.suggestionId);
        if (!targets.length) {
            setIsPageLoading(false);
            setSelectionResultsById({});
            setSelectionTotalsById({});
            return;
        }

        const requestId = pageFetchRequestRef.current + 1;
        pageFetchRequestRef.current = requestId;
        setIsPageLoading(true);
        setSelectionLoadingById((prev) => {
            const next = { ...prev };
            targets.forEach((entry) => {
                next[entry.suggestionId] = true;
            });
            return next;
        });

        let cancelled = false;
        const run = async () => {
            const results = await Promise.all(targets.map(async (entry) => {
                try {
                    const data = await fetchSuggestionListingsPage(entry.suggestion, entry.suggestionId, currentListingsPage);
                    return {
                        suggestionId: entry.suggestionId,
                        suggestion: entry.suggestion,
                        listings: data.listings,
                        total: data.total,
                    };
                } catch (error) {
                    console.error('Error fetching filtered list view listings:', error);
                    return {
                        suggestionId: entry.suggestionId,
                        suggestion: entry.suggestion,
                        listings: [],
                        total: 0,
                    };
                }
            }));
            if (cancelled || pageFetchRequestRef.current !== requestId) return;

            setSelectionResultsById((prev) => {
                const next = { ...prev };
                results.forEach((entry) => {
                    next[entry.suggestionId] = entry.listings;
                });
                return next;
            });
            setSelectionTotalsById((prev) => {
                const next = { ...prev };
                results.forEach((entry) => {
                    next[entry.suggestionId] = entry.total;
                });
                return next;
            });

            const pendingSuggestionId = String(pendingSuggestionFocusRef.current || '').trim();
            if (pendingSuggestionId) {
                const focusedEntry = results.find((entry) => entry.suggestionId === pendingSuggestionId);
                if (focusedEntry) {
                    pendingSuggestionFocusRef.current = '';
                    if (focusMapToSearchArea(focusedEntry.suggestion, focusedEntry.listings)) {
                        // The whole kommun / postort / … is in view.
                    } else if (focusedEntry.listings.length > 0) {
                        focusMapToListings(focusedEntry.listings, focusedEntry.listings.length <= 1);
                    } else {
                        const focused = focusMapToRegso(focusedEntry?.suggestion?.regsoId);
                        if (!focused) {
                            focusMapToRegsoIds(focusedEntry?.suggestion?.regsoIds);
                        }
                    }
                }
            }
        };

        run()
            .finally(() => {
                if (cancelled || pageFetchRequestRef.current !== requestId) return;
                setIsPageLoading(false);
                setSelectionLoadingById((prev) => {
                    let changed = false;
                    const next = { ...prev };
                    targets.forEach((entry) => {
                        if (!next[entry.suggestionId]) return;
                        delete next[entry.suggestionId];
                        changed = true;
                    });
                    return changed ? next : prev;
                });
            });

        return () => {
            cancelled = true;
        };
    }, [
        currentListingsPage,
        fetchSuggestionListingsPage,
        focusMapToListings,
        focusMapToSearchArea,
        focusMapToRegso,
        focusMapToRegsoIds,
        selectedSuggestions,
    ]);

    useEffect(() => {
        if (!selectedSuggestions.length) {
            setSelectionMapResultsById({});
            return;
        }
        const targets = selectedSuggestions
            .map((suggestion) => ({
                suggestion,
                suggestionId: String(suggestion?.id || '').trim(),
            }))
            .filter((entry) => entry.suggestionId);
        if (!targets.length) {
            setSelectionMapResultsById({});
            return;
        }

        const requestId = mapFetchRequestRef.current + 1;
        mapFetchRequestRef.current = requestId;
        let cancelled = false;
        const controller = new AbortController();

        const run = async () => {
            const results = await Promise.all(targets.map(async (entry) => {
                try {
                    const listingsForMap = await fetchSuggestionListingsForMap(
                        entry.suggestion,
                        entry.suggestionId,
                        controller.signal,
                    );
                    return {
                        suggestionId: entry.suggestionId,
                        listings: listingsForMap,
                    };
                } catch (error) {
                    if (error?.name === 'AbortError') {
                        return null;
                    }
                    console.error('Error fetching full map listings:', error);
                    return {
                        suggestionId: entry.suggestionId,
                        listings: selectionResultsByIdRef.current[entry.suggestionId] || [],
                    };
                }
            }));
            if (cancelled || mapFetchRequestRef.current !== requestId) return;

            const next = {};
            results.forEach((entry) => {
                if (!entry?.suggestionId) return;
                next[entry.suggestionId] = Array.isArray(entry.listings) ? entry.listings : [];
            });
            setSelectionMapResultsById(next);
        };

        run();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [fetchSuggestionListingsForMap, selectedSuggestions]);

    // focusMap: zoom the map to the result. Searches do; clicking an area on the map doesn't.
    const applySuggestion = useCallback((suggestion, { focusMap = true } = {}) => {
        if (!suggestion) return;
        if (suggestion?.type !== 'omrade-map') {
            const position = flatSuggestions.findIndex((entry) => entry.id === suggestion.id);
            track('search.select', {
                type: suggestion?.type || 'unknown',
                label: String(suggestion?.label || '').slice(0, 80),
                kommun: suggestion?.kommun || undefined,
                regso: suggestion?.regsoId || undefined,
                position: position >= 0 ? position + 1 : undefined,
            });
        }
        let suggestionToApply = suggestion;
        let suggestionId = String(suggestionToApply.id || '');
        if (!suggestionId) return;
        const targetRegsoId = String(suggestionToApply?.regsoId || '').trim();
        if (targetRegsoId) {
            const existingForRegso = selectedSuggestions.find(
                (entry) => String(entry?.regsoId || '').trim() === targetRegsoId,
            );
            if (existingForRegso) {
                suggestionToApply = existingForRegso;
                suggestionId = String(existingForRegso.id || '');
                if (!suggestionId) return;
                setSearchDraft('');
                setShowSuggestions(false);
                const cachedForExisting = selectionResultsByIdRef.current[suggestionId];
                if (Array.isArray(cachedForExisting)) {
                    setActiveSuggestion(existingForRegso);
                    if (!focusMap) return;
                    if (focusMapToSearchArea(suggestion.focusPoint ? suggestion : existingForRegso, cachedForExisting)) return;
                    if (cachedForExisting.length > 0) {
                        focusMapToListings(cachedForExisting, cachedForExisting.length <= 1);
                    } else {
                        const focused = focusMapToRegso(targetRegsoId);
                        if (!focused) {
                            focusMapToRegsoIds(suggestionToApply?.regsoIds);
                        }
                    }
                    return;
                }
            }
        }

        selectedSuggestionIdsRef.current.add(suggestionId);
        if (focusMap) pendingSuggestionFocusRef.current = suggestionId;
        setCurrentListingsPage(1);
        setActiveSuggestion(suggestionToApply);
        setSearchDraft('');
        setShowSuggestions(false);
        setSelectedSuggestions((prev) => (
            prev.some((entry) => entry.id === suggestionId) ? prev : [...prev, suggestionToApply]
        ));
    }, [flatSuggestions, focusMapToListings, focusMapToRegso, focusMapToRegsoIds, focusMapToSearchArea, selectedSuggestions]);

    const handleSearchSubmit = async (event) => {
        event.preventDefault();
        const selected = activeSuggestion || flatSuggestions[0] || null;
        if (selected) {
            applySuggestion(selected);
            return;
        }
        // Nothing listed yet: maybe a street whose lookup hasn't come back. Ask now and take the first.
        const query = searchDraft.trim();
        if (normalizeSearchText(query).length < LIST_VIEW_STREET_SEARCH_MIN_CHARS) return;
        try {
            const items = await lookupStreets(query);
            setStreetSuggestions({ query, items, loading: false });
            if (items[0]) applySuggestion(items[0]);
        } catch (error) {
            // Nothing found; the field keeps what was typed.
        }
    };

    const clearSearch = () => {
        setSearchDraft('');
        setActiveSuggestion(null);
        setShowSuggestions(false);
    };

    const handleSearchDraftChange = useCallback((event) => {
        const nextValue = event.target.value;
        setSearchDraft(nextValue);
        setShowSuggestions(true);
        if (!nextValue.trim()) {
            setActiveSuggestion(null);
            return;
        }
        if (activeSuggestion && nextValue !== activeSuggestion.label) {
            setActiveSuggestion(null);
        }
    }, [activeSuggestion]);

    const handleRemoveSelectedSuggestion = useCallback((suggestionId) => {
        const removed = selectedSuggestions.find((entry) => entry.id === suggestionId);
        track('search.remove', { type: removed?.type || 'unknown', label: String(removed?.label || '').slice(0, 80) });
        removeSuggestionsByIds([suggestionId]);
    }, [removeSuggestionsByIds, selectedSuggestions]);

    // "Rensa" clears every picked search, like kartvy's Rensa.
    const handleClearAllSuggestions = useCallback(() => {
        if (!selectedSuggestions.length) return;
        track('search.remove', { type: 'all' });
        removeSuggestionsByIds(selectedSuggestions.map((entry) => entry.id));
    }, [removeSuggestionsByIds, selectedSuggestions]);

    const handleBackToMapView = useCallback(() => {
        if (typeof onSwitchView === 'function') {
            onSwitchView('kartvy');
            return;
        }
        navigate('/', {
            state: {
                resetMapState: true,
                sourceView: 'listvy',
                ts: Date.now(),
            },
        });
    }, [navigate, onSwitchView]);

    const setMapCursor = useCallback((cursor) => {
        const container = mapInstance?.getContainer?.();
        if (!container) return;
        container.style.cursor = cursor || '';
    }, [mapInstance]);

    const handleRegsoTileToggle = useCallback((featureEntry) => {
        const regsoId = String(featureEntry?.properties?.regsokod || '').trim();
        if (!regsoId) return;
        // A short note on the map: "<area> aktiverat / avaktiverat".
        const announce = (active) => showMapToast(
            mapInstance,
            areaToggleToastText(regsoNameById.get(regsoId) || regsoId, active, languageState.swedish),
        );
        const selectedAreaSuggestion = selectedSuggestions.find(
            (entry) => String(entry?.regsoId || '').trim() === regsoId,
        );
        // Inside a bigger search (kommun, ort, län …): deselecting leaves the area out of that search;
        // clicking a deselected area brings it back.
        const updateExclusions = (shouldUpdate, update) => {
            setCurrentListingsPage(1);
            setSelectedSuggestions((prev) => prev.map((entry) => (
                shouldUpdate(entry)
                    ? { ...entry, excludedRegsoIds: update(Array.isArray(entry.excludedRegsoIds) ? entry.excludedRegsoIds : []) }
                    : entry
            )));
        };
        if (selectedAreaSuggestion?.id) {
            track('map.area_toggle', { selected: false, regso: regsoId });
            announce(false);
            // Also leave it out of bigger searches that contain it, so the area really ends up deselected.
            const containingIds = new Set(
                selectedSuggestions
                    .filter((entry) => entry.id !== selectedAreaSuggestion.id
                        && regsoCoverageBySuggestionId.get(String(entry?.id || '').trim())?.regsoIds.has(regsoId))
                    .map((entry) => String(entry.id)),
            );
            removeSuggestionsByIds([selectedAreaSuggestion.id]);
            if (containingIds.size) {
                updateExclusions((entry) => containingIds.has(String(entry?.id)), (ids) => [...ids, regsoId]);
            }
            return;
        }
        const isExcludedIn = (entry) => Array.isArray(entry?.excludedRegsoIds) && entry.excludedRegsoIds.includes(regsoId);
        // A click always wins over the area-score filters: an area they grey out is selected on its
        // own, also inside a bigger search (where it was hidden by the filters, not deselected).
        const hiddenByAreaFilters = regsoFiltersActive && !passesRegsoTileFilters(regsoId);
        if (selectedSuggestions.some(isExcludedIn)) {
            updateExclusions(isExcludedIn, (ids) => ids.filter((id) => id !== regsoId));
            if (!hiddenByAreaFilters) {
                track('map.area_toggle', { selected: true, regso: regsoId, within_search: true });
                announce(true);
                return;
            }
        }
        const coveringIds = hiddenByAreaFilters ? new Set() : new Set(
            selectedSuggestions
                .filter((entry) => regsoCoverageBySuggestionId.get(String(entry?.id || '').trim())?.regsoIds.has(regsoId))
                .map((entry) => String(entry.id)),
        );
        if (coveringIds.size) {
            track('map.area_toggle', { selected: false, regso: regsoId, within_search: true });
            announce(false);
            updateExclusions((entry) => coveringIds.has(String(entry?.id)), (ids) => [...ids, regsoId]);
            return;
        }
        track('map.area_toggle', { selected: true, regso: regsoId });
        announce(true);
        const regsoName = regsoNameById.get(regsoId) || regsoId;
        applySuggestion({
            id: `omrade-map:${regsoId}`,
            type: 'omrade-map',
            label: regsoName,
            kommun: '',
            ort: '',
            lan: '',
            regsoId,
            postort: '',
        }, { focusMap: false });
    }, [
        applySuggestion,
        languageState.swedish,
        mapInstance,
        passesRegsoTileFilters,
        regsoCoverageBySuggestionId,
        regsoFiltersActive,
        regsoNameById,
        removeSuggestionsByIds,
        selectedSuggestions,
    ]);

    useEffect(() => {
        regsoTileToggleHandlerRef.current = handleRegsoTileToggle;
    }, [handleRegsoTileToggle]);

    // Shift + drag a box (areaBoxSelect.js): the areas it touches all go on, or all off when most of them are on.
    const handleRegsoBoxToggle = useCallback((codes) => {
        // "Av" mode: the overlay is hidden, so the shift-drag box selects nothing (search bar only).
        if (checkedStateRef.current?.hideOverlay) return;
        if (!codes.length) return;
        if (codes.length === 1) {
            handleRegsoTileToggle({ properties: { regsokod: codes[0] } });
            return;
        }
        const turnOn = boxSelectTurnsOn(codes, (code) => searchRegsoSet.has(code));
        const changing = codes.filter((code) => searchRegsoSet.has(code) !== turnOn);
        if (!changing.length) return;
        const changingSet = new Set(changing);
        setCurrentListingsPage(1);
        if (turnOn) {
            // Areas left out of a search come back; the rest join as one search of their own.
            const excludedSomewhere = new Set(selectedSuggestions.flatMap((entry) => (
                Array.isArray(entry?.excludedRegsoIds) ? entry.excludedRegsoIds : []
            )));
            setSelectedSuggestions((prev) => prev.map((entry) => (
                Array.isArray(entry?.excludedRegsoIds) && entry.excludedRegsoIds.some((id) => changingSet.has(id))
                    ? { ...entry, excludedRegsoIds: entry.excludedRegsoIds.filter((id) => !changingSet.has(id)) }
                    : entry
            )));
            const fresh = changing.filter((code) => !excludedSomewhere.has(code));
            if (fresh.length) {
                const firstName = regsoNameById.get(fresh[0]) || fresh[0];
                const single = fresh.length === 1;
                const suggestion = {
                    id: single ? `omrade-map:${fresh[0]}` : `omrade-box:${fresh.slice().sort().join(',')}`,
                    type: single ? 'omrade-map' : 'omrade-word',
                    // Named by its size, not after one of its areas ("12 områden").
                    label: single ? firstName : `${fresh.length} ${languageState.swedish ? 'områden' : 'areas'}`,
                    kommun: '',
                    ort: '',
                    lan: '',
                    regsoId: single ? fresh[0] : '',
                    ...(single ? {} : { regsoIds: fresh }),
                    postort: '',
                };
                selectedSuggestionIdsRef.current.add(suggestion.id);
                setSelectedSuggestions((prev) => (prev.some((entry) => entry.id === suggestion.id) ? prev : [...prev, suggestion]));
            }
        } else {
            // Searches made only of these areas go; bigger ones leave them out.
            const removeIds = selectedSuggestions
                .filter((entry) => {
                    const covered = regsoCoverageBySuggestionId.get(String(entry?.id || '').trim())?.regsoIds;
                    return covered && covered.size > 0 && [...covered].every((id) => changingSet.has(id));
                })
                .map((entry) => String(entry.id));
            const removeSet = new Set(removeIds);
            if (removeIds.length) removeSuggestionsByIds(removeIds);
            setSelectedSuggestions((prev) => prev.map((entry) => {
                const id = String(entry?.id || '').trim();
                if (removeSet.has(id)) return entry;
                const covered = regsoCoverageBySuggestionId.get(id)?.regsoIds;
                const leaving = covered ? changing.filter((code) => covered.has(code)) : [];
                if (!leaving.length) return entry;
                const excluded = Array.isArray(entry.excludedRegsoIds) ? entry.excludedRegsoIds : [];
                return { ...entry, excludedRegsoIds: [...new Set([...excluded, ...leaving])] };
            }));
        }
        track('map.area_box', { selected: turnOn, count: changing.length });
        showMapToast(mapInstance, areasToggleToastText(changing.length, turnOn, languageState.swedish));
    }, [
        handleRegsoTileToggle,
        languageState.swedish,
        mapInstance,
        regsoCoverageBySuggestionId,
        regsoNameById,
        removeSuggestionsByIds,
        searchRegsoSet,
        selectedSuggestions,
    ]);
    const regsoBoxToggleRef = useRef(handleRegsoBoxToggle);
    regsoBoxToggleRef.current = handleRegsoBoxToggle;
    useEffect(() => (mapInstance ? attachAreaBoxSelect(mapInstance, {
        getLayers: () => regsoGeoJsonRef.current,
        onSelect: (codes) => regsoBoxToggleRef.current(codes),
    }) : undefined), [mapInstance]);

    const handleHelpBoxClick = useCallback((event) => {
        const toggle = event.target.closest('.helpBoxToggle');
        if (toggle) {
            event.preventDefault();
            event.stopPropagation();
            setHelpBoxCollapsed((prev) => {
                track('help.toggle', { collapsed: !prev });
                return !prev;
            });
        }
    }, []);

    useEffect(() => {
        if (!helpBoxHostRef.current) return;
        helpBoxHostRef.current.innerHTML = helpBoxData(
            checkedState,
            languageState,
            scoreToColor,
        );
        const container = helpBoxHostRef.current.querySelector('.helpBoxContainer');
        if (container) {
            container.classList.toggle('is-collapsed', helpBoxCollapsed);
        }
    }, [checkedState, helpBoxCollapsed, languageState, colourScheme]);

    const getOverlayColor = useCallback((regsoCode) => {
        if (!regsoCode || !colorCache) return '#2eb459';
        if (checkedState.squareMeterPriceApartment) return colorCache[`sqm_price_apartment_${regsoCode}`] || 'rgb(255, 0, 0)';
        if (checkedState.squareMeterPriceHouse) return colorCache[`sqm_price_house_${regsoCode}`] || 'rgb(255, 0, 0)';
        if (checkedState.ownedVsRented) return colorCache[`apartment_${regsoCode}`] || 'rgb(255, 0, 0)';
        if (checkedState.education) return colorCache[`education_${regsoCode}`] || 'rgb(0, 0, 255)';
        if (checkedState.income) return colorCache[`netInc_${regsoCode}`] || '#e8d1c5';
        if (checkedState.unemployment) return colorCache[`unemployment_${regsoCode}`] || 'rgb(255, 0, 0)';
        return colorCache[`combined_${regsoCode}`] || 'rgb(0, 0, 255)';
    }, [checkedState, colorCache]);

    // The hovered tile lives in a ref, not state, so hovering doesn't change getOverlayStyle and
    // restyle all ~3,360 tiles. Restyles that do happen keep the hover lightening.
    const hoveredRegsoTileRef = useRef('');
    const getOverlayStyle = useCallback((featureEntry) => {
        const regsoCode = String(featureEntry?.properties?.regsokod || '').trim();
        // While a search's reveal propagates, the tile shows its full colour whatever the Färger toggle is.
        const forceColour = isRevealColourForced(regsoCode);
        const effHideMap = forceColour ? false : checkedState.hideMap;
        // Landing backdrop: the overlay fades down the fixed Stockholm view by latitude — colour fills at the
        // top (~50 %), colour lines only in the middle (~35 %), no overlay at the bottom (~15 %).
        const landingBands = showLanding ? landingBandsRef.current : null;
        if (landingBands) {
            const landFill = getOverlayColor(regsoCode);
            const areaBounds = regsoBoundsByCode.get(regsoCode);
            const areaLat = areaBounds ? (areaBounds.south + areaBounds.north) / 2 : null;
            if (areaLat == null || areaLat >= landingBands.l1) {
                return { fillColor: landFill, fillOpacity: areaFillOpacity(landFill, 0.42), color: landFill, weight: 1.6, opacity: 1, stroke: true };
            }
            if (areaLat >= landingBands.l2) {
                return { fillColor: landFill, fillOpacity: 0, color: landFill, weight: 3.2, opacity: 0.6, stroke: true };
            }
            return { fillColor: landFill, fillOpacity: 0, color: landFill, weight: 0, opacity: 0, stroke: false };
        }
        const inSearchScope = !hasSearchSelection || searchRegsoSet.size === 0 || searchRegsoSet.has(regsoCode);
        const passesRegsoScope = !regsoFiltersActive || passesRegsoTileFilters(regsoCode);
        const isSelectedOutsideRegsoScope = selectedFilteredOutRegsoSet.has(regsoCode);
        // Areas outside the search keep their colours; the selection overlay dims them
        // (areaSelectionOverlay.js). The area-score filters grey out areas inside the search only: grey
        // under the dimming would be a second, darker grey.
        const isExcludedInSearch = hasSearchSelection && excludedSearchRegsoSet.has(regsoCode);
        const isVisible = !isExcludedInSearch && (!inSearchScope || passesRegsoScope || isSelectedOutsideRegsoScope);
        const isActiveTile = hasSearchSelection && inSearchScope && (passesRegsoScope || isSelectedOutsideRegsoScope);
        // Colour mode "none" (colourMode.js): no lines, except around the hovered area.
        const hideLines = forceColour ? false : Boolean(checkedState.hideMap && checkedState.hideLines);
        const isHovered = regsoCode !== '' && hoveredRegsoTileRef.current === regsoCode;
        // Left out by the filters, not by a click: light grey with a funnel (filteredAreaBadges.js).
        if (!isVisible && !isExcludedInSearch) {
            return {
                fillColor: FILTERED_OUT_FILL,
                // With no lines the light grey is a cover of its own (no hairlines between neighbours).
                fillOpacity: hideLines && !isHovered
                    ? 0
                    : (isHovered ? REGSO_HOVER_FILL_OPACITY_INACTIVE : (checkedState.hideMap ? 0.7 : 0.85)),
                color: checkedState.hideMap ? (isHovered ? '#7d8786' : '#9aa4a2') : 'black',
                opacity: 1,
                weight: isHovered ? 2 : (checkedState.hideMap ? 0.9 : 1),
                stroke: !hideLines || isHovered,
                filteredCover: hideLines && !isHovered,
                filteredOut: true,
            };
        }
        if (!isVisible) {
            return {
                // Grey also with the colours off: the areas the search's filters or a click leave out.
                fillColor: INACTIVE_REGSO_FILL_COLOR,
                // With no lines the grey is drawn by the grey cover (greyAreaCover.js), so neighbouring grey
                // areas meet without light hairlines between them.
                fillOpacity: hideLines && !isHovered ? 0 : 0.6,
                color: '#3b404a',
                opacity: 1,
                weight: 1,
                stroke: !hideLines || isHovered,
                greyCover: hideLines && !isHovered,
                // Hovered or not: its edges towards active areas keep their black line.
                greyArea: hideLines,
            };
        }
        const fillColor = getOverlayColor(regsoCode);
        const baseWeight = effHideMap ? 0.9 : 1;
        const activeWeight = effHideMap ? 1.25 : 2.4;
        const baseFillOpacity = areaFillOpacity(fillColor, isActiveTile ? 0.42 : 0.35);
        // Hovering lightens the fill so the map underneath is easier to see.
        return {
            fillColor: effHideMap ? 'transparent' : fillColor,
            fillOpacity: effHideMap
                ? 0
                : (isHovered ? REGSO_HOVER_FILL_OPACITY : baseFillOpacity),
            color: effHideMap ? fillColor : 'black',
            // With no lines, the hovered area's outline is the only one: a little heavier.
            weight: hideLines && isHovered ? 2 : (isActiveTile ? activeWeight : baseWeight),
            opacity: 1,
            stroke: !hideLines || isHovered,
        };
    }, [
        checkedState.hideMap,
        checkedState.hideLines,
        excludedSearchRegsoSet,
        getOverlayColor,
        hasSearchSelection,
        passesRegsoTileFilters,
        regsoBoundsByCode,
        regsoFiltersActive,
        searchRegsoSet,
        selectedFilteredOutRegsoSet,
        showLanding,
    ]);

    // The search's areas: everything outside them dimmed, a green line around them (same look as kartvy).
    const areaSelectionOverlayRef = useRef(null);
    useEffect(() => {
        if (!mapInstance) return undefined;
        const overlay = createAreaSelectionOverlay(mapInstance);
        areaSelectionOverlayRef.current = overlay;
        return () => {
            overlay.remove();
            areaSelectionOverlayRef.current = null;
        };
    }, [mapInstance]);
    useEffect(() => {
        const overlay = areaSelectionOverlayRef.current;
        if (!overlay) return;
        const active = hasSearchSelection && !showFavorites && searchRegsoSet.size > 0;
        // Areas clicked off stay inside the line (grey, not dimmed).
        overlay.update(overlayTopology, active ? new Set([...searchRegsoSet, ...excludedSearchRegsoSet]) : null);
    }, [excludedSearchRegsoSet, hasSearchSelection, mapInstance, overlayTopology, searchRegsoSet, showFavorites]);

    const getOverlayStyleRef = useRef(getOverlayStyle);
    getOverlayStyleRef.current = getOverlayStyle;
    // Whether the filters leave an area out (its card says so); stable, so the info panel doesn't re-render.
    const isAreaFilteredOutStable = useCallback((regsoCode) => Boolean(
        getOverlayStyleRef.current({ properties: { regsokod: regsoCode } })?.filteredOut,
    ), []);
    // Grey areas with the lines off (greyAreaCover.js), kept in step with each tile's style.
    const greyCoverRef = useRef(null);
    // Areas the filters leave out: their own light cover with the lines off, and a funnel on each.
    const filteredCoverRef = useRef(null);
    const filteredBadgesRef = useRef(null);
    const syncGreyCover = useCallback((layer, style) => {
        const code = layer.feature?.properties?.regsokod;
        filteredBadgesRef.current?.set(layer, Boolean(style?.filteredOut));
        const filteredCover = filteredCoverRef.current;
        if (filteredCover) {
            if (style?.filteredCover) filteredCover.show(layer, code);
            else filteredCover.hide(layer, code, { keepGrey: Boolean(style?.filteredOut) });
        }
        const cover = greyCoverRef.current;
        if (!cover) return;
        if (style?.greyCover) cover.show(layer, code);
        else cover.hide(layer, code, { keepGrey: Boolean(style?.greyArea) });
    }, []);
    useEffect(() => {
        if (!mapInstance) return undefined;
        const cover = createGreyAreaCover(mapInstance);
        greyCoverRef.current = cover;
        filteredCoverRef.current = createGreyAreaCover(mapInstance, FILTERED_COVER);
        filteredBadgesRef.current = createFilteredAreaBadges(mapInstance);
        regsoGeoJsonRef.current?.eachLayer((layer) => syncGreyCover(layer, getOverlayStyleRef.current(layer.feature)));
        return () => {
            cover.destroy();
            greyCoverRef.current = null;
            filteredCoverRef.current?.destroy();
            filteredCoverRef.current = null;
            filteredBadgesRef.current?.destroy();
            filteredBadgesRef.current = null;
        };
    }, [mapInstance, syncGreyCover]);
    useEffect(() => {
        regsoGeoJsonRef.current?.eachLayer((layer) => syncGreyCover(layer, getOverlayStyle(layer.feature)));
    }, [getOverlayStyle, syncGreyCover]);
    // Black lines where grey areas meet active ones (inside the search).
    useEffect(() => {
        greyCoverRef.current?.setScope(overlayTopology, (code) => searchRegsoSet.size === 0 || searchRegsoSet.has(code));
    }, [mapInstance, overlayTopology, searchRegsoSet]);
    // A tile that turns grey under the pointer (clicked off) loses the regular map inside it, and one turned
    // back on gets it again.
    const hoveredRegsoLayerRef = useRef(null);
    useEffect(() => {
        const hovered = hoveredRegsoLayerRef.current;
        if (!hovered?.feature) return;
        if (getOverlayStyle(hovered.feature).fillColor !== INACTIVE_REGSO_FILL_COLOR) {
            areaMapRevealRef.current.reveal(hovered);
        } else {
            // Once the area's homes have shrunk away, as it fades (areaToggleFade.js).
            whenAreaFadeReady(hovered.feature.properties?.regsokod, () => {
                if (hoveredRegsoLayerRef.current === hovered) areaMapRevealRef.current.hide(hovered);
            });
        }
    }, [getOverlayStyle]);
    // Re-apply the tile styles (used for the reveal-colour forcing, which isn't a React dependency).
    const checkedStateRef = useRef(checkedState);
    checkedStateRef.current = checkedState;
    const restyleRegsoTiles = useCallback((predicate) => {
        regsoGeoJsonRef.current?.eachLayer((layer) => {
            const code = String(layer.feature?.properties?.regsokod || '').trim();
            if (predicate && !predicate(code)) return;
            if (hoveredRegsoLayerRef.current === layer) return;
            const style = getOverlayStyleRef.current(layer.feature);
            layer.setStyle(style);
            syncGreyCover(layer, style);
        });
    }, [syncGreyCover]);
    // The overlay effect above may have begun a reveal-colour scope; restyle the revealing tiles so they
    // take the full colour at once (whatever the Färger toggle is).
    useEffect(() => {
        restyleRegsoTiles((code) => isRevealColourForced(code));
    }, [searchRegsoSet, excludedSearchRegsoSet, showFavorites, restyleRegsoTiles]);
    // The reveal finished: settle the tiles to the Färger toggle. Fyllda keeps the colour; Linjer/Ingen fade
    // the colour away now that the listings have grown in (the drawer's own area-fade path). The codes are in
    // reveal order, so the fade-out is staggered to match: the tile that lit up first fades its colour first.
    useEffect(() => onRevealColourEnd((codes) => {
        if (!checkedStateRef.current?.hideMap || !regsoGeoJsonRef.current) return;
        const scope = new Set(codes);
        noteFiltersApplied({ hold: false });
        fadeAreaLayers(regsoGeoJsonRef.current, codes);
        restyleRegsoTiles((code) => scope.has(code));
    }), [restyleRegsoTiles]);

    // The landing shows the live map full-bleed behind the search; it resizes entering/leaving the landing
    // (invalidateSize). On the landing it fits Stockholm and sets the latitude bands that fade the overlay
    // (colour → lines → none down the view); then the tiles are restyled to match.
    useEffect(() => {
        if (!mapInstance) return undefined;
        const apply = () => {
            mapInstance.invalidateSize();
            if (showLanding) {
                try {
                    mapInstance.fitBounds([[59.17, 17.78], [59.50, 18.32]], { padding: [8, 8], animate: false });
                } catch (error) { /* bounds not ready yet */ }
                const viewBounds = mapInstance.getBounds();
                const north = viewBounds.getNorth();
                const south = viewBounds.getSouth();
                landingBandsRef.current = {
                    l1: north - (0.50 * (north - south)),
                    l2: north - (0.85 * (north - south)),
                };
            } else {
                landingBandsRef.current = null;
            }
            restyleRegsoTiles();
        };
        const t1 = setTimeout(apply, 140);
        // Again after the map's own initial view settles, so the backdrop fit and bands win.
        const t2 = setTimeout(apply, 480);
        // Also re-sync whenever the map container actually settles to a new size — e.g. the landing → results
        // shrink to the 29vh map on a slow phone, where the fixed timers above can fire before the layout is
        // done. A stale Leaflet size otherwise leaves the overlay, markers and labels misaligned until the
        // next pan. invalidateSize reads the container size but doesn't change it, so there's no resize loop.
        let debounce;
        const schedule = () => { clearTimeout(debounce); debounce = setTimeout(apply, 120); };
        let observer = null;
        try {
            const container = mapInstance.getContainer();
            if (container && typeof ResizeObserver !== 'undefined') {
                observer = new ResizeObserver(schedule);
                observer.observe(container);
            }
        } catch (error) { /* no ResizeObserver: the timers above still cover the common case */ }
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(debounce);
            if (observer) observer.disconnect();
        };
    }, [showLanding, mapInstance, restyleRegsoTiles]);

    // Re-apply the landing overlay when its styling changes (e.g. the area colours finish loading).
    useEffect(() => {
        if (showLanding) restyleRegsoTiles();
    }, [getOverlayStyle, showLanding, restyleRegsoTiles]);

    const areaCardStateRef = useRef({ colorCache, areaStats, languageState });
    areaCardStateRef.current = { colorCache, areaStats, languageState };
    // Phones (≤ 700px, Direction A): the map, the filters and Restid each open full-height over the feed
    // from the chip row under the search. Null means none is open (the feed fills the screen).
    const [mobileSheet, setMobileSheet] = useState(null); // null | 'filter' | 'restid'
    const [renderedSheet, setRenderedSheet] = useState(null); // content kept through the close slide
    const sheetRef = useRef(null);
    const [isPhoneLayout, setIsPhoneLayout] = useState(isMobileViewportOnLoad);
    // The compared areas (compareAreas.js), shared with kartvy: their numbers on the map, the comparison card in
    // the panel under the map (desktop and tablets) or a sheet from the chip row (phones), and its page over the
    // view ("Jämför i storvy", ComparePreview.js).
    const comparedAreas = useComparedAreas();
    // Its homes shown from its page: the phone's chip for it stands aside (compareAreas.js).
    const comparedHomesShown = useComparedHomesShown();
    const [comparePreview, setComparePreview] = useState(null);
    const openComparePreview = useCallback((codes) => {
        track('compare.preview', { count: codes.length, view: 'listvy' });
        setComparePreview(codes);
    }, []);
    // The page's "Visa alla N bostäder" here: the compared areas become the search, each its own pick.
    const showComparedHomesHere = useCallback((areas) => {
        setComparePreview(null);
        setSelectedSuggestions(areas.filter((area) => area?.code).map((area) => ({
            id: `omrade-map:${area.code}`,
            type: 'omrade-map',
            label: area.name || area.code,
            kommun: '',
            ort: '',
            lan: '',
            regsoId: area.code,
            postort: '',
        })));
    }, []);
    const compareMarkersRef = useRef(null);
    useEffect(() => {
        if (!mapInstance) return undefined;
        const markers = createCompareMarkers(mapInstance, (code) => {
            let found = null;
            regsoGeoJsonRef.current?.eachLayer((featureLayer) => {
                if (!found && String(featureLayer.feature?.properties?.regsokod || '').trim() === code) found = featureLayer;
            });
            return found;
        }, { view: 'listvy', getSwedish: () => compareSwedishRef.current });
        compareMarkersRef.current = markers;
        return () => {
            markers.destroy();
            if (compareMarkersRef.current === markers) compareMarkersRef.current = null;
        };
    }, [mapInstance]);
    const compareSwedishRef = useRef(languageState.swedish);
    useEffect(() => {
        if (compareSwedishRef.current === languageState.swedish) return;
        compareSwedishRef.current = languageState.swedish;
        compareMarkersRef.current?.refresh();
    }, [languageState.swedish]);

    const onEachRegsoFeature = useCallback((featureEntry, layer) => {
        layer.setStyle(getOverlayStyle(featureEntry));
        syncGreyCover(layer, getOverlayStyle(featureEntry));
        layer.on('remove', () => {
            greyCoverRef.current?.hide(layer);
            filteredCoverRef.current?.hide(layer);
            filteredBadgesRef.current?.set(layer, false);
        });
        const regsoCode = String(featureEntry?.properties?.regsokod || '').trim();
        const regsoName = String(
            featureEntry?.properties?.regso
            || featureEntry?.properties?.name
            || regsoNameById.get(regsoCode)
            || regsoCode,
        ).trim();

        // Handlers read the current style function through a ref: layers outlive this closure.
        // The tile settles back to its base look a short delay after the pointer leaves it (the hover tail),
        // not at once; a longer delay when moving straight on to another tile, a shorter one when leaving the
        // map. Stored on the layer so the next tile's mouseover can extend this one's tail.
        let hoverResetTimer = null;
        const scheduleHoverReset = (delayMs) => {
            if (hoverResetTimer) clearTimeout(hoverResetTimer);
            hoverResetTimer = setTimeout(() => {
                hoverResetTimer = null;
                if (hoveredRegsoLayerRef.current === layer || !layer._map) return;
                const style = getOverlayStyleRef.current(featureEntry);
                layer.setStyle(style);
                syncGreyCover(layer, style);
                areaMapRevealRef.current.hide(layer);
            }, delayMs);
        };
        layer.__lvHoverReset = scheduleHoverReset;
        layer.on('mouseover', () => {
            // Touch tap: phones/touch have no hover area look. A tap fires a synthesized mouseover that would
            // flash the area's coloured map before the tile's own select/deselect animation, so skip it.
            if (lastPointerTypeRef.current === 'touch') return;
            // "Av" mode: no area overlay at all, so hovering an area does nothing.
            if (checkedStateRef.current?.hideOverlay) return;
            if (hoverResetTimer) { clearTimeout(hoverResetTimer); hoverResetTimer = null; }
            // Let the tile just left trail a little longer (the tail), instead of settling at the shorter exit
            // delay its own mouseout scheduled.
            const previousHoverLayer = hoveredRegsoLayerRef.current;
            if (previousHoverLayer && previousHoverLayer !== layer && typeof previousHoverLayer.__lvHoverReset === 'function') {
                previousHoverLayer.__lvHoverReset(REGSO_HOVER_TAIL_MS);
            }
            setMapCursor('pointer');
            infoPanelRef.current?.setHoveredArea(regsoCode, regsoName);
            // The comparison card's "Pekar på" column.
            setCompareHover(regsoCode, regsoName);
            hoveredRegsoTileRef.current = regsoCode;
            hoveredRegsoLayerRef.current = layer;
            const style = getOverlayStyleRef.current(featureEntry);
            layer.setStyle(style);
            syncGreyCover(layer, style);
            // Tiles outside the search stay grey, without the regular map.
            if (style.fillColor !== INACTIVE_REGSO_FILL_COLOR) {
                areaMapRevealRef.current.reveal(layer);
                // Cut this area out of the dimming so its coloured map shows through, like an active tile.
                areaSelectionOverlayRef.current?.setHoverHole(regsoCode);
            }
        });
        layer.on('mouseout', () => {
            // Touch never applied the hover look (see mouseover), so nothing to undo on a tap's mouseout.
            if (lastPointerTypeRef.current === 'touch') return;
            setMapCursor('pointer');
            infoPanelRef.current?.clearHoveredArea(regsoCode);
            if (hoveredRegsoTileRef.current === regsoCode) hoveredRegsoTileRef.current = '';
            if (hoveredRegsoLayerRef.current === layer) hoveredRegsoLayerRef.current = null;
            areaSelectionOverlayRef.current?.setHoverHole(null);
            // Keep the tile's hover look briefly (the tail), then settle it.
            scheduleHoverReset(REGSO_HOVER_EXIT_MS);
        });
        // Click selects the area; a double click adds it to the comparison or takes it out (as in kartvy). The
        // select waits briefly so a double click doesn't also select and deselect the area.
        let pendingClickTimer = null;
        const toggleCompare = () => {
            if (toggleComparedArea(regsoCode, regsoName, { view: 'listvy' }) === 'full' && layer._map) {
                showMapToast(layer._map, areaCardStateRef.current.languageState?.swedish === false
                    ? `You can compare up to ${MAX_COMPARED} areas. Remove one from the card first.`
                    : `Du kan jämföra upp till ${MAX_COMPARED} områden. Ta bort ett i kortet först.`);
            }
        };
        layer.on('click', (event) => {
            // "Av" mode: the overlay is hidden, so area clicks do nothing — no select/deselect and no
            // comparison. Only the search bar changes the selection then.
            if (checkedStateRef.current?.hideOverlay) return;
            if (pendingClickTimer) {
                clearTimeout(pendingClickTimer);
                pendingClickTimer = null;
                toggleCompare();
                return;
            }
            pendingClickTimer = setTimeout(() => {
                pendingClickTimer = null;
                // The area fades to its new look: turned off, after its homes have shrunk away; turned on,
                // before they grow in.
                noteAreaToggle(regsoCode, { hold: listingsShownInArea(regsoCode) });
                fadeAreaLayer(layer, regsoCode);
                regsoTileToggleHandlerRef.current?.(featureEntry);
            }, REGSO_DOUBLE_CLICK_MS);
        });
        layer.on('remove', () => {
            if (pendingClickTimer) clearTimeout(pendingClickTimer);
            pendingClickTimer = null;
        });
    }, [getOverlayStyle, listingsShownInArea, regsoNameById, setMapCursor, syncGreyCover]);

    const backendTotalListingsForSearch = useMemo(
        () => searchSuggestions.reduce((sum, suggestion) => {
            const suggestionId = String(suggestion?.id || '').trim();
            if (!suggestionId) return sum;
            const explicitTotal = Number(selectionTotalsById[suggestionId]);
            if (Number.isFinite(explicitTotal) && explicitTotal >= 0) {
                return sum + Math.floor(explicitTotal);
            }
            const fallbackCount = Array.isArray(selectionResultsById[suggestionId])
                ? selectionResultsById[suggestionId].length
                : 0;
            return sum + fallbackCount;
        }, 0),
        [searchSuggestions, selectionResultsById, selectionTotalsById],
    );
    const mapFilteredListingsTotal = shouldFetchListings ? filteredMapListings.length : 0;
    const totalListingsForSearch = shouldFetchListings
        ? Math.max(backendTotalListingsForSearch, mapFilteredListingsTotal)
        : 0;
    const totalListingsPages = totalListingsForSearch > 0
        ? Math.ceil(totalListingsForSearch / LISTINGS_PAGE_SIZE)
        : 0;
    const activeListingsPage = totalListingsPages > 0
        ? Math.min(Math.max(currentListingsPage, 1), totalListingsPages)
        : 1;

    // The page before a reload: applied once the results are in (loading them resets the page to 1 first).
    useEffect(() => {
        if (restored.page <= 1 || totalListingsPages < restored.page) return;
        setCurrentListingsPage(restored.page);
        restored.page = 1;
    }, [restored, totalListingsPages]);
    useEffect(() => {
        saveViewState('listvy', { tab: listingFeedTab, favorites: showFavorites, page: currentListingsPage });
    }, [currentListingsPage, listingFeedTab, showFavorites]);
    // Save the picks (shared with kartvy) once they change.
    useEffect(() => {
        const signature = selectedSuggestions.map((entry) => `${entry.id}:${(entry.excludedRegsoIds || []).join(',')}`).join('|');
        if (signature === restored.signature) return;
        restored.signature = null;
        saveListvySelection(selectedSuggestions, regsoCoverageBySuggestionId);
    }, [regsoCoverageBySuggestionId, restored, selectedSuggestions]);

    useEffect(() => {
        if (!totalListingsPages) return;
        if (currentListingsPage > totalListingsPages) {
            setCurrentListingsPage(totalListingsPages);
        }
    }, [currentListingsPage, totalListingsPages]);
    // A page change starts at the top of the new page: on desktop the list scrolls itself; in the one-column
    // layouts (phone, tablet) the page jumps up to the tabs above the list (a glide across thousands of pixels
    // would be slow, and the list changing under it would stop it).
    const goToListingsPage = useCallback((page) => {
        setCurrentListingsPage(Math.min(Math.max(1, page), Math.max(1, totalListingsPages)));
        const column = feedColumnRef.current;
        if (!column) return;
        const list = column.querySelector('.list-view-feed-list');
        if (list && list.scrollHeight > list.clientHeight && window.getComputedStyle(list).overflowY !== 'visible') {
            list.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        const scroller = column.closest('.list-view-page');
        const tabs = column.querySelector('.list-view-feed-tabbar') || column;
        if (!scroller) return;
        const offset = tabs.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 8;
        if (offset < 0) scroller.scrollBy({ top: offset });
    }, [totalListingsPages]);

    // On ultra-wide monitors the page is capped at 1920px and centred, leaving dead space on each side.
    // A wheel over that margin would otherwise do nothing; forward it to the listings column so the
    // middle scrolls wherever the pointer is.
    useEffect(() => {
        const onWheel = (event) => {
            const page = pageRootRef.current;
            const feed = feedColumnRef.current;
            if (!page || !feed) return;
            const rect = page.getBoundingClientRect();
            if (event.clientX >= rect.left && event.clientX <= rect.right) return;
            if (feed.scrollHeight <= feed.clientHeight) return;
            let delta = event.deltaY;
            if (event.deltaMode === 1) delta *= 16; // lines → pixels
            else if (event.deltaMode === 2) delta *= feed.clientHeight; // pages → pixels
            feed.scrollTop += delta;
            event.preventDefault();
        };
        window.addEventListener('wheel', onWheel, { passive: false });
        return () => window.removeEventListener('wheel', onWheel);
    }, []);

    const paginationItems = useMemo(() => {
        if (totalListingsPages <= 1) return [];
        if (totalListingsPages <= 8) {
            return Array.from({ length: totalListingsPages }, (_, index) => index + 1);
        }
        const itemSet = new Set([1, totalListingsPages]);
        const pageStart = Math.max(2, activeListingsPage - 1);
        const pageEnd = Math.min(totalListingsPages - 1, activeListingsPage + 1);
        for (let page = pageStart; page <= pageEnd; page += 1) {
            itemSet.add(page);
        }
        const pages = Array.from(itemSet).sort((a, b) => a - b);
        const finalItems = [];
        for (let index = 0; index < pages.length; index += 1) {
            const page = pages[index];
            const previousPage = pages[index - 1];
            if (index > 0 && page - previousPage > 1) {
                finalItems.push('ellipsis');
            }
            finalItems.push(page);
        }
        return finalItems;
    }, [activeListingsPage, totalListingsPages]);

    const pageStartDisplay = totalListingsForSearch > 0
        ? ((activeListingsPage - 1) * LISTINGS_PAGE_SIZE) + 1
        : 0;
    const pageEndDisplay = totalListingsForSearch > 0
        ? Math.min(activeListingsPage * LISTINGS_PAGE_SIZE, totalListingsForSearch)
        : 0;
    const locale = languageState.swedish ? 'sv-SE' : 'en-US';
    const filteredAreaWarningLabel = languageState.swedish ? 'Utanför områdesfilter' : 'Outside area filters';
    const filteredAreaWarningTooltip = languageState.swedish
        ? 'Det här området matchar inte de aktiva områdesfiltren.'
        : 'This area does not match the applied area filters.';
    const favoritesSubtitle = favorites.length
        ? (languageState.swedish
            ? `${favorites.length} ${favorites.length === 1 ? 'sparad bostad' : 'sparade bostäder'}`
            : `${favorites.length} saved ${favorites.length === 1 ? 'home' : 'homes'}`)
        : (languageState.swedish ? 'Inga favoriter ännu.' : 'No favourites yet.');
    const feedSubtitle = showFavorites ? favoritesSubtitle : !shouldFetchListings
        ? ''
        : (searchLoading || isPageLoading)
            ? (languageState.swedish ? 'Laddar sida…' : 'Loading page…')
            : (totalListingsForSearch > 0
                ? (languageState.swedish
                    ? `${pageStartDisplay.toLocaleString(locale)}-${pageEndDisplay.toLocaleString(locale)} av ${totalListingsForSearch.toLocaleString(locale)}`
                    : `${pageStartDisplay.toLocaleString(locale)}-${pageEndDisplay.toLocaleString(locale)} of ${totalListingsForSearch.toLocaleString(locale)}`)
                : (languageState.swedish ? 'Inga bostäder hittades.' : 'No listings found.'));
    const hasListingsPagination = !showFavorites && totalListingsPages > 1;
    const keepFeedSurfaceExpanded = !showFavorites && shouldFetchListings && (searchLoading || isPageLoading);
    const fallbackValue = '—';
    const formatKrPerSqmValue = (value) => {
        if (!Number.isFinite(value)) return fallbackValue;
        return Number(value)
            .toLocaleString('sv-SE', { maximumFractionDigits: 0 })
            .replace(/\s+/g, ' ');
    };
    const fullViewRegsoCode = fullViewListing ? extractListingRegsoCode(fullViewListing) : '';
    const fullViewAreaScore = fullViewListing
        ? normalizeListingScore(fullViewListing.betyg ?? (fullViewRegsoCode ? areaStats[`combined_${fullViewRegsoCode}`] : null))
        : null;
    const fullViewAreaMetrics = buildListingAreaInfo({
        regsoCode: fullViewRegsoCode, areaStats, colorCache, topology: overlayTopology,
    });
    const outsideMaskRenderer = useMemo(
        () => L.svg({ pane: OUTSIDE_SWEDEN_MASK_PANE }),
        [],
    );
    const regsoLayerKey = useMemo(
        () => `regso-layer-${checkedState.hideMap ? 1 : 0}-${checkedState.hideLines ? 1 : 0}`,
        [checkedState.hideMap, checkedState.hideLines],
    );
    useEffect(() => {
        // A rebuilt area layer starts on the full outlines.
        if (mapInstance) areaGeometrySwitchRef.current.apply(regsoGeoJsonRef.current, mapInstance.getZoom());
    }, [mapInstance, mapPanesReady, regsoLayerKey, topojsonFeature]);
    // The compared areas' numbers, again after the area layer is rebuilt.
    useEffect(() => {
        compareMarkersRef.current?.update(comparedAreas);
    }, [comparedAreas, mapInstance, mapPanesReady, topojsonFeature, regsoLayerKey]);
    // "Nära mig": the browser's location, reverse-geocoded (Nominatim) to its kommun, which is then searched.
    const [nearMeState, setNearMeState] = useState('idle');
    const nearMeResetRef = useRef(null);
    useEffect(() => () => window.clearTimeout(nearMeResetRef.current), []);
    const handleNearMe = useCallback(() => {
        const fail = (reason) => {
            track('search.near_me', { result: reason });
            setNearMeState('error');
            window.clearTimeout(nearMeResetRef.current);
            nearMeResetRef.current = window.setTimeout(() => setNearMeState('idle'), 3500);
        };
        if (!navigator.geolocation) {
            fail('unsupported');
            return;
        }
        setNearMeState('locating');
        navigator.geolocation.getCurrentPosition(async ({ coords }) => {
            try {
                const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&zoom=10&accept-language=sv&lat=${coords.latitude}&lon=${coords.longitude}`;
                const response = await fetch(url);
                if (!response.ok) throw new Error(String(response.status));
                const address = (await response.json())?.address || {};
                const candidates = [address.municipality, address.city, address.town, address.county]
                    .filter(Boolean)
                    .map((name) => normalizeSearchText(String(name).replace(/\s+kommun$/i, '')));
                const kommunName = kommunSuggestionsIndex.find((kommun) => candidates.includes(normalizeSearchText(kommun)));
                if (!kommunName) {
                    fail('outside');
                    return;
                }
                track('search.near_me', { result: 'found', kommun: kommunName });
                setNearMeState('idle');
                applySuggestion({
                    id: `kommun:${kommunName}`,
                    type: 'kommun',
                    label: `${kommunName} kommun`,
                    kommun: kommunName,
                    ort: '',
                    lan: '',
                    regsoId: '',
                });
            } catch (error) {
                fail('error');
            }
        }, (error) => fail(error?.code === 1 ? 'denied' : 'error'), { timeout: 10000, maximumAge: 600000 });
    }, [applySuggestion, kommunSuggestionsIndex]);
    const nearMeLabel = nearMeState === 'locating'
        ? (languageState.swedish ? 'Letar…' : 'Locating…')
        : nearMeState === 'error'
            ? (languageState.swedish ? 'Hittade inte din plats' : "Couldn't find you")
            : (languageState.swedish ? 'Nära mig' : 'Near me');

    // The heading and "Nära mig" only show before the first search, so the band stays compact afterwards.
    const renderSearchBandTitle = () => (searchSuggestions.length === 0 ? (
        <h2 className="list-view-search-band-title">
            {languageState.swedish ? 'Hitta bostäder i ett område du gillar' : 'Find homes in an area you like'}
        </h2>
    ) : null);
    const renderNearMe = () => (searchSuggestions.length === 0 ? (
        <button
            type="button"
            className={`list-view-near-me${nearMeState === 'error' ? ' is-error' : ''}`}
            onClick={handleNearMe}
            disabled={nearMeState === 'locating'}
        >
            <PlaceOutlinedIcon aria-hidden="true" />
            {nearMeLabel}
        </button>
    ) : null);

    // The search bar, and under it the picked searches with the way to kartvy at their right (the owner,
    // 2026-11-01: below the search bar on the right, as on the landing, so kartvy is one click away; the landing has
    // its own in its row of buttons, and phones the Karta chip).
    const renderSearchControls = (formRef, { landing = false } = {}) => (
        <>
            <form className="list-view-search-form" onSubmit={handleSearchSubmit} ref={formRef}>
                <SearchRoundedIcon className="list-view-search-icon" aria-hidden="true" />
                <input
                    type="text"
                    value={searchDraft}
                    onChange={handleSearchDraftChange}
                    onFocus={() => setShowSuggestions(true)}
                    aria-label={languageState.swedish ? 'Sök ort, kommun, område eller gata' : 'Search city, municipality, area or street'}
                    placeholder={languageState.swedish ? 'Sök ort, kommun, område eller gata' : 'Search city, municipality, area or street'}
                    autoComplete="off"
                />
                {searchDraft && (
                    <button type="button" className="list-view-search-clear" onClick={clearSearch} aria-label={languageState.swedish ? 'Rensa sökning' : 'Clear search'}>
                        ×
                    </button>
                )}
                <button
                    type="submit"
                    className="list-view-search-submit"
                    disabled={searchLoading}
                    aria-label={searchLoading
                        ? (languageState.swedish ? 'Söker…' : 'Searching…')
                        : (languageState.swedish ? 'Sök' : 'Search')}
                >
                    <SearchRoundedIcon aria-hidden="true" />
                </button>
                {showSuggestions && searchTokens.length > 0 && (flatSuggestions.length > 0 || streetLookupPending) && (
                    <div className="list-view-search-suggestions">
                        {flatSuggestions.map((suggestion) => {
                            const parts = suggestionParts(suggestion, languageState.swedish, suggestionKommun(suggestion));
                            return (
                                <button
                                    key={suggestion.id}
                                    type="button"
                                    className={`list-view-search-suggestion-item${activeSuggestion?.id === suggestion.id ? ' is-selected' : ''}`}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => applySuggestion(suggestion)}
                                >
                                    <span className="kartvy-search-label">
                                        {parts.name}
                                        {parts.place && <span className="kartvy-search-place"> · {parts.place}</span>}
                                    </span>
                                    <SearchScore value={suggestionScore(suggestion)} swedish={languageState.swedish} />
                                    <span className="kartvy-search-tag">
                                        {parts.tag}
                                        {parts.count && <span className="kartvy-search-tag-count">{parts.count}</span>}
                                    </span>
                                </button>
                            );
                        })}
                        {streetLookupPending && groupedSuggestions.address.length === 0 && (
                            <div className="list-view-search-suggestion-pending">
                                {languageState.swedish ? 'Söker gator…' : 'Looking up streets…'}
                            </div>
                        )}
                    </div>
                )}
            </form>
            {landing ? null : (
                <div className="list-view-search-under">
                    <ListViewSearchChips
                        suggestions={searchSuggestions}
                        swedish={languageState.swedish}
                        onRemove={handleRemoveSelectedSuggestion}
                        onClearAll={handleClearAllSuggestions}
                    />
                    <button
                        type="button"
                        className="list-view-kartvy-btn"
                        onClick={handleBackToMapView}
                        aria-label={languageState.swedish ? 'Gå till kartvy' : 'Go to map view'}
                    >
                        <MapOutlinedIcon aria-hidden="true" />
                        <span>Kartvy</span>
                    </button>
                </div>
            )}
        </>
    );
    const finishMapColumnSlide = () => {
        clearTimeout(mapColumnSlideTimerRef.current);
        setMapColumnSliding(false);
        mapInstance?.invalidateSize();
    };

    const toggleMapColumn = () => {
        if (!mapColumnCollapsed && mapColumnRef.current) {
            setMapColumnWidth(mapColumnRef.current.getBoundingClientRect().width);
        }
        setMapColumnSliding(true);
        track('column.toggle', { collapsed: !mapColumnCollapsed });
        setMapColumnCollapsed((collapsed) => !collapsed);
        // Fallback in case transitionend doesn't fire (e.g. the browser can't animate the grid).
        clearTimeout(mapColumnSlideTimerRef.current);
        mapColumnSlideTimerRef.current = setTimeout(finishMapColumnSlide, MAP_COLUMN_SLIDE_MS + 120);
    };

    // A card's "Visa på kartan" (as on kartvy's rail cards): the side map flies to the home at the closest zoom and
    // pulses it, and the card reads "Visas på kartan". A folded map column opens first; on tablets, where the map is
    // above the list, the page scrolls up to it.
    const [locatedListingId, setLocatedListingId] = useState(null);
    // Forgotten once its card is no longer on the page (another page, search or filter).
    useEffect(() => {
        if (locatedListingId && !visibleLoadedListings.some((listing) => String(listing.id) === locatedListingId)) {
            setLocatedListingId(null);
        }
    }, [locatedListingId, visibleLoadedListings]);
    const locateListingOnMap = (listing) => {
        const lat = Number(listing?.lat);
        const lng = Number(listing?.lng);
        if (!mapInstance || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
        track('map.list_locate', { action: 'show', view: 'listvy', ...listingProps(listing) });
        setLocatedListingId(String(listing.id));
        const fly = () => {
            mapInstance.stop();
            mapInstance.once('moveend', () => pulseListingAt(mapInstance, [lat, lng], listing.id));
            mapInstance.flyTo([lat, lng], mapInstance.getMaxZoom?.() ?? 17, { duration: 0.9 });
        };
        if (mapColumnCollapsed) {
            toggleMapColumn();
            setTimeout(fly, MAP_COLUMN_SLIDE_MS + 160);
            return;
        }
        if (window.matchMedia?.('(max-width: 1200px)').matches) {
            mapColumnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        fly();
    };

    // The intro tips (IntroTips.js) once the first search has shown its homes (the welcome first, if this is the
    // first visit to the site), held back while a shared tip is showing; phones have no map beside the list, so its
    // tip is left out there.
    const [introOpen, setIntroOpen] = useState(false);
    const introTips = useMemo(
        () => [WELCOME_TIP, ...(isPhoneLayout ? LISTVY_TIPS.filter((tip) => tip.phone !== false) : LISTVY_TIPS)],
        [isPhoneLayout],
    );
    const hasVisibleListings = visibleLoadedListings.length > 0;
    const sharedIntroOpen = useSharedIntroOpen();
    useEffect(() => {
        if (introOpen || sharedIntroOpen || showLanding || !hasVisibleListings || introSeen('listvy')) return undefined;
        const timer = window.setTimeout(() => setIntroOpen(true), 1200);
        return () => window.clearTimeout(timer);
    }, [introOpen, sharedIntroOpen, showLanding, hasVisibleListings]);

    // The shared "Lista eller karta" tip once a search is made (a place added); not for the searches a reload restores.
    const searchCountRef = useRef(selectedSuggestions.length);
    useEffect(() => {
        if (selectedSuggestions.length > searchCountRef.current) requestIntroTip('switch', 1600);
        searchCountRef.current = selectedSuggestions.length;
    }, [selectedSuggestions.length]);

    const handlePageTransitionEnd = (event) => {
        if (event.target !== event.currentTarget || event.propertyName !== 'grid-template-columns') return;
        finishMapColumnSlide();
    };

    const mapColumnClassName = [
        'list-view-map-column',
        mapColumnCollapsed ? 'is-collapsed' : '',
        mapColumnSliding ? 'is-sliding' : '',
    ].filter(Boolean).join(' ');

    // Phone chip row → the Filter bottom sheet (Karta goes to kartvy instead). The sheet host
    // stays mounted; `mobileSheet` toggles it open, `renderedSheet` keeps its content through the close
    // slide so it doesn't empty mid-animation. Dragging the handle down past a threshold closes it.
    const openMobileSheet = (sheet) => {
        if (sheetRef.current) { sheetRef.current.style.transition = ''; sheetRef.current.style.transform = ''; }
        setMobileSheet(sheet);
        track('listvy.sheet', { sheet });
    };
    const closeMobileSheet = () => setMobileSheet(null);
    useEffect(() => {
        if (!comparedAreas.length || comparedHomesShown) setMobileSheet((current) => (current === 'compare' ? null : current));
    }, [comparedAreas.length, comparedHomesShown]);
    useEffect(() => {
        if (mobileSheet) { setRenderedSheet(mobileSheet); return undefined; }
        const timer = window.setTimeout(() => setRenderedSheet(null), 360);
        return () => window.clearTimeout(timer);
    }, [mobileSheet]);
    useEffect(() => {
        if (!mobileSheet) return undefined;
        const onKey = (event) => { if (event.key === 'Escape') setMobileSheet(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [mobileSheet]);
    // Drag the grab handle (or header) to dismiss: follow the finger down, close past ~110px.
    const onSheetDragStart = (event) => {
        const sheet = sheetRef.current;
        if (!sheet) return;
        const startY = event.clientY;
        let dy = 0;
        sheet.style.transition = 'none';
        try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch (e) { /* ignore */ }
        const onMove = (moveEvent) => {
            dy = Math.max(0, moveEvent.clientY - startY);
            sheet.style.transform = `translateY(${dy}px)`;
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            sheet.style.transition = '';
            if (dy > 110) {
                sheet.style.transform = 'translateY(100%)';
                setMobileSheet(null);
            } else {
                sheet.style.transform = '';
            }
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        const mq = window.matchMedia(`(max-width: ${MOBILE_LAYOUT_MAX_WIDTH}px)`);
        const onChange = () => setIsPhoneLayout(mq.matches);
        onChange();
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    // Leaving the phone layout (rotate/resize) with a sheet open: drop back to the normal view.
    useEffect(() => {
        if (!isPhoneLayout && mobileSheet) setMobileSheet(null);
    }, [isPhoneLayout, mobileSheet]);

    return (
        <div
            ref={pageRootRef}
            className={`list-view-page${mapColumnCollapsed ? ' is-map-column-collapsed' : ''}${showLanding ? ' is-landing' : ''}${revealingAfterLanding ? ' is-revealing' : ''}${mobileSheet ? ' is-sheet-open' : ''}`}
            onTransitionEnd={handlePageTransitionEnd}
        >
            {showLanding && (
                <section className="list-view-landing" aria-label={languageState.swedish ? 'Sök bostäder' : 'Search homes'}>
                    {/* The whole landing scene — the map, its dimming, the house markers and the area cards —
                        is one baked static image, so it appears instantly (no projection or data wait). The
                        live map keeps loading behind it for the fly-out when a search starts. */}
                    <img
                        className="list-view-landing-poster"
                        src={`${process.env.PUBLIC_URL}/landing-map-${darkMode ? 'dark' : 'light'}.webp`}
                        alt=""
                        aria-hidden="true"
                    />
                    <div className={`list-view-landing-inner${landingPanel === 'filter' ? ' is-panel-filter' : ''}`}>
                        <div className="list-view-landing-card list-view-search-band">
                            <h1 className="list-view-landing-title">
                                {languageState.swedish ? 'Var vill du bo?' : 'Where would you like to live?'}
                            </h1>
                            <p className="list-view-landing-sub">
                                {languageState.swedish
                                    ? 'Sök på ort, kommun, område eller gata – så tonas kartan fram med områdets färger.'
                                    : 'Search a place, municipality, area or street — the map then appears in the area colours.'}
                            </p>
                            {renderSearchControls(landingSearchFormRef, { landing: true })}
                            <div className="list-view-landing-actions">
                                <div className="lvl-actions-side">
                                    {renderNearMe()}
                                </div>
                                <div className="lvl-actions-filters">
                                    <button
                                        type="button"
                                        className={`lvl-action${landingPanel === 'filter' ? ' is-open' : ''}`}
                                        aria-expanded={landingPanel === 'filter'}
                                        onClick={() => setLandingPanel((panel) => (panel === 'filter' ? null : 'filter'))}
                                    >
                                        <TuneRoundedIcon aria-hidden="true" />
                                        {languageState.swedish ? 'Filter' : 'Filters'}
                                        <ExpandMoreRoundedIcon className="lvl-action-chevron" aria-hidden="true" />
                                    </button>
                                </div>
                                <button type="button" className="lvl-action lvl-action--kartvy" onClick={handleBackToMapView}>
                                    <MapOutlinedIcon aria-hidden="true" />
                                    Kartvy
                                </button>
                            </div>
                            {landingPanel === 'filter' && (
                                <div className="lvl-dropdown lvl-dropdown--filter">
                                    <FilterList
                                        languageState={languageState}
                                        checkedState={checkedState}
                                        handleChange={handleMapToggleChange}
                                        setDrawerTab={() => {}}
                                        setOpen={() => setLandingPanel(null)}
                                        open
                                        setCustomFilterActive={setCustomFilterActive}
                                        setCustomFilter={setCustomFilter}
                                        setKey={setKey}
                                        setListingFilters={setListingFilters}
                                        onBeforeApply={handleBeforeFilterApply}
                                        getPriceHistogram={getPriceHistogram}
                                        priceHistogramScope={priceHistogramScope}
                                        showMapSwitches={false}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <svg className="list-view-landing-route" viewBox="0 0 460 150" aria-hidden="true">
                            <path d="M62 50 C 180 18, 220 136, 380 108" fill="none" stroke="#ffffff" strokeWidth="5.25" strokeDasharray="2 11" strokeLinecap="round" />
                            <path d="M62 50 C 180 18, 220 136, 380 108" fill="none" stroke="#1f7a62" strokeWidth="3.5" strokeDasharray="2 11" strokeLinecap="round" />
                            <g transform="translate(40,28)">
                                <circle cx="20" cy="20" r="22" fill="#ffffff" stroke="#1f7a62" strokeWidth="2.6" />
                                <rect x="11" y="17" width="18" height="12" rx="2" fill="none" stroke="#1f7a62" strokeWidth="2.4" />
                                <path d="M16 17v-2a4 4 0 0 1 8 0v2" fill="none" stroke="#1f7a62" strokeWidth="2.4" />
                            </g>
                            <g transform="translate(360,86)">
                                <circle cx="20" cy="20" r="22" fill="#4a96d0" stroke="#ffffff" strokeWidth="2.6" />
                                <path d="M11 21l9-7 9 7" fill="none" stroke="#ffffff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M13 20v7h14v-7" fill="none" stroke="#ffffff" strokeWidth="2.6" />
                            </g>
                            {/* 30% larger than its 70×26 base, scaled about its centre so it stays put on the route. */}
                            <g transform="translate(181.5,58.1) scale(1.3)">
                                <rect x="0" y="0" width="70" height="26" rx="13" fill="#1f7a62" />
                                <circle cx="16" cy="13" r="7" fill="none" stroke="#ffffff" strokeWidth="2.2" />
                                <path d="M16 9v4l2.6 1.6" fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" />
                                <text x="30" y="17.5" fill="#ffffff" fontSize="12.5" fontWeight="700" fontFamily="Roboto, sans-serif">30 min</text>
                            </g>
                    </svg>
                </section>
            )}
            <section
                ref={mapColumnRef}
                className={mapColumnClassName}
                style={mapColumnWidth ? { '--map-column-width': `${mapColumnWidth}px` } : undefined}
            >
                <button
                    type="button"
                    className="list-view-map-column-tab"
                    onClick={toggleMapColumn}
                    aria-expanded={!mapColumnCollapsed}
                    aria-label={mapColumnCollapsed
                        ? (languageState.swedish ? 'Visa karta och områdesinfo' : 'Show map and area info')
                        : (languageState.swedish ? 'Dölj karta och områdesinfo' : 'Hide map and area info')}
                >
                    {/* Folded, the tab says what's there (a map icon and "Karta"), and pointing at it shows a hint
                        (design A of the "Prickar, flikar och kartflik" canvas, with a hint for the map preview). */}
                    <MapOutlinedIcon className="list-view-map-column-tab-icon" aria-hidden="true" />
                    <span className="list-view-map-column-tab-label" aria-hidden="true">
                        {languageState.swedish ? 'Karta' : 'Map'}
                    </span>
                    <svg className="list-view-map-column-tab-chevron" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M15.4 7.4 14 6l-6 6 6 6 1.4-1.4-4.6-4.6z" />
                    </svg>
                    <span className="list-view-map-column-tab-hint" aria-hidden="true">
                        {languageState.swedish ? 'Visa kartan och områdena' : 'Show the map and the areas'}
                    </span>
                </button>
                <div className="list-view-map-column-clip">
                <div className="list-view-sticky-shell list-view-map-shell">
                    <div className="list-view-map-frame">
                        <button
                            type="button"
                            className="list-view-map-toggle-btn"
                            onClick={handleColourModeToggle}
                            aria-label={nextColourModeLabel(getColourMode(checkedState), languageState.swedish)}
                            title={nextColourModeLabel(getColourMode(checkedState), languageState.swedish)}
                        >
                            <span className="list-view-map-toggle-icon" aria-hidden="true">
                                <ColourModeIcon mode={getColourMode(checkedState)} fontSize="inherit" />
                            </span>
                        </button>
                        <button
                            type="button"
                            className={`list-view-map-toggle-btn list-view-map-metric-btn${colourMetricAnchor ? ' is-open' : ''}`}
                            onClick={(event) => {
                                const anchor = event.currentTarget;
                                setColourMetricAnchor((current) => (current ? null : anchor));
                            }}
                            aria-haspopup="menu"
                            aria-expanded={Boolean(colourMetricAnchor)}
                            aria-label={languageState.swedish ? 'Färgkarta' : 'Colour map'}
                            title={languageState.swedish ? 'Färgkarta' : 'Colour map'}
                        >
                            <span className="list-view-map-toggle-icon" aria-hidden="true">
                                <PaletteOutlined fontSize="inherit" />
                            </span>
                        </button>
                        <ColourMetricMenu
                            anchorEl={colourMetricAnchor}
                            onClose={() => setColourMetricAnchor(null)}
                            checkedState={checkedState}
                            onSelect={handleColourMetricSelect}
                            swedish={languageState.swedish}
                            placement="below"
                        />
                        <div
                            className={`list-view-map-helpbox ${helpBoxCollapsed ? 'is-mini' : ''}${checkedState.hideOverlay ? ' is-overlay-off' : ''}`}
                            ref={helpBoxHostRef}
                            onClick={handleHelpBoxClick}
                            onMouseDown={(event) => event.stopPropagation()}
                            onTouchStart={(event) => event.stopPropagation()}
                        />
                        <MapContainer
                            center={!restored.fromOtherView && restored.view.centre ? restored.view.centre : SWEDEN_CENTER}
                            zoom={!restored.fromOtherView && Number.isFinite(restored.view.zoom) ? restored.view.zoom : 5}
                            maxBounds={SWEDEN_MAP_BOUNDS}
                            minZoom={LIST_VIEW_TILE_MIN_ZOOM}
                            maxZoom={LIST_VIEW_TILE_MAX_ZOOM}
                            scrollWheelZoom
                            doubleClickZoom={false}
                            tapHold={false}
                            wheelPxPerZoomLevel={90}
                            wheelDebounceTime={LIST_VIEW_WHEEL_DEBOUNCE_MS}
                            preferCanvas={!darkMode}
                            inertia
                            inertiaDeceleration={3200}
                            inertiaMaxSpeed={2200}
                            easeLinearity={0.2}
                            className="list-view-map"
                        >
                            <MapInstanceBridge onMapReady={setMapInstance} />
                            {useOpenFreeMap ? (
                                <OpenFreeMapBase
                                    hideMap={Boolean(checkedState.hideMap)}
                                    dark={darkMode}
                                    swedish={languageState.swedish}
                                    labelPane={mapPanesReady ? OPEN_FREE_MAP_LABEL_PANE : null}
                                    showHover={showHoverTileLayer}
                                    hoverPane={LIST_VIEW_HOVER_TILE_PANE}
                                    onHoverLayer={handleHoverGlLayer}
                                    onFail={handleOpenFreeMapFail}
                                />
                            ) : (
                                <TileLayer
                                    url={tileUrl}
                                    eventHandlers={{ tileerror: handleTileLoadError }}
                                    maxZoom={LIST_VIEW_TILE_MAX_ZOOM}
                                    keepBuffer={LIST_VIEW_TILE_KEEP_BUFFER}
                                    updateWhenZooming={false}
                                    updateWhenIdle
                                    updateInterval={LIST_VIEW_TILE_UPDATE_INTERVAL_MS}
                                    detectRetina={false}
                                    bounds={SWEDEN_MAP_BOUNDS}
                                />
                            )}
                            {!useOpenFreeMap && showHoverTileLayer && (
                                <TileLayer
                                    ref={hoverTileLayerRef}
                                    url={TILE_URL_BRIGHT}
                                    pane={LIST_VIEW_HOVER_TILE_PANE}
                                    maxZoom={LIST_VIEW_TILE_MAX_ZOOM}
                                    keepBuffer={LIST_VIEW_TILE_KEEP_BUFFER}
                                    updateWhenZooming={false}
                                    updateWhenIdle
                                    updateInterval={LIST_VIEW_TILE_UPDATE_INTERVAL_MS}
                                    detectRetina={false}
                                    bounds={EMPTY_TILE_BOUNDS}
                                />
                            )}
                            {mapPanesReady && topojsonFeature && (
                                <GeoJSON
                                    key={regsoLayerKey}
                                    ref={regsoGeoJsonRef}
                                    data={topojsonFeature}
                                    pane={LIST_VIEW_REGSO_PANE}
                                    interactive
                                    style={getOverlayStyle}
                                    onEachFeature={onEachRegsoFeature}
                                />
                            )}
                            {mapPanesReady && darkMode && outsideSwedenMask && (
                                <GeoJSON
                                    data={outsideSwedenMask}
                                    pane={OUTSIDE_SWEDEN_MASK_PANE}
                                    renderer={outsideMaskRenderer}
                                    interactive={false}
                                    style={() => ({
                                        fillColor: '#050a14',
                                        fillOpacity: 0.58,
                                        color: 'transparent',
                                        weight: 0,
                                        pointerEvents: 'none',
                                    })}
                                />
                            )}
                        </MapContainer>
                    </div>

                    <ListViewInfoPanel
                        handleRef={infoPanelRef}
                        languageState={languageState}
                        areaStats={areaStats}
                        colorCache={colorCache}
                        regsoNameById={regsoNameById}
                        listingById={listingById}
                        hoveredListingId={hoveredListingId}
                        mapSelectedListingId={mapSelectedListingId}
                        helpers={INFO_PANEL_HELPERS}
                        isAreaFilteredOut={isAreaFilteredOutStable}
                        onComparePreview={openComparePreview}
                    />
                </div>
                </div>
            </section>

            <section className="list-view-mobile-search-row">
                <div className="list-view-mobile-search-shell list-view-search-band">
                    {renderSearchBandTitle()}
                    {renderSearchControls(mobileSearchFormRef)}
                    {renderNearMe()}
                    {feedSubtitle ? <p className="list-view-mobile-search-subtitle">{feedSubtitle}</p> : null}
                    <nav
                        className="list-view-mobile-chips"
                        aria-label={languageState.swedish ? 'Visa karta och filter' : 'Show map and filters'}
                    >
                        <button type="button" className="lv-chip" onClick={() => openMobileSheet('filter')}>
                            <TuneRoundedIcon aria-hidden="true" />
                            {languageState.swedish ? 'Filter' : 'Filters'}
                        </button>
                        <button type="button" className="lv-chip lv-chip--map" onClick={handleBackToMapView}>
                            <MapOutlinedIcon aria-hidden="true" />
                            {languageState.swedish ? 'Karta' : 'Map'}
                        </button>
                        {comparedAreas.length > 0 && !comparedHomesShown && (
                            // The comparison made on the map (kartvy), in a sheet like the filters; not once its
                            // homes were shown from its page, when the homes are all there is.
                            <button type="button" className="lv-chip lv-chip--compare" onClick={() => openMobileSheet('compare')}>
                                <span className="lv-chip-compare-dots" aria-hidden="true">
                                    {comparedAreas.slice(0, 3).map((area) => {
                                        const score = Number(areaStats?.[`combined_${area.code}`]);
                                        return <i key={area.code} style={{ background: Number.isFinite(score) ? (scoreToColor[score] || '#8f9a97') : '#8f9a97' }} />;
                                    })}
                                </span>
                                {languageState.swedish ? `Jämför ${comparedAreas.length}` : `Compare ${comparedAreas.length}`}
                            </button>
                        )}
                    </nav>
                </div>
            </section>

            {/* Phone (≤ 700px) Filter bottom sheet: slides up over the dimmed feed, scrolls
                inside, and closes on the ✕, the scrim, Escape or a drag of the handle. */}
            {isPhoneLayout && (
                <>
                    <div
                        className={`lv-sheet-scrim${mobileSheet ? ' is-open' : ''}`}
                        onClick={closeMobileSheet}
                        aria-hidden="true"
                    />
                    <div
                        ref={sheetRef}
                        className={`lv-sheet${mobileSheet ? ' is-open' : ''}${renderedSheet ? ` lv-sheet--${renderedSheet}` : ''}`}
                        role="dialog"
                        aria-modal="true"
                        aria-label={renderedSheet === 'compare'
                            ? (languageState.swedish ? 'Jämförelse' : 'Comparison')
                            : (languageState.swedish ? 'Filter' : 'Filters')}
                    >
                        <div
                            className="lv-sheet-grab"
                            onPointerDown={onSheetDragStart}
                            aria-hidden="true"
                        >
                            <span className="lv-sheet-grab-bar" />
                        </div>
                        <div className="lv-sheet-head">
                            <b>
                                {renderedSheet === 'compare'
                                    ? (languageState.swedish ? `Jämför ${comparedAreas.length} områden` : `Comparing ${comparedAreas.length} areas`)
                                    : (languageState.swedish ? 'Filter' : 'Filters')}
                            </b>
                            <button
                                type="button"
                                className="lv-sheet-x"
                                onClick={closeMobileSheet}
                                aria-label={languageState.swedish ? 'Stäng' : 'Close'}
                            >
                                <CloseRoundedIcon aria-hidden="true" />
                            </button>
                        </div>
                        <div className="lv-sheet-body">
                            {renderedSheet === 'compare' && (
                                <CompareCard
                                    swedish={languageState.swedish}
                                    colorCache={colorCache}
                                    areaStats={areaStats}
                                    variant="sheet"
                                    maxVisible={2}
                                    embedded
                                    view="listvy"
                                    onPreview={openComparePreview}
                                />
                            )}
                            {renderedSheet === 'filter' && (
                                // The full filter list (Restid section included) with one "Sök bostäder".
                                <FilterList
                                    languageState={languageState}
                                    checkedState={checkedState}
                                    handleChange={handleMapToggleChange}
                                    setDrawerTab={() => {}}
                                    setOpen={closeMobileSheet}
                                    open
                                    setCustomFilterActive={setCustomFilterActive}
                                    setCustomFilter={setCustomFilter}
                                    setKey={setKey}
                                    setListingFilters={setListingFilters}
                                    onBeforeApply={handleBeforeFilterApply}
                                    getPriceHistogram={getPriceHistogram}
                                    priceHistogramScope={priceHistogramScope}
                                    showMapSwitches={false}
                                />
                            )}
                        </div>
                    </div>
                </>
            )}

            <section className="list-view-feed-column" ref={feedColumnRef}>
                <div className="list-view-feed-header list-view-search-band">
                    {renderSearchBandTitle()}
                    {renderSearchControls(searchFormRef)}
                    {renderNearMe()}
                    {feedSubtitle ? <p>{feedSubtitle}</p> : null}
                </div>
                <div
                    className="list-view-feed-tabbar"
                    role="tablist"
                    aria-label={languageState.swedish ? 'Välj listningstyp' : 'Select listing type'}
                >
                    <button
                        type="button"
                        role="tab"
                        aria-selected={!showFavorites && listingFeedTab === LISTING_FEED_TAB_FOR_SALE}
                        className={`list-view-feed-tab${!showFavorites && listingFeedTab === LISTING_FEED_TAB_FOR_SALE ? ' is-active' : ''}`}
                        onClick={() => {
                            setShowFavorites(false);
                            setListingFeedTab(LISTING_FEED_TAB_FOR_SALE);
                        }}
                    >
                        {languageState.swedish ? 'Till salu' : 'For sale'}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={!showFavorites && listingFeedTab === LISTING_FEED_TAB_COMING_SOON}
                        className={`list-view-feed-tab${!showFavorites && listingFeedTab === LISTING_FEED_TAB_COMING_SOON ? ' is-active' : ''}`}
                        onClick={() => {
                            setShowFavorites(false);
                            setListingFeedTab(LISTING_FEED_TAB_COMING_SOON);
                        }}
                    >
                        {languageState.swedish ? 'Kommande' : 'Coming soon'}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={showFavorites}
                        className={`list-view-feed-tab list-view-feed-tab--favorites${showFavorites ? ' is-active' : ''}`}
                        onClick={() => setShowFavorites(true)}
                    >
                        <FavoriteBorderRoundedIcon fontSize="inherit" aria-hidden="true" />
                        {languageState.swedish ? 'Favoriter' : 'Favourites'}
                        {favorites.length > 0 && <span className="list-view-feed-tab-count">{favorites.length}</span>}
                    </button>
                </div>
                <div
                    className={`list-view-feed-list${keepFeedSurfaceExpanded ? ' is-loading' : ''}${hasListingsPagination ? ' has-pagination' : ' no-pagination'}`}
                >
                    {showFavorites && favorites.length === 0 && (
                        <div className="list-view-feed-welcome">
                            <span className="list-view-feed-welcome-icon" aria-hidden="true">
                                <FavoriteBorderRoundedIcon fontSize="inherit" />
                            </span>
                            <h3>{languageState.swedish ? 'Inga favoriter ännu' : 'No favourites yet'}</h3>
                            <p>
                                {languageState.swedish
                                    ? 'Tryck på stjärnan på en bostad för att spara den här.'
                                    : 'Tap the star on a home to save it here.'}
                            </p>
                        </div>
                    )}
                    {showSearchPrompt && (
                        <div className="list-view-feed-welcome">
                            <span className="list-view-feed-welcome-icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z" />
                                    <circle cx="12" cy="10" r="2.4" />
                                </svg>
                            </span>
                            <h3>{languageState.swedish ? 'Var vill du bo?' : 'Where would you like to live?'}</h3>
                            <p>
                                {languageState.swedish
                                    ? 'Sök på en ort eller klicka på kartan för att se bostäder och hur området står sig mot närområdet.'
                                    : 'Search for a town or click the map to see homes and how the area compares with nearby areas.'}
                            </p>
                        </div>
                    )}
                    {feedListings.map((listing, listingIndex) => {
                        const areaValue = parseAreaValue(listing);
                        const sqmPriceValue = getListingSqmPriceValue(listing);
                        const listingRegsoCode = extractListingRegsoCode(listing);
                        const showFilteredAreaWarning = Boolean(
                            listingRegsoCode && selectedFilteredOutRegsoSet.has(listingRegsoCode),
                        );
                        const listingType = normalizeListingType(listing.type, languageState);
                        const roomsText = listing.rooms ? `${listing.rooms} ${languageState.swedish ? 'rum' : 'rooms'}` : '';
                        const areaText = areaValue != null ? `${Math.round(areaValue)} m²` : '';
                        const sqmPriceText = Number.isFinite(sqmPriceValue)
                            ? `${formatKrPerSqmValue(sqmPriceValue)} kr/m²`
                            : '';
                        const feeText = listing.fee ? `${languageState.swedish ? 'Avgift' : 'Fee'} ${listing.fee}` : '';
                        const metaPrimary = [listingType, roomsText, areaText].filter(Boolean).join(' · ');
                        const metaSecondary = [sqmPriceText, feeText].filter(Boolean).join(' · ');
                        const score = normalizeListingScore(listing.betyg ?? (listing.regsoId ? areaStats[`combined_${listing.regsoId}`] : null));
                        const scoreColor = score != null ? scoreInkColor(scoreToColor[score] || '#8f9aab') : '#8f9aab';
                        const scoreLabel = score == null
                            ? '-'
                            : (Number.isInteger(score) ? `${score}` : score.toFixed(1));
                        const scoreTextLabel = getAreaScoreLabel(score, languageState);
                        const scoreAreaLabelText = scoreTextLabel
                            ? `${scoreTextLabel} ${languageState.swedish ? 'område' : 'area'}`
                            : '';
                        const activeStars = score == null ? 0 : Math.max(1, Math.min(5, Math.round((score / 6) * 5)));
                        const brokerLogo = getBrokerLogo(listing);
                        const brokerName = listing.broker || '';
                        return (
                            <article
                                key={listing.id}
                                className={`list-view-card${hoveredListingId === String(listing.id) ? ' is-hovered' : ''}${mapSelectedListingId === String(listing.id) ? ' is-map-selected' : ''}`}
                                onMouseEnter={() => setHoveredListingId(String(listing.id))}
                                onMouseLeave={() => setHoveredListingId(null)}
                                onClick={() => openFullViewListing(listing)}
                                onDoubleClick={() => handleListingCardDoubleClick(listing)}
                            >
                                <div className="list-view-card-media">
                                    <ListingCardGallery
                                        listing={listing}
                                        alt={listing.title || listing.address}
                                        eager={listingIndex < 8}
                                        highPriority={listingIndex < 2}
                                        swedish={languageState.swedish}
                                    />
                                    <FavoriteButton
                                        listing={listing}
                                        swedish={languageState.swedish}
                                        source="card"
                                        className="list-view-card-favorite"
                                    />
                                    <ListingCardFloorplan
                                        listing={listing}
                                        swedish={languageState.swedish}
                                    />
                                    {listing.isNewConstruction && (
                                        <span className="list-view-card-new">
                                            {languageState.swedish ? 'Nyproduktion' : 'New build'}
                                        </span>
                                    )}
                                    {listing.favoriteRemoved && (
                                        <span className="list-view-card-removed">
                                            {languageState.swedish ? 'Inte längre till salu' : 'No longer for sale'}
                                        </span>
                                    )}
                                </div>
                                <div className="list-view-card-body">
                                    <div className="list-view-card-head">
                                        <h3>{listing.address || listing.title}</h3>
                                        <span className="list-view-card-price">
                                            {formatListingPrice(
                                                listing,
                                                languageState.swedish ? 'Utropspris saknas' : 'Price missing',
                                            )}
                                        </span>
                                    </div>
                                    <p className="list-view-card-subtitle">
                                        {[listing.ort, listing.kommun].filter(Boolean).join(', ') || listing.subtitle || ''}
                                    </p>
                                    {metaPrimary && (
                                        <p className="list-view-card-meta">{metaPrimary}</p>
                                    )}
                                    {metaSecondary && (
                                        <p className="list-view-card-meta list-view-card-meta--secondary">
                                            {metaSecondary}
                                            <AreaTrendPill regso={listingRegsoCode} type={listing.type} swedish={languageState.swedish} />
                                        </p>
                                    )}
                                    {commuteTimes.active && commuteTimes.times[listing.id] !== undefined && (
                                        <p className="list-view-card-commute">
                                            {commuteTimes.times[listing.id] != null ? (
                                                <span className="commute-chip">
                                                    <CommuteModeIcon mode={commuteTimes.mode} size={13} />
                                                    {`${commuteTimes.times[listing.id]} min ${commuteModeLabel(commuteTimes.mode, languageState.swedish, true)}`}
                                                </span>
                                            ) : (
                                                <span className="commute-chip is-far">
                                                    {languageState.swedish ? 'Över 90 min till jobbet' : 'Over 90 min to work'}
                                                </span>
                                            )}
                                        </p>
                                    )}
                                    {Number.isFinite(Number(listing.lat)) && Number.isFinite(Number(listing.lng)) && (
                                        <button
                                            type="button"
                                            className={`list-view-card-locate${locatedListingId === String(listing.id) ? ' is-active' : ''}`}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                locateListingOnMap(listing);
                                            }}
                                            onDoubleClick={(event) => event.stopPropagation()}
                                        >
                                            {locatedListingId === String(listing.id) ? (
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    <path d="M5 12.5l4.5 4.5L19 7.5" />
                                                </svg>
                                            ) : (
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinejoin="round" aria-hidden="true">
                                                    <path d="M12 21s-6.5-6.2-6.5-11a6.5 6.5 0 0 1 13 0c0 4.8-6.5 11-6.5 11z" />
                                                    <circle cx="12" cy="10" r="2.3" />
                                                </svg>
                                            )}
                                            {locatedListingId === String(listing.id)
                                                ? (languageState.swedish ? 'Visas på kartan' : 'Shown on the map')
                                                : (languageState.swedish ? 'Visa på kartan' : 'Show on map')}
                                        </button>
                                    )}
                                    <div className="list-view-card-score-section">
                                        {showFilteredAreaWarning && (
                                            <span
                                                className="list-view-card-filter-warning"
                                                title={filteredAreaWarningTooltip}
                                                aria-label={filteredAreaWarningTooltip}
                                            >
                                                <WarningAmberOutlinedIcon
                                                    className="list-view-card-filter-warning-icon"
                                                    fontSize="inherit"
                                                    aria-hidden="true"
                                                />
                                                <span>{filteredAreaWarningLabel}</span>
                                            </span>
                                        )}
                                        <div className="list-view-card-score">
                                            <div className="list-view-card-score-main">
                                                <div className="list-view-card-score-row">
                                                    <span className="list-view-card-score-label">
                                                        {languageState.swedish ? 'Områdesbetyg' : 'Area score'}
                                                    </span>
                                                    <span className="list-view-card-score-value" style={{ color: scoreColor }}>
                                                        {scoreLabel}
                                                    </span>
                                                    <span className="list-view-card-stars" aria-hidden="true">
                                                        {Array.from({ length: 5 }, (_, index) => (
                                                            <span
                                                                key={`${listing.id}-star-${index}`}
                                                                className={index < activeStars ? 'is-active' : ''}
                                                                style={index < activeStars ? { color: scoreColor } : undefined}
                                                            >
                                                                ★
                                                            </span>
                                                        ))}
                                                    </span>
                                                </div>
                                                {scoreAreaLabelText ? (
                                                    <span className="list-view-card-score-label-text">
                                                        {scoreAreaLabelText}
                                                    </span>
                                                ) : null}
                                            </div>
                                            {listing.realtorUrl && (
                                                <div className="list-view-card-score-actions">
                                                    <a
                                                        className="list-view-card-link list-view-card-link--inline"
                                                        href={listing.realtorUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(event) => event.stopPropagation()}
                                                    >
                                                        {languageState.swedish ? 'Besök mäklare' : 'Visit realtor'}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="list-view-card-foot">
                                        <span className="list-view-card-date">{listing.days || ''}</span>
                                        <div className="list-view-card-foot-right">
                                            {brokerLogo && (
                                                <span className="list-view-card-broker">
                                                    <img src={brokerLogo} alt={brokerName || 'Broker'} loading="lazy" />
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                    {shouldFetchListings && !isPageLoading && visibleLoadedListings.length === 0 && (
                        <div className="list-view-feed-status">
                            {languageState.swedish ? 'Inga bostäder matchar filtren.' : 'No listings match the filters.'}
                        </div>
                    )}
                    {/* A plain bar after the last listing, not one hovering over the list. */}
                    {hasListingsPagination && (
                        <nav
                            className="list-view-pagination"
                            aria-label={languageState.swedish ? 'Sidnavigering för bostäder' : 'Listing pagination'}
                        >
                            <button
                                type="button"
                                className="list-view-pagination-btn"
                                onClick={() => goToListingsPage(activeListingsPage - 1)}
                                disabled={activeListingsPage <= 1 || isPageLoading}
                            >
                                {languageState.swedish ? 'Föregående' : 'Previous'}
                            </button>
                            <div className="list-view-pagination-pages">
                                {paginationItems.map((item, index) => (
                                    item === 'ellipsis'
                                        ? (
                                            <span key={`pagination-ellipsis-${index}`} className="list-view-pagination-ellipsis">…</span>
                                        )
                                        : (
                                            <button
                                                key={`pagination-page-${item}`}
                                                type="button"
                                                className={`list-view-pagination-page${item === activeListingsPage ? ' is-active' : ''}`}
                                                onClick={() => goToListingsPage(Number(item))}
                                                disabled={isPageLoading}
                                                aria-current={item === activeListingsPage ? 'page' : undefined}
                                            >
                                                {item}
                                            </button>
                                        )
                                ))}
                            </div>
                            <button
                                type="button"
                                className="list-view-pagination-btn"
                                onClick={() => goToListingsPage(totalListingsPages)}
                                disabled={activeListingsPage >= totalListingsPages || isPageLoading}
                            >
                                {languageState.swedish ? 'Sista' : 'Last'}
                            </button>
                        </nav>
                    )}
                </div>
                {!hasListingsPagination && (
                    <div className="list-view-feed-end-spacer" aria-hidden="true" />
                )}
            </section>

            {/* Desktop and tablet use the inline filter column; the phone uses the bottom sheet above. */}
            {!isPhoneLayout && (
                <ListViewFilterColumn
                    languageState={languageState}
                    checkedState={checkedState}
                    handleChange={handleMapToggleChange}
                    setCustomFilterActive={setCustomFilterActive}
                    setCustomFilter={setCustomFilter}
                    setKey={setKey}
                    setListingFilters={setListingFilters}
                    getPriceHistogram={getPriceHistogram}
                    priceHistogramScope={priceHistogramScope}
                    onBeforeApply={handleBeforeFilterApply}
                />
            )}
            {introOpen && (
                <IntroTips
                    view="listvy"
                    tips={introTips}
                    swedish={languageState.swedish}
                    phone={isPhoneLayout}
                    paused={Boolean(fullViewListing) || Boolean(mobileSheet)}
                    onClose={() => setIntroOpen(false)}
                />
            )}
            <SharedIntroTips
                view="listvy"
                tips={SHARED_TIPS}
                swedish={languageState.swedish}
                phone={isPhoneLayout}
                paused={Boolean(fullViewListing) || mobileSheet === 'compare'}
            />
            <ListingFullViewModal
                open={Boolean(fullViewListing)}
                listing={fullViewListing}
                onClose={closeFullViewListing}
                languageState={languageState}
                areaScore={fullViewAreaScore}
                areaMetrics={fullViewAreaMetrics}
                wide
                openFrom={fullViewOpenFrom}
                openAtFloorplan={fullViewAtFloorplan}
                detailsLoading={fullViewDetailsLoading}
                detailsError={fullViewDetailsError}
                darkMode={darkMode}
            />
            {comparePreview && (
                <ComparePreview
                    codes={comparePreview}
                    languageState={languageState}
                    onClose={() => setComparePreview(null)}
                    onShowHomes={showComparedHomesHere}
                />
            )}
        </div>
    );
};

export default ListViewPage;
